import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'react-toastify/dist/ReactToastify.css';
import 'leaflet/dist/leaflet.css';
import ThemeEffects from '../components/ThemeEffects';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import trivanaLogo from '../Assets/Trivana_full_logo.png';
import yelofastLogo from '../Assets/Full Logo Yellow.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';
const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (_error) {
    return API_BASE_URL.replace(/\/+$/, '');
  }
})();
const API_PORT = (() => {
  try {
    return new URL(API_BASE_URL).port || '';
  } catch (_error) {
    return '';
  }
})();

const SESSION_ID_REGEX = /^sess_\d+_[a-z0-9]+$/i;

const createSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const resolveMediaUrl = (url) => {
  if (!url) {
    return '';
  }

  const raw = String(url).trim();
  if (!raw) {
    return '';
  }

  if (/^(data:|blob:)/i.test(raw)) {
    return raw;
  }

  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      const isLoopback = ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname);

      if (isLoopback && parsed.port && parsed.port !== API_PORT) {
        return `${API_ORIGIN}${parsed.pathname}${parsed.search}`;
      }

      return raw;
    }
  } catch (_error) {
    return '';
  }

  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return `${API_ORIGIN}${normalizedPath}`;
};

const getOrCreateSessionId = () => {
  const fallbackSessionId = createSessionId();

  if (typeof window === 'undefined') {
    return fallbackSessionId;
  }

  try {
    let sessionId = window.localStorage.getItem('session_id');

    if (typeof sessionId === 'string') {
      sessionId = sessionId.trim();
    }

    // Keep a strict format so backend/session filters stay deterministic.
    if (!sessionId || !SESSION_ID_REGEX.test(sessionId)) {
      sessionId = fallbackSessionId;
      window.localStorage.setItem('session_id', sessionId);
    }

    return sessionId;
  } catch (_error) {
    return fallbackSessionId;
  }
};

const buildCartStorageKey = (restaurantId, sessionId) => `cart_${restaurantId || 'unknown'}_${sessionId || 'anonymous'}`;

const loadCartFromStorage = (storageKey) => {
  if (typeof window === 'undefined' || !storageKey) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const buildLocationStorageKey = (sessionId) => `location_${sessionId || 'anonymous'}`;
const buildCheckoutContactStorageKey = (restaurantId, sessionId) => `checkout_contact_${restaurantId || 'unknown'}_${sessionId || 'anonymous'}`;

const loadLocationFromStorage = (storageKey) => {
  if (typeof window === 'undefined' || !storageKey) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    const latitude = Number(parsed?.latitude);
    const longitude = Number(parsed?.longitude);
    const accuracyValue = parsed?.accuracy;
    const accuracy = accuracyValue == null ? null : Number(accuracyValue);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }

    return {
      latitude,
      longitude,
      accuracy: Number.isFinite(accuracy) ? accuracy : null,
    };
  } catch (_error) {
    return null;
  }
};

const saveLocationToStorage = (storageKey, { latitude, longitude, accuracy = null }) => {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }

  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify({
      latitude: Number(latitude),
      longitude: Number(longitude),
      accuracy: accuracy == null ? null : Number(accuracy),
    }));
  } catch (_error) {
    // Ignore storage failures to keep UI functional.
  }
};

const loadCheckoutContactFromStorage = (storageKey) => {
  if (typeof window === 'undefined' || !storageKey) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    }

    // Backward-compatible fallback keys if older versions stored the values separately.
    const legacyPhone = window.localStorage.getItem('customerPhone')
      || window.localStorage.getItem('whatsappNumber')
      || window.localStorage.getItem('phone');
    const legacyName = window.localStorage.getItem('customerName') || window.localStorage.getItem('name');
    const legacyEmail = window.localStorage.getItem('customerEmail') || window.localStorage.getItem('email');

    if (!legacyPhone && !legacyName && !legacyEmail) {
      return null;
    }

    return {
      name: legacyName || '',
      phone: legacyPhone || '',
      email: legacyEmail || ''
    };
  } catch (_error) {
    return null;
  }
};

const saveCheckoutContactToStorage = (storageKey, contact = {}) => {
  if (typeof window === 'undefined' || !storageKey) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify({
      name: String(contact?.name || '').trim(),
      phone: String(contact?.phone || '').trim(),
      email: String(contact?.email || '').trim()
    }));
  } catch (_error) {
    // Ignore storage failures to keep UI functional.
  }
};

const preloadImages = (items = []) => {
  if (typeof window === 'undefined' || !Array.isArray(items) || items.length === 0) {
    return;
  }

  items.forEach((item) => {
    const imageUrl = resolveMediaUrl(item?.imageUrl || item?.image || '');
    if (!imageUrl) return;

    const img = new Image();
    img.decoding = 'async';
    img.src = imageUrl;
  });
};

const LazyImage = ({ src, alt, index = 0, className, style, placeholderStyle }) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !src) return;

    const delay = Math.min(index * 150, 1200);
    const timer = setTimeout(() => {
      setLoadedSrc(src);
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, src, index]);

  if (!loadedSrc) {
    return <div ref={containerRef} className={className} style={placeholderStyle || style} />;
  }

  return (
    <img
      ref={containerRef}
      className={className}
      style={style}
      src={loadedSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
    />
  );
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (!Array.isArray(center) || center.length !== 2) return;
    const [lat, lng] = center;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    map.setView([lat, lng], map.getZoom(), { animate: false });
  }, [map, center?.[0], center?.[1]]);
  return null;
};

const MapClickSelector = ({ onPick }) => {
  useMapEvents({
    click: (event) => {
      const latitude = Number(event?.latlng?.lat);
      const longitude = Number(event?.latlng?.lng);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return;
      }
      if (typeof onPick === 'function') {
        onPick({ latitude, longitude });
      }
    }
  });

  return null;
};

const EnsureMapReady = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    let frame = requestAnimationFrame(() => {
      map.invalidateSize();
      if (Array.isArray(center) && center.length === 2) {
        map.setView(center, map.getZoom(), { animate: false });
      }
    });

    const t = setTimeout(() => {
      map.invalidateSize();
    }, 240);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(t);
    };
  }, [map, center?.[0], center?.[1]]);

  return null;
};

// Template Theme Configurations — enriched with QR template glassmorphism/visual tokens
const TEMPLATE_THEMES = {

  'Normal': {
    gradient: 'linear-gradient(135deg, #fdfbfb 0%, #ecf4f9 100%)',
    primaryColor: '#2b3a4a',
    secondaryColor: '#6d8396',
    accentColor: '#b4a07d',
    splashBg: '#fdfbfb',
    glassPanel: 'rgba(255, 255, 255, 0.85)',
    glassPanelBorder: '1px solid rgba(0,0,0,0.06)',
    glassPanelShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
    glassCard: 'rgba(255, 255, 255, 0.70)',
    glassCardBorder: '1px solid rgba(255, 255, 255, 0.8)',
    glassCardShadow: '0 10px 25px -5px rgba(43, 58, 74, 0.12)',
    categoryActiveGrad: 'linear-gradient(135deg, #2b3a4a 0%, #6d8396 100%)',
    categoryActiveShadow: '0 4px 15px rgba(43, 58, 74, 0.35)',
    font: "'Inter', 'Poppins', sans-serif",
    splashTitle: 'Welcome',
    splashSubtitle: 'An Exquisite Dining Experience',
    greeting: 'Our Signature Menu',
    tagline: 'Discover culinary perfection 🍽️',
    icon: '🍽️',
    headerIcon: '🍽️',
    splashDecoColor1: 'rgba(43,58,74,0.15)',
    splashDecoColor2: 'rgba(109,131,150,0.15)',
    isDark: false
  },
  'Spring': {
    gradient: 'linear-gradient(135deg, #fff0f5 0%, #f0fdf4 50%, #fff7ed 100%)',
    primaryColor: '#fb7185',
    secondaryColor: '#e11d48',
    accentColor: '#f59e0b',
    splashBg: '#fff0f5',
    glassPanel: 'rgba(255, 255, 255, 0.70)',
    glassPanelBorder: '1px solid rgba(251, 113, 133, 0.1)',
    glassPanelShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
    glassCard: 'rgba(255, 255, 255, 0.60)',
    glassCardBorder: '1px solid rgba(255, 255, 255, 0.8)',
    glassCardShadow: '0 10px 25px -5px rgba(251, 113, 133, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #fb7185 0%, #e11d48 100%)',
    categoryActiveShadow: '0 4px 15px rgba(251, 113, 133, 0.4)',
    font: "'Great Vibes', cursive",
    splashTitle: 'Namaste',
    splashSubtitle: 'Atithi Devo Bhava',
    greeting: 'Fresh Spring Menu',
    tagline: 'Blossoming flavors await you 🌸',
    icon: '🌸',
    headerIcon: '🌱',
    splashDecoColor1: 'rgba(253, 224, 71, 0.3)',
    splashDecoColor2: 'rgba(251, 113, 133, 0.3)',
    isDark: false
  },
  'Summer': {
    gradient: 'linear-gradient(135deg, #fff1eb 0%, #ace0f9 100%)',
    primaryColor: '#ff7b54',
    secondaryColor: '#ffb26b',
    accentColor: '#ffd56b',
    splashBg: '#fff1eb',
    glassPanel: 'rgba(255, 255, 255, 0.80)',
    glassPanelBorder: '1px solid rgba(255, 123, 84, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(255, 123, 84, 0.08)',
    glassCard: 'rgba(255, 255, 255, 0.70)',
    glassCardBorder: '1px solid rgba(255, 255, 255, 0.8)',
    glassCardShadow: '0 10px 25px -5px rgba(255, 123, 84, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #ff7b54 0%, #ffb26b 100%)',
    categoryActiveShadow: '0 4px 15px rgba(255, 123, 84, 0.4)',
    font: "'Pacifico', cursive",
    splashTitle: 'Namaste',
    splashSubtitle: 'Atithi Devo Bhava',
    greeting: 'Summer Delights',
    tagline: 'Cool & refreshing treats ☀️',
    icon: '☀️',
    headerIcon: '☀️',
    splashDecoColor1: 'rgba(255, 213, 107, 0.4)',
    splashDecoColor2: 'rgba(255, 123, 84, 0.3)',
    isDark: false
  },
  'Autumn': {
    gradient: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)',
    primaryColor: '#ea580c',
    secondaryColor: '#c2410c',
    accentColor: '#f59e0b',
    splashBg: '#fff7ed',
    glassPanel: 'rgba(255, 255, 255, 0.60)',
    glassPanelBorder: '1px solid rgba(234, 88, 12, 0.2)',
    glassPanelShadow: '0 4px 30px rgba(69, 26, 3, 0.05)',
    glassCard: 'rgba(255, 255, 255, 0.50)',
    glassCardBorder: '1px solid rgba(255, 255, 255, 0.6)',
    glassCardShadow: '0 10px 25px -5px rgba(234, 88, 12, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
    categoryActiveShadow: '0 4px 15px rgba(234, 88, 12, 0.4)',
    font: "'Great Vibes', cursive",
    splashTitle: 'Namaste',
    splashSubtitle: 'Atithi Devo Bhava',
    greeting: 'Autumn Comfort',
    tagline: 'Golden Flavors of Autumn 🍂',
    icon: '🍂',
    headerIcon: '🍁',
    splashDecoColor1: 'rgba(251, 146, 60, 0.4)',
    splashDecoColor2: 'rgba(253, 224, 71, 0.4)',
    isDark: false
  },
  'Winter': {
    gradient: 'linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)',
    primaryColor: '#38bdf8',
    secondaryColor: '#3b82f6',
    accentColor: '#dbeafe',
    splashBg: '#0f172a',
    glassPanel: 'rgba(30, 41, 59, 0.70)',
    glassPanelBorder: '1px solid rgba(255, 255, 255, 0.1)',
    glassPanelShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
    glassCard: 'rgba(255, 255, 255, 0.05)',
    glassCardBorder: '1px solid rgba(255, 255, 255, 0.05)',
    glassCardShadow: '0 4px 6px rgba(0,0,0,0.1)',
    categoryActiveGrad: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
    categoryActiveShadow: '0 0 15px rgba(14, 165, 233, 0.5)',
    font: "'Dancing Script', cursive",
    splashTitle: 'Welcome',
    splashSubtitle: 'Winter Wonderland',
    greeting: 'Frozen Delights',
    tagline: 'Warm soups & sizzling platters ❄️',
    icon: '❄️',
    headerIcon: '❄️',
    splashDecoColor1: 'rgba(56, 189, 248, 0.2)',
    splashDecoColor2: 'rgba(59, 130, 246, 0.2)',
    isDark: true
  },
  'Christmas': {
    gradient: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
    primaryColor: '#ef4444',
    secondaryColor: '#16a34a',
    accentColor: '#fbbf24',
    splashBg: '#0f2027',
    glassPanel: 'rgba(20, 40, 45, 0.80)',
    glassPanelBorder: '1px solid rgba(239, 68, 68, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(0,0,0,0.15)',
    glassCard: 'rgba(20, 40, 45, 0.65)',
    glassCardBorder: '1px solid rgba(255,255,255,0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #ef4444 0%, #16a34a 100%)',
    categoryActiveShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
    font: "'Mountains of Christmas', cursive",
    splashTitle: 'Merry Christmas',
    splashSubtitle: 'Season of Joy',
    greeting: 'Festive Special Menu',
    tagline: 'Celebrate with joy & flavors 🎄',
    icon: '🎄',
    headerIcon: '🎄',
    splashDecoColor1: 'rgba(239, 68, 68, 0.2)',
    splashDecoColor2: 'rgba(22, 163, 74, 0.2)',
    isDark: true
  },
  'Diwali': {
    gradient: 'linear-gradient(135deg, #2b1459 0%, #4c1d95 55%, #7c2d12 100%)',
    primaryColor: '#ffb300',
    secondaryColor: '#ff8f00',
    accentColor: '#ffc107',
    splashBg: '#2b1459',
    glassPanel: 'rgba(43, 20, 89, 0.82)',
    glassPanelBorder: '1px solid rgba(255, 179, 0, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(255, 179, 0, 0.08)',
    glassCard: 'rgba(55, 28, 112, 0.68)',
    glassCardBorder: '1px solid rgba(255, 179, 0, 0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(255, 179, 0, 0.2)',
    categoryActiveGrad: 'linear-gradient(135deg, #ffb300 0%, #ff8f00 100%)',
    categoryActiveShadow: '0 4px 15px rgba(255, 179, 0, 0.4)',
    font: "'Satisfy', cursive",
    splashTitle: 'Happy Diwali',
    splashSubtitle: 'Festival of Lights',
    greeting: 'Diwali Festival Specials',
    tagline: 'Light up your taste buds 🪔',
    icon: '🪔',
    headerIcon: '🪔',
    splashDecoColor1: 'rgba(255, 179, 0, 0.25)',
    splashDecoColor2: 'rgba(255, 143, 0, 0.25)',
    isDark: true
  },
  'Eid': {
    gradient: 'linear-gradient(135deg, #051914 0%, #0b362b 100%)',
    primaryColor: '#95dbb7',
    secondaryColor: '#6bc79b',
    accentColor: '#cdefe0',
    splashBg: '#051914',
    glassPanel: 'rgba(10, 35, 25, 0.80)',
    glassPanelBorder: '1px solid rgba(149, 219, 183, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(0,0,0,0.15)',
    glassCard: 'rgba(10, 35, 25, 0.65)',
    glassCardBorder: '1px solid rgba(149, 219, 183, 0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(149, 219, 183, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #95dbb7 0%, #6bc79b 100%)',
    categoryActiveShadow: '0 4px 15px rgba(149, 219, 183, 0.4)',
    font: "'Amiri', serif",
    splashTitle: 'Eid Mubarak',
    splashSubtitle: 'Celebration of Blessings',
    greeting: 'Eid Special Feast',
    tagline: 'Blessed flavors for you 🌙',
    icon: '🌙',
    headerIcon: '🌙',
    splashDecoColor1: 'rgba(149, 219, 183, 0.2)',
    splashDecoColor2: 'rgba(107, 199, 155, 0.2)',
    isDark: true
  },
  'Holi': {
    gradient: 'radial-gradient(circle at 20% 80%, #fef3c7 0%, #fffbeb 100%)',
    primaryColor: '#ec4899',
    secondaryColor: '#8b5cf6',
    accentColor: '#f59e0b',
    splashBg: '#fffbeb',
    glassPanel: 'rgba(255, 255, 255, 0.85)',
    glassPanelBorder: '2px solid #ec4899',
    glassPanelShadow: '0 4px 20px rgba(236, 72, 153, 0.15)',
    glassCard: 'rgba(255, 255, 255, 0.90)',
    glassCardBorder: '2px solid white',
    glassCardShadow: '0 8px 15px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0,0,0,0.02)',
    categoryActiveGrad: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
    categoryActiveShadow: '0 4px 15px rgba(236, 72, 153, 0.4)',
    font: "'Fredoka One', cursive",
    splashTitle: 'Happy Holi',
    splashSubtitle: 'Festival of Colors',
    greeting: 'Colors of Flavor',
    tagline: 'Taste the rainbow of joy 🎨',
    icon: '🎨',
    headerIcon: '💜',
    splashDecoColor1: 'rgba(236, 72, 153, 0.3)',
    splashDecoColor2: 'rgba(139, 92, 246, 0.3)',
    isDark: false
  },
  'Navratri': {
    gradient: 'linear-gradient(135deg, #2a0845 0%, #6441a5 100%)',
    primaryColor: '#ffbb00',
    secondaryColor: '#ff6a00',
    accentColor: '#ee0979',
    splashBg: '#2a0845',
    glassPanel: 'rgba(40, 15, 60, 0.75)',
    glassPanelBorder: '1px solid rgba(255, 187, 0, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(0,0,0,0.15)',
    glassCard: 'rgba(40, 15, 60, 0.60)',
    glassCardBorder: '1px solid rgba(255, 187, 0, 0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(255, 187, 0, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #ffbb00 0%, #ff6a00 100%)',
    categoryActiveShadow: '0 4px 15px rgba(255, 187, 0, 0.4)',
    font: "'Bebas Neue', cursive",
    splashTitle: 'Navratri',
    splashSubtitle: 'Nine Nights of Celebration',
    greeting: 'Navratri Special',
    tagline: 'Nine nights of divine flavors 🪔',
    icon: '🪔',
    headerIcon: '🪔',
    splashDecoColor1: 'rgba(255, 187, 0, 0.25)',
    splashDecoColor2: 'rgba(238, 9, 121, 0.25)',
    isDark: true
  },
  'Makar Sankranti': {
    gradient: 'linear-gradient(135deg, #1f1c2c 0%, #928dab 100%)',
    primaryColor: '#f3904f',
    secondaryColor: '#e16120',
    accentColor: '#ffb75e',
    splashBg: '#1f1c2c',
    glassPanel: 'rgba(40, 40, 60, 0.75)',
    glassPanelBorder: '1px solid rgba(243, 144, 79, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(0,0,0,0.15)',
    glassCard: 'rgba(40, 40, 60, 0.60)',
    glassCardBorder: '1px solid rgba(243, 144, 79, 0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(243, 144, 79, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #f3904f 0%, #e16120 100%)',
    categoryActiveShadow: '0 4px 15px rgba(243, 144, 79, 0.4)',
    font: "'Righteous', cursive",
    splashTitle: 'Makar Sankranti',
    splashSubtitle: 'Harvest Festival',
    greeting: 'Harvest Celebration',
    tagline: 'Traditional flavors & blessings 🪁',
    icon: '🪁',
    headerIcon: '🪁',
    splashDecoColor1: 'rgba(243, 144, 79, 0.25)',
    splashDecoColor2: 'rgba(255, 183, 94, 0.25)',
    isDark: true
  },
  'Teej': {
    gradient: 'linear-gradient(135deg, #134e2f 0%, #1a6b3c 50%, #2a8750 100%)',
    primaryColor: '#52c97c',
    secondaryColor: '#f9a826',
    accentColor: '#ffd166',
    splashBg: '#134e2f',
    glassPanel: 'rgba(15, 60, 35, 0.80)',
    glassPanelBorder: '1px solid rgba(82, 201, 124, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(0,0,0,0.15)',
    glassCard: 'rgba(15, 60, 35, 0.65)',
    glassCardBorder: '1px solid rgba(82, 201, 124, 0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(82, 201, 124, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #52c97c 0%, #f9a826 100%)',
    categoryActiveShadow: '0 4px 15px rgba(82, 201, 124, 0.4)',
    font: "'Satisfy', cursive",
    splashTitle: 'Teej Mubarak',
    splashSubtitle: 'Monsoon Festival of Rajasthan',
    greeting: 'Teej Special Menu',
    tagline: 'Celebrate the monsoon blessings 🌿',
    icon: '🌿',
    headerIcon: '🌿',
    splashDecoColor1: 'rgba(82, 201, 124, 0.25)',
    splashDecoColor2: 'rgba(249, 168, 38, 0.25)',
    isDark: true
  },
  'Gangaur': {
    gradient: 'linear-gradient(135deg, #6b0f1a 0%, #b91c1c 50%, #fc5c7d 100%)',
    primaryColor: '#fc5c7d',
    secondaryColor: '#fbb034',
    accentColor: '#fff0a5',
    splashBg: '#6b0f1a',
    glassPanel: 'rgba(80, 10, 25, 0.80)',
    glassPanelBorder: '1px solid rgba(252, 92, 125, 0.15)',
    glassPanelShadow: '0 4px 30px rgba(0,0,0,0.15)',
    glassCard: 'rgba(80, 10, 25, 0.65)',
    glassCardBorder: '1px solid rgba(252, 92, 125, 0.1)',
    glassCardShadow: '0 10px 25px -5px rgba(252, 92, 125, 0.15)',
    categoryActiveGrad: 'linear-gradient(135deg, #fc5c7d 0%, #fbb034 100%)',
    categoryActiveShadow: '0 4px 15px rgba(252, 92, 125, 0.4)',
    font: "'Fredoka One', cursive",
    splashTitle: 'Gangaur Utsav',
    splashSubtitle: 'Festival of Devotion & Colors',
    greeting: 'Gangaur Special Thali',
    tagline: 'Vibrant flavors of Rajasthan 🌺',
    icon: '🌺',
    headerIcon: '🌺',
    splashDecoColor1: 'rgba(252, 92, 125, 0.25)',
    splashDecoColor2: 'rgba(251, 176, 52, 0.25)',
    isDark: true
  },
  'Royal Rajasthan': {
    gradient: 'linear-gradient(180deg, #fffaf5 0%, #fff3e8 52%, #fff7ed 100%)',
    primaryColor: '#ea580c',
    secondaryColor: '#f97316',
    accentColor: '#fb923c',
    splashBg: '#ea580c',
    glassPanel: 'rgba(255, 255, 255, 0.86)',
    glassPanelBorder: '1px solid rgba(251, 146, 60, 0.25)',
    glassPanelShadow: '0 8px 24px rgba(234, 88, 12, 0.16)',
    glassCard: 'rgba(255, 255, 255, 0.84)',
    glassCardBorder: '1px solid rgba(251, 146, 60, 0.2)',
    glassCardShadow: '0 10px 25px -5px rgba(234, 88, 12, 0.18)',
    categoryActiveGrad: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    categoryActiveShadow: '0 4px 15px rgba(249, 115, 22, 0.4)',
    font: "'Poppins', sans-serif",
    splashTitle: 'Namaste',
    splashSubtitle: 'Atithi Devo Bhava',
    greeting: 'Hungry?',
    tagline: 'Explore our delicious menu 🍽️',
    icon: '🍽️',
    headerIcon: '🧡',
    splashDecoColor1: 'rgba(251, 146, 60, 0.2)',
    splashDecoColor2: 'rgba(234, 88, 12, 0.2)',
    isDark: false
  }

};

const TemplatedMenuPage = () => {
  const { restaurantId } = useParams();
  const [sessionId] = useState(() => getOrCreateSessionId());
  const cartStorageKey = buildCartStorageKey(restaurantId, sessionId);
  const locationStorageKey = buildLocationStorageKey(sessionId);
  const checkoutContactStorageKey = buildCheckoutContactStorageKey(restaurantId, sessionId);

  const [restaurant, setRestaurant] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [cart, setCart] = useState(() => loadCartFromStorage(cartStorageKey));
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [themeReady, setThemeReady] = useState(false);
  const [royalLoaderOpened, setRoyalLoaderOpened] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [showCategoryMenuModal, setShowCategoryMenuModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 767 : false);

  // Addon states
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectionRulesByGroup, setSelectionRulesByGroup] = useState({});
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [editingCartItemId, setEditingCartItemId] = useState(null);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [minDisplayPriceByProduct, setMinDisplayPriceByProduct] = useState({});
  const [pendingSpecialInstructions, setPendingSpecialInstructions] = useState('');
  const [editingSpecialRequest, setEditingSpecialRequest] = useState({
    open: false,
    cartItemId: null,
    text: ''
  });

  // Opening hours / restaurant open status
  const [restaurantOpen, setRestaurantOpen] = useState(true);
  const [closedMessage, setClosedMessage] = useState('');

  // Razorpay checkout states
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showClearCartConfirm, setShowClearCartConfirm] = useState(false);
  const [showOrderConfirmPopup, setShowOrderConfirmPopup] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    phone: '',
    email: '',
    addressLine: '',
    landmark: '',
    city: '',
    deliveryInstructions: '',
    vehicleColor: '',
    vehicleNumber: ''
  });
  const [fulfillmentType, setFulfillmentType] = useState('DELIVERY');
  const [inputValue, setLocalitySearchText] = useState('');
  const [selectedLocality, setSelectedLocality] = useState(null);
  const [localities, setAllLocalities] = useState([]);
  const [filteredLocalities, setFilteredLocalities] = useState([]);
  const [loadingLocalities, setLoadingLocalities] = useState(false);
  const [hasFetchedLocalities, setHasFetchedLocalities] = useState(false);
  const [showLocalityDropdown, setShowLocalityDropdown] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [showCouponList, setShowCouponList] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponsError, setCouponsError] = useState('');
  const quickInstructionChips = ['No tomatoes', 'No chilli', 'No onion or garlic'];
  const [customerLocation, setCustomerLocation] = useState({
    loading: false,
    latitude: null,
    longitude: null,
    accuracy: null,
    error: ''
  });
  const [mapCenterOverride, setMapCenterOverride] = useState(null);
  const [deliverySettings, setDeliverySettings] = useState({
    isDeliveryEnabled: true,
    isSlabPricingEnabled: false,
    minimumOrderAmountForDelivery: 0,
    fallbackDeliveryCharge: 0,
    slabs: []
  });
  const [gstSettings, setGstSettings] = useState({
    isTaxEnabled: true,
    taxPercentage: 5,
    taxName: 'GST'
  });

  const menuGridRef = useRef(null);
  const localityDropdownRef = useRef(null);
  const addressLineRef = useRef(null);
  const localityFetchInFlightRef = useRef(false);
  const fallbackMapCenter = [28.0229, 73.3119];
  const selectedLocalityLatitude = Number(selectedLocality?.latitude);
  const selectedLocalityLongitude = Number(selectedLocality?.longitude);
  const hasSelectedLocalityCoordinates = Number.isFinite(selectedLocalityLatitude) && Number.isFinite(selectedLocalityLongitude);
  const hasMapOverride = Array.isArray(mapCenterOverride)
    && mapCenterOverride.length === 2
    && Number.isFinite(Number(mapCenterOverride[0]))
    && Number.isFinite(Number(mapCenterOverride[1]));
  const mapCenter = hasMapOverride
    ? [Number(mapCenterOverride[0]), Number(mapCenterOverride[1])]
    : hasSelectedLocalityCoordinates
    ? [selectedLocalityLatitude, selectedLocalityLongitude]
    : customerLocation.latitude != null && customerLocation.longitude != null
      ? [customerLocation.latitude, customerLocation.longitude]
      : fallbackMapCenter;

  useEffect(() => {
    const savedCheckoutContact = loadCheckoutContactFromStorage(checkoutContactStorageKey);
    if (!savedCheckoutContact) {
      return;
    }

    setCheckoutForm((prev) => {
      const nextPhone = String(prev.phone || savedCheckoutContact.phone || '').replace(/\D/g, '').slice(0, 10);
      return {
        ...prev,
        name: prev.name || String(savedCheckoutContact.name || '').trim(),
        phone: prev.phone || nextPhone,
        email: prev.email || String(savedCheckoutContact.email || '').trim()
      };
    });
  }, [checkoutContactStorageKey]);

  useEffect(() => {
    const hasAnyContactValue = Boolean(
      String(checkoutForm.name || '').trim()
      || String(checkoutForm.phone || '').trim()
      || String(checkoutForm.email || '').trim()
    );

    if (!hasAnyContactValue) {
      return;
    }

    saveCheckoutContactToStorage(checkoutContactStorageKey, {
      name: checkoutForm.name,
      phone: checkoutForm.phone,
      email: checkoutForm.email
    });
  }, [checkoutContactStorageKey, checkoutForm.name, checkoutForm.phone, checkoutForm.email]);

  useEffect(() => {
    setShowSplash(true);
    setLoadingProgress(0);
    setThemeReady(false);
    setRoyalLoaderOpened(false);
    loadRestaurantData();
  }, [restaurantId]);

  useEffect(() => {
    if (!(showSplash && themeReady)) return;
    const isRoyalTemplate = /royal/i.test(String(restaurant?.menuTemplate || ''));
    if (!isRoyalTemplate) {
      setRoyalLoaderOpened(false);
      return;
    }

    setRoyalLoaderOpened(false);
    const timer = setTimeout(() => {
      setRoyalLoaderOpened(true);
    }, 3500);

    return () => clearTimeout(timer);
  }, [showSplash, themeReady, restaurant?.menuTemplate, restaurantId]);

  // Splash screen progress animation — stays active until data is loaded
  useEffect(() => {
    if (showSplash && themeReady) {
      const isRoyalTemplate = /royal/i.test(String(restaurant?.menuTemplate || ''));
      const progressStep = isRoyalTemplate ? 0.85 : 2;
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            // Only finish splash if data loading is also complete
            if (!isRoyalTemplate && !loading) {
              clearInterval(interval);
              setTimeout(() => setShowSplash(false), 950);
            }
            return 100;
          }
          return Math.min(100, prev + progressStep);
        });
      }, 30);
      return () => clearInterval(interval);
    }
  }, [showSplash, loading, themeReady, restaurant?.menuTemplate, royalLoaderOpened]);

  // Royal Rajasthan: hide splash only after shutter has opened and data is ready
  useEffect(() => {
    const isRoyalTemplate = /royal/i.test(String(restaurant?.menuTemplate || ''));
    if (!(showSplash && themeReady && isRoyalTemplate && royalLoaderOpened && !loading)) return;

    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 1100);

    return () => clearTimeout(timer);
  }, [showSplash, themeReady, restaurant?.menuTemplate, royalLoaderOpened, loading]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock page scroll during splash and while addon modal is open.
  useEffect(() => {
    if (showSplash || showAddonModal) {
      document.body.style.overflow = 'hidden';
      if (showSplash) {
        window.scrollTo(0, 0);
      }
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showSplash, showAddonModal]);

  // Debounce search query (300ms) for smart filtering
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    setCart(loadCartFromStorage(cartStorageKey));
  }, [cartStorageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(cartStorageKey, JSON.stringify(cart));
    } catch (_error) {
      // Keep cart state functional even if storage is unavailable.
    }
  }, [cart, cartStorageKey]);

  useEffect(() => {
    if (!appliedCoupon) return;
    setAppliedCoupon(null);
    setCouponCode('');
  }, [cart]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (!event || event.key !== cartStorageKey) return;
      setCart(loadCartFromStorage(cartStorageKey));
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [cartStorageKey]);

  useEffect(() => {
    const storedLocation = loadLocationFromStorage(locationStorageKey);
    if (storedLocation) {
      setCustomerLocation((prev) => ({
        ...prev,
        loading: false,
        error: '',
        latitude: storedLocation.latitude,
        longitude: storedLocation.longitude,
        accuracy: storedLocation.accuracy,
      }));
    } else {
      setCustomerLocation((prev) => ({
        ...prev,
        loading: false,
        latitude: null,
        longitude: null,
        accuracy: null,
      }));
    }
  }, [locationStorageKey]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (!event || event.key !== locationStorageKey) return;
      const storedLocation = loadLocationFromStorage(locationStorageKey);
      if (!storedLocation) return;

      setCustomerLocation((prev) => ({
        ...prev,
        loading: false,
        error: '',
        latitude: storedLocation.latitude,
        longitude: storedLocation.longitude,
        accuracy: storedLocation.accuracy,
      }));
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [locationStorageKey]);

  useEffect(() => {
    if (products.length > 0) {
      preloadImages(products);
    }
  }, [products]);

  const filterLocalities = useCallback((sourceList, query) => {
    const list = Array.isArray(sourceList) ? sourceList : [];
    const safeQuery = String(query || '').trim().toLowerCase();

    if (!safeQuery) {
      return list;
    }

    return list.filter((locality) => {
      const name = String(locality?.localityName || '').toLowerCase();
      const code = String(locality?.localityCode || '').toLowerCase();
      return name.includes(safeQuery) || code.includes(safeQuery);
    });
  }, []);

  const renderHighlightedLocalityText = useCallback((text, query) => {
    const safeText = String(text || '');
    const safeQuery = String(query || '').trim();

    if (!safeText || !safeQuery) {
      return safeText;
    }

    const lowerText = safeText.toLowerCase();
    const lowerQuery = safeQuery.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerQuery);

    if (matchIndex < 0) {
      return safeText;
    }

    const beforeMatch = safeText.slice(0, matchIndex);
    const matchedText = safeText.slice(matchIndex, matchIndex + safeQuery.length);
    const afterMatch = safeText.slice(matchIndex + safeQuery.length);

    return (
      <>
        {beforeMatch}
        <span style={{ backgroundColor: '#fef08a', color: '#111827', borderRadius: '3px', padding: '0 2px' }}>
          {matchedText}
        </span>
        {afterMatch}
      </>
    );
  }, []);

  useEffect(() => {
    setHasFetchedLocalities(false);
    localityFetchInFlightRef.current = false;
    setAllLocalities([]);
    setFilteredLocalities([]);
    setSelectedLocality(null);
    setMapCenterOverride(null);
    setLocalitySearchText('');
    setLoadingLocalities(false);
    setShowLocalityDropdown(false);
  }, [restaurantId]);

  const applyPickedLocation = (latitude, longitude, accuracy = null, options = {}) => {
    const safeLatitude = Number(latitude);
    const safeLongitude = Number(longitude);
    const safeAccuracy = accuracy == null ? null : Number(accuracy);
    const keepLoading = Boolean(options?.keepLoading);

    if (!Number.isFinite(safeLatitude) || !Number.isFinite(safeLongitude)) {
      return;
    }

    setCustomerLocation(prev => ({
      ...prev,
      loading: keepLoading ? prev.loading : false,
      latitude: safeLatitude,
      longitude: safeLongitude,
      accuracy: Number.isFinite(safeAccuracy) ? safeAccuracy : null,
      error: ''
    }));

    setMapCenterOverride([safeLatitude, safeLongitude]);

    saveLocationToStorage(locationStorageKey, {
      latitude: safeLatitude,
      longitude: safeLongitude,
      accuracy: Number.isFinite(safeAccuracy) ? safeAccuracy : null,
    });
  };

  const reverseGeocodeAndAutofillAddress = useCallback(async ({ latitude, longitude }) => {
    const safeLatitude = Number(latitude);
    const safeLongitude = Number(longitude);

    if (!Number.isFinite(safeLatitude) || !Number.isFinite(safeLongitude)) {
      return;
    }

    try {
      const response = await axios.get('https://nominatim.openstreetmap.org/reverse', {
        params: {
          format: 'jsonv2',
          lat: safeLatitude,
          lon: safeLongitude,
          addressdetails: 1,
          zoom: 18
        },
        timeout: 12000
      });

      const address = response?.data?.address || {};
      const displayName = String(response?.data?.display_name || '').trim();

      const uniqueAddressParts = [];
      const pushAddressPart = (value) => {
        const normalized = String(value || '').trim();
        if (!normalized) return;
        if (uniqueAddressParts.some((existing) => existing.toLowerCase() === normalized.toLowerCase())) {
          return;
        }
        uniqueAddressParts.push(normalized);
      };

      pushAddressPart([address?.house_number, address?.house_name].filter(Boolean).join(' ').trim());
      pushAddressPart(address?.building || address?.amenity);
      pushAddressPart(address?.road || address?.pedestrian || address?.footway || address?.residential || address?.path);
      pushAddressPart(address?.neighbourhood || address?.suburb || address?.quarter || address?.hamlet);
      pushAddressPart(address?.city_district || address?.city || address?.town || address?.village);

      const detailedAddressFromFields = uniqueAddressParts.slice(0, 4).join(', ');

      const genericAdminPattern = /(tehsil|district|division|state|country|india|region)/i;
      const displaySegments = displayName
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean);

      const fallbackDisplaySegments = displaySegments
        .filter((segment, index) => index === 0 || !genericAdminPattern.test(segment))
        .slice(0, 4);

      let resolvedAddressLine = detailedAddressFromFields || fallbackDisplaySegments.join(', ').trim() || displayName;
      const resolvedLandmark = String(
        address?.amenity
        || address?.building
        || address?.tourism
        || address?.suburb
        || address?.neighbourhood
        || ''
      ).trim();
      const resolvedPincode = String(address?.postcode || '').trim();
      const resolvedCityFromGeocode = String(
        address?.city
        || address?.town
        || address?.village
        || address?.municipality
        || ''
      ).trim();

      const cityCandidates = [
        address?.city,
        address?.town,
        address?.village,
        address?.municipality,
        restaurant?.city,
      ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean);

      const normalizedAddressLine = String(resolvedAddressLine || '').trim().toLowerCase();
      const isVeryGenericAddress =
        !normalizedAddressLine
        || cityCandidates.includes(normalizedAddressLine)
        || (/^[a-z\s]+$/i.test(normalizedAddressLine) && normalizedAddressLine.split(/\s+/).length <= 2 && cityCandidates.some((city) => normalizedAddressLine.includes(city)));

      if (isVeryGenericAddress) {
        const validLocalities = (Array.isArray(localities) ? localities : []).filter((item) => {
          const lat = Number(item?.latitude);
          const lng = Number(item?.longitude);
          return Number.isFinite(lat) && Number.isFinite(lng) && String(item?.localityName || '').trim();
        });

        if (validLocalities.length > 0) {
          const toRadians = (value) => (Number(value) * Math.PI) / 180;
          const distanceInKm = (lat1, lon1, lat2, lon2) => {
            const earthRadiusKm = 6371;
            const dLat = toRadians(lat2 - lat1);
            const dLon = toRadians(lon2 - lon1);
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
              + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2))
              * Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return earthRadiusKm * c;
          };

          const nearest = validLocalities
            .map((item) => ({
              localityName: String(item.localityName || '').trim(),
              localityCode: String(item.localityCode || '').trim(),
              distanceKm: distanceInKm(safeLatitude, safeLongitude, Number(item.latitude), Number(item.longitude)),
            }))
            .sort((a, b) => a.distanceKm - b.distanceKm)[0];

          if (nearest?.localityName) {
            const nearestLabel = nearest.localityCode
              ? `${nearest.localityName} (${nearest.localityCode})`
              : nearest.localityName;
            const cityLabel = String(restaurant?.city || resolvedCityFromGeocode || address?.city || '').trim();
            resolvedAddressLine = [
              `Near ${nearestLabel}`,
              cityLabel,
            ].filter(Boolean).join(', ');
          }
        }
      }

      setCheckoutForm((prev) => ({
        ...prev,
        addressLine: resolvedAddressLine || prev.addressLine,
        landmark: resolvedLandmark || prev.landmark,
        city: (restaurant?.city || '').trim() || resolvedCityFromGeocode || prev.city,
      }));
    } catch (error) {
      console.warn('Reverse geocode failed', error);
      setCustomerLocation((prev) => ({
        ...prev,
        error: prev.error || 'GPS found. Could not auto-fill address, please enter manually.'
      }));
    }
  }, [restaurant?.city, localities]);

  const requestCustomerLocation = () => {
    if (!navigator?.geolocation) {
      setCustomerLocation(prev => ({
        ...prev,
        loading: false,
        error: 'Location not supported on this device/browser. Please enter address manually.'
      }));
      return;
    }

    setCustomerLocation(prev => ({ ...prev, loading: true, error: '' }));
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        applyPickedLocation(
          position.coords.latitude,
          position.coords.longitude,
          Math.round(position.coords.accuracy || 0),
          { keepLoading: true }
        );

        await reverseGeocodeAndAutofillAddress({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });

        setCustomerLocation((prev) => ({
          ...prev,
          loading: false,
        }));
      },
      () => {
        setCustomerLocation(prev => ({
          ...prev,
          loading: false,
          error: 'Location permission denied. Please fill delivery address manually.'
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  const handleMapLocationPick = async ({ latitude, longitude }) => {
    setCustomerLocation((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));

    applyPickedLocation(latitude, longitude, null, { keepLoading: true });

    await reverseGeocodeAndAutofillAddress({ latitude, longitude });

    setCustomerLocation((prev) => ({
      ...prev,
      loading: false,
      error: ''
    }));
  };

  useEffect(() => {
    if (showCheckoutModal) {
      const lockedCity = (restaurant?.city || '').trim();
      if (lockedCity) {
        setCheckoutForm(prev => ({ ...prev, city: lockedCity }));
      }

      const storedLocation = loadLocationFromStorage(locationStorageKey);
      if (storedLocation) {
        setCustomerLocation((prev) => ({
          ...prev,
          loading: false,
          error: '',
          latitude: storedLocation.latitude,
          longitude: storedLocation.longitude,
          accuracy: storedLocation.accuracy,
        }));
      }

      setShowLocalityDropdown(false);
    }
  }, [showCheckoutModal, locationStorageKey, restaurant?.city]);

  useEffect(() => {
    if (currentView !== 'home') return;

    let shouldShow = false;
    if (typeof window !== 'undefined') {
      shouldShow = window.sessionStorage.getItem('showOrderConfirmPopup') === 'true';
      if (shouldShow) {
        window.sessionStorage.removeItem('showOrderConfirmPopup');
      }
    }

    if (!shouldShow) return;

    const timer = window.setTimeout(() => {
      setShowOrderConfirmPopup(true);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [currentView]);

  const loadLocalitiesFromApi = useCallback(async ({ city }) => {
    const safeCity = String(city || '').trim();
    if (!safeCity) return;

    if (hasFetchedLocalities || localityFetchInFlightRef.current) {
      return;
    }

    localityFetchInFlightRef.current = true;
    setLoadingLocalities(true);
    try {
      const token = localStorage.getItem('token');
      const apiKey =
        localStorage.getItem('apiKey') ||
        process.env.REACT_APP_API_KEY ||
        process.env.REACT_APP_YELOFAST_API_KEY ||
        '';

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (apiKey) {
        headers['x-api-key'] = apiKey;
        headers['api-key'] = apiKey;
      }
      headers['x-session-id'] = sessionId;

      const endpoint = `${API_BASE_URL}/api/localities?city=${encodeURIComponent(safeCity)}`;

      const response = await axios.get(endpoint, { headers });
      const payload = response?.data?.data || response?.data || [];
      const list = Array.isArray(payload) ? payload : [];

      console.log('[LOCALITIES] API response', {
        city: safeCity,
        endpoint,
        totalRows: list.length,
        rawResponse: response?.data
      });

      if (list.length === 0) {
        console.warn('[LOCALITIES] API returned empty list', {
          city: safeCity,
          endpoint,
          rawResponse: response?.data
        });
      }

      setHasFetchedLocalities(true);
      setAllLocalities(list);
      setFilteredLocalities(filterLocalities(list, inputValue));
    } catch (error) {
      console.warn('Failed to fetch localities', error);
      setHasFetchedLocalities(true);
      setAllLocalities([]);
      setFilteredLocalities([]);
    } finally {
      localityFetchInFlightRef.current = false;
      setLoadingLocalities(false);
    }
  }, [filterLocalities, hasFetchedLocalities, sessionId]);

  useEffect(() => {
    const city = (restaurant?.city || checkoutForm.city || '').trim();
    if (!city) return;

    console.log('[LOCALITIES] Initial locality fetch on mount/city update', { city });
    loadLocalitiesFromApi({ city });
  }, [loadLocalitiesFromApi, restaurant?.city, checkoutForm.city]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!localityDropdownRef.current) return;
      if (!localityDropdownRef.current.contains(event.target)) {
        setShowLocalityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const city = (restaurant?.city || checkoutForm.city || '').trim();
    if (!city) return;

    setFilteredLocalities(filterLocalities(localities, inputValue));
  }, [localities, inputValue, checkoutForm.city, filterLocalities, restaurant?.city]);

  const handleSelectLocality = (locality) => {
    setSelectedLocality(locality);
    setLocalitySearchText(locality?.localityName || '');
    setFilteredLocalities(filterLocalities(localities, locality?.localityName || ''));
    setShowLocalityDropdown(false);

    console.log('[LOCALITY] Selected locality from dropdown', {
      localityName: locality?.localityName || null,
      localityCode: locality?.localityCode || null,
      latitude: locality?.latitude ?? null,
      longitude: locality?.longitude ?? null,
    });

    const localityLatitude = Number(locality?.latitude);
    const localityLongitude = Number(locality?.longitude);
    if (Number.isFinite(localityLatitude) && Number.isFinite(localityLongitude)) {
      setMapCenterOverride([localityLatitude, localityLongitude]);
    }
  };

  const loadRestaurantData = async () => {
    try {
      setLoading(true);
      setThemeReady(false);

      const normalizeRestaurantData = (data = {}) => ({
        ...data,
        ownerId: data.ownerId ?? data.owner_id ?? data.vendorId ?? data.vendor_id ?? null,
        vendorId: data.vendorId ?? data.vendor_id ?? data.ownerId ?? data.owner_id ?? null,
        deliverySlabs: Array.isArray(data.deliverySlabs)
          ? data.deliverySlabs
          : Array.isArray(data.delivery_slabs)
            ? data.delivery_slabs
            : [],
        isDeliveryEnabled: data.isDeliveryEnabled ?? data.is_delivery_enabled ?? data.deliveryEnabled ?? null,
        isSlabPricingEnabled: data.isSlabPricingEnabled ?? data.is_slab_pricing_enabled ?? data.slabPricingEnabled ?? null,
        minimumOrderAmountForDelivery: data.minimumOrderAmountForDelivery ?? data.minimum_order_amount_for_delivery ?? data.minimumOrderAmount ?? null,
        fallbackDeliveryCharge: data.fallbackDeliveryCharge ?? data.fallback_delivery_charge ?? data.deliveryCharge ?? null,
        restaurantName: data.restaurantName ?? data.restaurant_name ?? '',
        cuisineType: data.cuisineType ?? data.cuisine_type ?? '',
        logoUrl: data.logoUrl ?? data.logo_url ?? data.logo ?? '',
        contactPhone: data.contactPhone ?? data.contact_phone ?? '',
        contactEmail: data.contactEmail ?? data.contact_email ?? '',
        city: data.city ?? '',
        mapLocation: data.mapLocation ?? data.map_location ?? '',
        websiteUrl: data.websiteUrl ?? data.website_url ?? '',
        instagramUrl: data.instagramUrl ?? data.instagram_url ?? '',
        facebookUrl: data.facebookUrl ?? data.facebook_url ?? '',
        whatsappNumber: data.whatsappNumber ?? data.whatsapp_number ?? '',
        menuTemplate: data.menuTemplate ?? data.menu_template ?? ''
      });

      // Prefer public menu endpoint because it includes social/contact fields.
      const headers = getPublicRequestHeaders();
      let restaurantResponse = await axios.get(
        `${API_BASE_URL}/api/public/menu/restaurant/${restaurantId}`,
        { headers }
      );

      // Fallback to vendor endpoint if public endpoint is not available.
      if (!restaurantResponse?.data || restaurantResponse?.data?.error) {
        console.warn('Public menu endpoint failed, trying vendor public endpoint');
        restaurantResponse = await axios.get(
          `${API_BASE_URL}/api/vendor/restaurants/${restaurantId}/public`,
          { headers }
        );
      }

      const restaurantPayload = restaurantResponse?.data?.data || restaurantResponse?.data;
      const restaurantData = normalizeRestaurantData(restaurantPayload || {});

      if (process.env.NODE_ENV !== 'production') {
        console.log('[DELIVERY_SLABS] Restaurant loaded', {
          restaurantId,
          restaurantData
        });
      }

      const loadDeliverySettingsForRestaurant = async () => {
        try {
          const headers = getPublicRequestHeaders();
          const response = await axios.get(
            `${API_BASE_URL}/api/public/menu/restaurant/${restaurantId}/delivery-settings`,
            { headers }
          );

          let payload = response?.data?.data || response?.data || {};

          if (process.env.NODE_ENV !== 'production') {
            console.log('[DELIVERY_SLABS] Public delivery settings response', {
              restaurantId,
              payload
            });
          }

          if (!payload || !Array.isArray(payload?.slabs) || payload.slabs.length === 0) {
            try {
              const vendorResponse = await axios.get(
                `${API_BASE_URL}/api/vendor/delivery-settings/restaurant/${restaurantId}`,
                { headers }
              );
              const vendorPayload = vendorResponse?.data?.data || vendorResponse?.data || null;
              if (process.env.NODE_ENV !== 'production') {
                console.log('[DELIVERY_SLABS] Vendor delivery settings response', {
                  restaurantId,
                  vendorPayload
                });
              }
              if (vendorPayload) {
                payload = vendorPayload;
              }
            } catch (vendorError) {
              console.warn('Vendor delivery settings fallback failed:', vendorError?.message || vendorError);
            }
          }
          const rawSlabs = Array.isArray(payload?.slabs) ? payload.slabs : [];
          const fallbackFromRestaurant = buildDeliverySettingsFromRestaurant(restaurantData);

          let normalizedSlabs = rawSlabs
            .filter((slab) => slab?.isActive !== false)
            .map((slab) => ({
              minOrderAmount: Number(slab?.minOrderAmount || 0),
              maxOrderAmount: slab?.maxOrderAmount === null || slab?.maxOrderAmount === undefined
                ? null
                : Number(slab?.maxOrderAmount),
              deliveryCharge: Number(slab?.deliveryCharge || 0),
              slabName: slab?.slabName || ''
            }))
            .sort((a, b) => a.minOrderAmount - b.minOrderAmount);

          if (normalizedSlabs.length === 0 && fallbackFromRestaurant?.slabs?.length) {
            normalizedSlabs = fallbackFromRestaurant.slabs;
          }

          const resolvedIsDeliveryEnabled = payload?.isDeliveryEnabled;
          const resolvedIsSlabPricingEnabled = payload?.isSlabPricingEnabled;

          setDeliverySettings({
            isDeliveryEnabled: resolvedIsDeliveryEnabled == null
              ? fallbackFromRestaurant?.isDeliveryEnabled !== false
              : resolvedIsDeliveryEnabled !== false,
            isSlabPricingEnabled: resolvedIsSlabPricingEnabled == null
              ? fallbackFromRestaurant?.isSlabPricingEnabled === true
              : resolvedIsSlabPricingEnabled === true,
            minimumOrderAmountForDelivery: Number(payload?.minimumOrderAmountForDelivery || fallbackFromRestaurant?.minimumOrderAmountForDelivery || 0),
            fallbackDeliveryCharge: Number(payload?.fallbackDeliveryCharge || fallbackFromRestaurant?.fallbackDeliveryCharge || 0),
            slabs: normalizedSlabs
          });

          if (process.env.NODE_ENV !== 'production') {
            console.debug('[DELIVERY_SLABS] Settings loaded', {
              restaurantId,
              isDeliveryEnabled: payload?.isDeliveryEnabled !== false,
              isSlabPricingEnabled: payload?.isSlabPricingEnabled === true,
              slabCount: normalizedSlabs.length,
              slabs: normalizedSlabs
            });
          }
        } catch (error) {
          // Public menu should stay functional even if delivery settings API is unavailable.
          console.warn('Delivery settings not available, using restaurant fallback.', error);
          const fallbackFromRestaurant = buildDeliverySettingsFromRestaurant(restaurantData);
          if (fallbackFromRestaurant) {
            setDeliverySettings(fallbackFromRestaurant);
            return;
          }
          setDeliverySettings({
            isDeliveryEnabled: true,
            isSlabPricingEnabled: false,
            minimumOrderAmountForDelivery: 0,
            fallbackDeliveryCharge: 0,
            slabs: []
          });
        }
      };

      const loadTaxSettingsForRestaurant = async () => {
        try {
          const headers = getPublicRequestHeaders();
          const response = await axios.get(
            `${API_BASE_URL}/api/billing-pos/restaurants/${restaurantId}/tax-settings`,
            { headers }
          );

          const payload = response?.data || {};
          setGstSettings({
            isTaxEnabled: payload?.isTaxEnabled !== false,
            taxPercentage: Number(payload?.taxPercentage ?? 5),
            taxName: payload?.taxName || 'GST'
          });
        } catch (error) {
          // Keep menu functional even if GST settings API fails.
          console.warn('Tax settings not available, using default GST.', error);
          setGstSettings({
            isTaxEnabled: true,
            taxPercentage: 5,
            taxName: 'GST'
          });
        }
      };

      if (restaurantData?.restaurantName || restaurantData?.restaurant_name) {
        setRestaurant(restaurantData);
        const restaurantDeliveryFallback = buildDeliverySettingsFromRestaurant(restaurantData);
        if (restaurantDeliveryFallback) {
          setDeliverySettings(restaurantDeliveryFallback);
        }
        setCheckoutForm((prev) => ({
          ...prev,
          city: (restaurantData?.city || '').trim() || prev.city
        }));
        await Promise.all([
          loadDeliverySettingsForRestaurant(),
          loadTaxSettingsForRestaurant()
        ]);
        setThemeReady(true);
        document.title = `${restaurantData.restaurantName} - Menu`;
        if (!restaurantData.instagramUrl && !restaurantData.facebookUrl && !restaurantData.websiteUrl && !restaurantData.whatsappNumber) {
          console.warn('Restaurant loaded but social links are empty', restaurantData);
        }

        // Prefer the POS customizations endpoint so the web menu gets the same
        // addons + modifier groups that the desktop POS already shows.
        let productsData = [];
        try {
          const customizationsResp = await axios.get(
            `${API_BASE_URL}/api/billing-pos/restaurants/${restaurantId}/available-items-with-customizations`,
            { headers }
          );
          productsData = Array.isArray(customizationsResp?.data?.items)
            ? customizationsResp.data.items
            : Array.isArray(customizationsResp?.data?.data)
              ? customizationsResp.data.data
              : [];
        } catch (customizationError) {
          console.warn('Failed to load items with customizations, falling back to public menu items:', customizationError?.message || customizationError);

          const itemsResp = await axios.get(
            `${API_BASE_URL}/api/public/menu/restaurant/${restaurantId}/items`,
            { headers }
          );
          productsData = Array.isArray(itemsResp.data) ? itemsResp.data : (itemsResp.data?.data || []);
        }

        // Normalize the menu items into the product shape expected by the UI.
        const mapped = (productsData || []).map((it) => {
          const pid = Number(it.itemId ?? it.item_id ?? it.id ?? 0) || null;
          const catId = it.categoryId ?? it.category_id ?? null;
          const catName = (it.categoryName ?? it.category_name ?? it.category) || 'Others';
          const normalizedAddons = Array.isArray(it.addons) ? it.addons : [];
          const normalizedModifierGroups = Array.isArray(it.modifierGroups) ? it.modifierGroups : [];

          return {
            productId: pid,
            productName: String(it.itemName || it.item_name || it.name || '').trim(),
            price: Number(it.price ?? it.base_price ?? 0),
            discountPrice: it.discountPrice ?? it.discount_price ?? null,
            imageUrl: it.imageUrl ?? it.image_url ?? it.image ?? '',
            description: it.description ?? it.desc ?? '',
            isActive: (it.isAvailable == null && it.is_available == null)
              ? true
              : (it.isAvailable === true || it.isAvailable === 1 || it.is_available === true || it.is_available === 1),
            productCategory: {
              categoryId: catId,
              categoryName: String(catName || 'Others')
            },
            addons: normalizedAddons,
            modifierGroups: normalizedModifierGroups,
            categoryIsMrp: Boolean(it.categoryIsMrp ?? it.category_is_mrp ?? it.isMrp ?? it.is_mrp ?? false),
            // preserve raw payload for debugging if needed
            __raw: it
          };
        });

        const activeProducts = mapped.filter((p) => p?.isActive !== false && p.productId != null);
        setProducts(activeProducts);
        loadMinimumDisplayPrices(activeProducts);

        const catResp = await axios.get(
          `${API_BASE_URL}/api/public/menu/restaurant/${restaurantId}/categories`,
          { headers }
        );
        const rawCategories = Array.isArray(catResp.data) ? catResp.data : (catResp.data?.data || []);

        setCategories((rawCategories || []).filter((c) => c?.isActive !== false));

        await fetchRestaurantOpenStatus();

      } else {
        setThemeReady(true);
        toast.error('Restaurant not found');
      }
    } catch (error) {
      console.error('Error loading restaurant:', error);
      setThemeReady(true);
      toast.error('Failed to load restaurant details');
    } finally {
      setLoading(false);
    }
  };

  const getPublicRequestHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    const apiKey =
      localStorage.getItem('apiKey') ||
      process.env.REACT_APP_API_KEY ||
      process.env.REACT_APP_YELOFAST_API_KEY ||
      '';

    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (apiKey) {
      headers['x-api-key'] = apiKey;
      headers['api-key'] = apiKey;
    }
    headers['x-session-id'] = sessionId;
    return headers;
  }, [sessionId]);

  const fetchRestaurantOpenStatus = useCallback(async () => {
    try {
      const headers = getPublicRequestHeaders();
      const openResponse = await axios.get(
        `${API_BASE_URL}/api/vendor/opening-hours/restaurant/${restaurantId}/is-open`,
        { headers }
      );

      if (openResponse?.data?.success) {
        const status = openResponse?.data?.data || {};
        const isOpenNow = Boolean(status?.isOpen);

        setRestaurantOpen(isOpenNow);
        if (isOpenNow) {
          setClosedMessage('');
        } else {
          setClosedMessage(status?.message || 'Restaurant is currently closed');
        }
      }
    } catch (openErr) {
      // If check fails, don't block orders
      console.warn('Could not check opening hours:', openErr);
    }
  }, [restaurantId, getPublicRequestHeaders]);

  useEffect(() => {
    if (!restaurantId) {
      return undefined;
    }

    let isDisposed = false;

    const refreshStatus = async () => {
      if (isDisposed) return;
      await fetchRestaurantOpenStatus();
    };

    const intervalId = window.setInterval(() => {
      refreshStatus();
    }, 30000);

    const handleFocus = () => {
      refreshStatus();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshStatus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [restaurantId, fetchRestaurantOpenStatus]);

  const getBaseProductPrice = (product) => Number(product?.discountPrice ?? product?.price ?? 0);

  const calculateMinimumRequiredModifiersTotal = (groups) => {
    return (groups || []).reduce((sum, group) => {
      const minSelection = Number(group?.minSelection || 0);
      const isRequired = !!group?.isRequired || minSelection > 0;
      if (!isRequired || minSelection <= 0) {
        return sum;
      }

      const sortedOptionPrices = (group?.options || [])
        .map((option) => Number(option?.optionPrice || 0))
        .filter((price) => !Number.isNaN(price))
        .sort((a, b) => a - b);

      if (sortedOptionPrices.length < minSelection) {
        return sum;
      }

      const groupMinimum = sortedOptionPrices
        .slice(0, minSelection)
        .reduce((acc, price) => acc + price, 0);

      return sum + groupMinimum;
    }, 0);
  };

  const calculateMinimumModifierOptionTotal = (groups) => {
    return (groups || []).reduce((sum, group) => {
      const optionPrices = (group?.options || [])
        .map((option) => Number(option?.optionPrice || 0))
        .filter((price) => !Number.isNaN(price));

      if (optionPrices.length === 0) {
        return sum;
      }

      return sum + Math.min(...optionPrices);
    }, 0);
  };

  const loadMinimumDisplayPrices = async (productList) => {
    if (!Array.isArray(productList) || productList.length === 0) {
      setMinDisplayPriceByProduct({});
      return;
    }

    const headers = getPublicRequestHeaders();
    const nextMap = {};

    await Promise.all(productList.map(async (product) => {
      const productId = product?.productId;
      if (!productId) return;

      const basePrice = getBaseProductPrice(product);
      if (basePrice > 0) {
        nextMap[productId] = { price: basePrice, estimatedFromModifiers: false };
        return;
      }

      try {
        const groups = Array.isArray(product?.modifierGroups) ? product.modifierGroups : [];
        const minRequiredTotal = calculateMinimumRequiredModifiersTotal(groups);
        const minOptionTotal = calculateMinimumModifierOptionTotal(groups);
        const estimatedPrice = Number(basePrice || 0) + Number(minRequiredTotal || minOptionTotal || 0);

        nextMap[productId] = {
          price: estimatedPrice,
          estimatedFromModifiers: Number(minRequiredTotal || minOptionTotal || 0) > 0
        };
      } catch (error) {
        nextMap[productId] = { price: Number(basePrice || 0), estimatedFromModifiers: false };
      }
    }));

    setMinDisplayPriceByProduct(nextMap);
  };

  const getProductDisplayPriceMeta = (product) => {
    const productId = product?.productId;
    const basePrice = getBaseProductPrice(product);
    const cached = productId ? minDisplayPriceByProduct?.[productId] : null;
    if (cached) return cached;

    if (basePrice <= 0) {
      const groups = Array.isArray(product?.modifierGroups) ? product.modifierGroups : [];
      const minRequiredTotal = calculateMinimumRequiredModifiersTotal(groups);
      const minOptionTotal = calculateMinimumModifierOptionTotal(groups);
      const estimatedTotal = Number(minRequiredTotal || minOptionTotal || 0);

      return {
        price: Number(basePrice || 0) + estimatedTotal,
        estimatedFromModifiers: estimatedTotal > 0
      };
    }

    return { price: basePrice, estimatedFromModifiers: false };
  };

  // Fetch active addons for a product
  const fetchAddons = async (productOrId) => {
    try {
      setLoadingAddons(true);
      const headers = getPublicRequestHeaders();
      const requestedProductId = Number(
        typeof productOrId === 'object'
          ? (productOrId?.productId ?? productOrId?.itemId ?? productOrId?.id)
          : productOrId
      ) || null;

      if (typeof productOrId === 'object') {
        const embeddedAddons = Array.isArray(productOrId?.addons) ? productOrId.addons : [];
        const embeddedModifierGroups = Array.isArray(productOrId?.modifierGroups) ? productOrId.modifierGroups : [];

        if (embeddedAddons.length > 0 || embeddedModifierGroups.length > 0) {
          return applyProductCustomizations(productOrId);
        }
      }

      try {
        const catalogResponse = await axios.get(
          `${API_BASE_URL}/api/billing-pos/restaurants/${restaurantId}/available-items-with-customizations`,
          { headers }
        );
        const items = Array.isArray(catalogResponse?.data?.items)
          ? catalogResponse.data.items
          : Array.isArray(catalogResponse?.data?.data)
            ? catalogResponse.data.data
            : [];

        const matchedItem = items.find((item) => Number(item?.itemId ?? item?.item_id ?? item?.id) === requestedProductId) || null;
        if (matchedItem) {
          return applyProductCustomizations({
            ...matchedItem,
            productId: requestedProductId,
            addons: Array.isArray(matchedItem.addons) ? matchedItem.addons : [],
            modifierGroups: Array.isArray(matchedItem.modifierGroups) ? matchedItem.modifierGroups : []
          });
        }

        setAvailableAddons([]);
        setSelectionRulesByGroup({});
        return [];
      } catch (catalogError) {
        console.warn('POS customization catalog failed for product:', requestedProductId, catalogError?.message || catalogError);
        setAvailableAddons([]);
        setSelectionRulesByGroup({});
        return [];
      }
    } catch (error) {
      console.error('Error fetching addons:', error);
      setAvailableAddons([]);
      setSelectionRulesByGroup({});
      return [];
    } finally {
      setLoadingAddons(false);
    }
  };

  // Helper: Get price for current order type (DINE_IN, DELIVERY, TAKEAWAY, DRIVETHROUGH)
  const getPriceForOrderType = (basePrice, orderTypePrices) => {
    if (!Array.isArray(orderTypePrices) || orderTypePrices.length === 0) {
      return Number(basePrice ?? 0);
    }

    // Map frontend fulfillmentType to backend order type names
    const orderTypeMap = {
      'DELIVERY': 'DELIVERY',
      'TAKEAWAY': 'TAKEAWAY',
      'DRIVETHROUGH': 'DRIVETHROUGH',
      'DINEIN': 'DINE_IN',
      'DINE_IN': 'DINE_IN'
    };

    const currentOrderType = orderTypeMap[fulfillmentType] || fulfillmentType;

    // Find matching price for current order type
    const matchingPrice = orderTypePrices.find(
      p => p.orderType === currentOrderType && p.isActive
    );

    if (matchingPrice && matchingPrice.price != null) {
      return Number(matchingPrice.price);
    }

    return Number(basePrice ?? 0);
  };

  // Recalculate cart addon prices when order type changes
  useEffect(() => {
    if (cart.length === 0) return;

    const updatedCart = cart.map(cartItem => {
      // Skip if no addons
      if (!Array.isArray(cartItem.selectedAddons) || cartItem.selectedAddons.length === 0) {
        return cartItem;
      }

      // Recalculate addon prices based on new order type
      const updatedAddons = cartItem.selectedAddons.map(addon => {
        if (!addon.orderTypePrices || addon.orderTypePrices.length === 0) {
          return addon; // No order type pricing available
        }

        const newPrice = getPriceForOrderType(addon.basePrice || addon.addonPrice, addon.orderTypePrices);

        return {
          ...addon,
          addonPrice: newPrice
        };
      });

      return {
        ...cartItem,
        selectedAddons: updatedAddons
      };
    });

    // Only update if prices actually changed
    const pricesChanged = updatedCart.some((item, idx) => {
      const oldItem = cart[idx];
      if (!oldItem.selectedAddons || !item.selectedAddons) return false;

      return item.selectedAddons.some((addon, addonIdx) => {
        const oldAddon = oldItem.selectedAddons[addonIdx];
        return oldAddon && addon.addonPrice !== oldAddon.addonPrice;
      });
    });

    if (pricesChanged) {
      setCart(updatedCart);
      try {
        window.localStorage.setItem(cartStorageKey, JSON.stringify(updatedCart));
      } catch (_error) {
        // Silently fail if localStorage is unavailable
      }
    }
  }, [fulfillmentType]);

  const applyProductCustomizations = (product) => {
    const productAddons = Array.isArray(product?.addons) ? product.addons : [];
    const productModifierGroups = Array.isArray(product?.modifierGroups) ? product.modifierGroups : [];

    const flattenedAddons = [];
    const nextRules = {};

    productAddons.forEach((addon) => {
      const basePrice = Number(addon?.addonPrice ?? addon?.price ?? 0);
      const finalPrice = getPriceForOrderType(basePrice, addon?.orderTypePrices);

      flattenedAddons.push({
        addonId: addon?.addonId ?? addon?.id ?? null,
        addonName: addon?.addonName ?? addon?.name ?? '',
        addonPrice: finalPrice,
        basePrice: basePrice,
        orderTypePrices: addon?.orderTypePrices || [],
        optionId: null,
        legacyAddonId: addon?.legacyAddonId ?? addon?.addonId ?? addon?.id ?? null,
        groupId: 'legacy-default',
        groupName: 'Add-ons',
        groupDisplayOrder: Number(addon?.displayOrder ?? 9999)
      });
    });

    productModifierGroups.forEach((group) => {
      const groupId = String(group?.groupId || `group-${group?.groupName || 'addons'}`);
      nextRules[groupId] = {
        groupName: group?.groupName || 'Add-ons',
        isRequired: !!group?.isRequired,
        minSelection: Number(group?.minSelection || 0),
        maxSelection: Number(group?.maxSelection || 99),
        displayOrder: Number(group?.displayOrder ?? 9999),
        dependsOnGroupId: group?.dependsOnGroupId != null ? String(group.dependsOnGroupId) : null,
        dependsOnGroupName: group?.dependsOnGroupName || null,
        dynamicLabel: !!group?.dynamicLabel
      };

      (Array.isArray(group?.options) ? group.options : []).forEach((option) => {
        const basePrice = Number(option?.optionPrice || 0);
        const finalPrice = getPriceForOrderType(basePrice, option?.orderTypePrices);

        flattenedAddons.push({
          addonId: option?.legacyAddonId || option?.optionId,
          addonName: option?.optionName,
          addonPrice: finalPrice,
          basePrice: basePrice,
          orderTypePrices: option?.orderTypePrices || [],
          optionId: option?.optionId || null,
          legacyAddonId: option?.legacyAddonId || null,
          contextKey: option?.contextKey || null,
          groupId,
          groupName: group?.groupName || 'Add-ons',
          groupDisplayOrder: Number(group?.displayOrder ?? 9999)
        });
      });
    });

    setSelectionRulesByGroup(nextRules);
    setAvailableAddons(flattenedAddons);
    return flattenedAddons;
  };

  // Show addon selection modal
  const closeAddonModal = () => {
    setShowAddonModal(false);
    setSelectedAddons([]);
    setCurrentProduct(null);
    setPendingSpecialInstructions('');
    setEditingCartItemId(null);
  };

  const openAddonModalWithProduct = async (product, preselectedAddons = [], specialInstructions = '', editCartItemId = null) => {
    setCurrentProduct(product);
    setPendingSpecialInstructions(String(specialInstructions || '').trim());
    setSelectedAddons(Array.isArray(preselectedAddons) ? preselectedAddons : []);
    setEditingCartItemId(editCartItemId);

    const hasEmbeddedCustomizations =
      (Array.isArray(product?.addons) && product.addons.length > 0) ||
      (Array.isArray(product?.modifierGroups) && product.modifierGroups.length > 0);

    if (hasEmbeddedCustomizations) {
      applyProductCustomizations(product);
      setShowAddonModal(true);
      return;
    }

    const fetchedAddons = await fetchAddons(product);
    const hasAddons = Array.isArray(fetchedAddons) && fetchedAddons.length > 0;

    if (hasAddons) {
      setShowAddonModal(true);
      return;
    }

    if (editCartItemId) {
      setShowAddonModal(false);
      setCurrentProduct(null);
      setPendingSpecialInstructions('');
      setEditingCartItemId(null);
      return;
    }

    const cartItem = {
      ...product,
      quantity: 1,
      specialInstructions: '',
      selectedAddons: [],
      cartItemId: `${product.productId}_${Date.now()}`
    };

    setCart(prev => [...prev, cartItem]);
    setCurrentProduct(null);
  };

  const showAddonSelectionModal = async (product) => {
    await openAddonModalWithProduct(product, [], '', null);
  };

  const openCartItemAddonModal = async (item) => {
    if (!item || !Array.isArray(item.selectedAddons) || item.selectedAddons.length === 0) {
      return;
    }

    await openAddonModalWithProduct(
      item,
      item.selectedAddons,
      item.specialInstructions || '',
      item.cartItemId || null
    );
  };

  // Toggle addon selection
  const normalizeContextKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  const buildParentContextKeys = (parentOption) => {
    if (!parentOption) return new Set();
    const optionId = parentOption?.optionId || parentOption?.legacyAddonId || parentOption?.addonId;
    const rawKeys = [
      optionId ? `optionid:${optionId}` : null,
      optionId ? `id:${optionId}` : null,
      optionId ? `${optionId}` : null,
      parentOption?.addonName || parentOption?.optionName || null
    ].filter(Boolean);

    return new Set(rawKeys.map((key) => normalizeContextKey(key)).filter(Boolean));
  };

  const matchesDependentContext = (addon, parentOption) => {
    const contextKey = normalizeContextKey(addon?.contextKey);
    if (!contextKey) return true;
    if (!parentOption) return false;
    return buildParentContextKeys(parentOption).has(contextKey);
  };

  const normalizeAddonSelections = (selections) => {
    let nextSelections = [...(selections || [])];

    for (let guard = 0; guard < 6; guard += 1) {
      const groupedSelections = nextSelections.reduce((acc, item) => {
        const key = String(item?.groupId || 'default');
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {});

      const keepLastInGroup = {};
      Object.entries(groupedSelections).forEach(([groupId, items]) => {
        const rule = selectionRulesByGroup?.[groupId];
        if (Number(rule?.maxSelection || 99) === 1 && items.length > 0) {
          keepLastInGroup[groupId] = items[items.length - 1];
        }
      });

      const filteredSelections = nextSelections.filter((item) => {
        const groupId = String(item?.groupId || 'default');
        const rule = selectionRulesByGroup?.[groupId];

        if (keepLastInGroup[groupId] && keepLastInGroup[groupId] !== item) {
          return false;
        }

        const dependsOnGroupId = rule?.dependsOnGroupId;
        if (!dependsOnGroupId) {
          return true;
        }

        const parentOption = groupedSelections[String(dependsOnGroupId)]?.[0] || null;
        return matchesDependentContext(item, parentOption);
      });

      if (
        filteredSelections.length === nextSelections.length &&
        filteredSelections.every((item, index) => item === nextSelections[index])
      ) {
        break;
      }

      nextSelections = filteredSelections;
    }

    return nextSelections;
  };

  const getAddonSelectionSignature = (selections) => {
    return (selections || [])
      .map((item) => `${String(item?.groupId || 'default')}:${String(item?.addonId || item?.optionId || item?.legacyAddonId || item?.addonName || '')}`)
      .join('|');
  };

  const getSelectionText = (min, max) => {
    const safeMin = Number(min || 0);
    const safeMax = Number(max || 0);

    if (safeMin === 0 && safeMax === 0) return '';
    if (safeMin === 1 && safeMax === 1) return 'Select any one';
    if (safeMin === 1 && safeMax > 1) return `Select at least 1 (up to ${safeMax})`;
    if (safeMin > 1 && safeMax > 1) return `Select at least ${safeMin} (up to ${safeMax})`;
    if (safeMin >= 1 && safeMax === 0) return `Select at least ${safeMin}`;
    if (safeMin === 0 && safeMax > 0) return `Select up to ${safeMax}`;
    return '';
  };

  const getAddonIdentityKey = (addon) => String(
    addon?.optionId
    || addon?.legacyAddonId
    || addon?.addonId
    || addon?.addonName
    || ''
  );

  useEffect(() => {
    if (!showAddonModal) return;

    setSelectedAddons((prev) => {
      let normalized = normalizeAddonSelections(prev);

      const groupedSelections = normalized.reduce((acc, addon) => {
        const key = String(addon?.groupId || 'default');
        if (!acc[key]) acc[key] = [];
        acc[key].push(addon);
        return acc;
      }, {});

      const groupedAvailableAddons = Object.values(
        availableAddons.reduce((acc, addon) => {
          const fallbackId = addon?.groupId || `group-${addon?.groupName || 'addons'}`;
          const groupId = String(fallbackId);
          if (!acc[groupId]) {
            acc[groupId] = {
              groupId,
              groupDisplayOrder: Number(
                addon?.groupDisplayOrder
                ?? selectionRulesByGroup?.[groupId]?.displayOrder
                ?? 9999
              ),
              options: []
            };
          }
          acc[groupId].options.push(addon);
          return acc;
        }, {})
      ).sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder);

      groupedAvailableAddons.forEach(({ groupId, options }) => {
        const rule = selectionRulesByGroup?.[groupId];
        const minSelection = Number(rule?.minSelection || 0);
        const hasSelection = (groupedSelections[groupId] || []).length > 0;

        if (minSelection < 1 || hasSelection) return;

        const dependsOnGroupId = rule?.dependsOnGroupId;
        const selectedParentOption = dependsOnGroupId
          ? groupedSelections[String(dependsOnGroupId)]?.[0] || null
          : null;

        if (dependsOnGroupId && !selectedParentOption) return;

        const contextFilteredOptions = (options || []).filter((addon) =>
          matchesDependentContext(addon, selectedParentOption)
        );

        const firstOption = contextFilteredOptions[0];
        if (!firstOption) return;

        normalized = normalizeAddonSelections([...normalized, firstOption]);
        groupedSelections[groupId] = [firstOption];
      });

      const prevSignature = getAddonSelectionSignature(prev);
      const nextSignature = getAddonSelectionSignature(normalized);
      return prevSignature === nextSignature ? prev : normalized;
    });
  }, [showAddonModal, availableAddons, selectionRulesByGroup]);

  const pruneInvalidDependentSelections = (selections) => {
    return normalizeAddonSelections(selections);
  };

  const toggleAddonSelection = (addon) => {
    const currentAddonKey = getAddonIdentityKey(addon);

    setSelectedAddons(prev => {
      const exists = prev.find((a) => getAddonIdentityKey(a) === currentAddonKey);
      if (exists) {
        return normalizeAddonSelections(prev.filter((a) => getAddonIdentityKey(a) !== currentAddonKey));
      } else {
        const groupId = addon?.groupId;
        const rule = selectionRulesByGroup?.[String(groupId)];
        if (rule) {
          const selectedInGroup = prev.filter((item) => item?.groupId === groupId);
          if (Number(rule.maxSelection || 99) === 1 && selectedInGroup.length >= 1) {
            const withoutGroup = prev.filter((item) => item?.groupId !== groupId);
            return normalizeAddonSelections([...withoutGroup, addon]);
          }
          if (selectedInGroup.length >= Number(rule.maxSelection || 99)) {
            toast.error(`Max ${rule.maxSelection} selection allowed in ${rule.groupName}`, { important: true });
            return prev;
          }
        }
        return normalizeAddonSelections([...prev, addon]);
      }
    });
  };

  const openSpecialRequestEditor = (item) => {
    setEditingSpecialRequest({
      open: true,
      cartItemId: item.cartItemId,
      text: item.specialInstructions || ''
    });
  };

  const closeSpecialRequestEditor = () => {
    setEditingSpecialRequest({
      open: false,
      cartItemId: null,
      text: ''
    });
  };

  const saveSpecialRequest = () => {
    const nextText = String(editingSpecialRequest.text || '').slice(0, 100).trim();

    setCart(prev => prev.map((item) => {
      if (item.cartItemId !== editingSpecialRequest.cartItemId) {
        return item;
      }

      return {
        ...item,
        specialInstructions: nextText
      };
    }));

    closeSpecialRequestEditor();
  };

  const addCartItem = (product, addons = [], specialInstructions = '') => {
    const normalizedAddons = normalizeAddonSelections(addons);
    const cartItem = {
      ...product,
      quantity: 1,
      specialInstructions: specialInstructions.trim(),
      selectedAddons: normalizedAddons,
      cartItemId: `${product.productId}_${Date.now()}` // Unique ID for cart item
    };

    setCart(prev => [...prev, cartItem]);
    return cartItem;
  };

  // Add to cart with selected addons
  const addToCartWithAddons = (product, addons) => {
    const normalizedAddons = normalizeAddonSelections(addons);
    const nextSpecialInstructions = String(pendingSpecialInstructions || '').trim();

    if (editingCartItemId) {
      setCart((prev) => prev.map((item) => {
        if (item.cartItemId !== editingCartItemId) {
          return item;
        }

        return {
          ...item,
          selectedAddons: normalizedAddons,
          specialInstructions: nextSpecialInstructions,
        };
      }));
      closeAddonModal();
      toast.success('Item updated');
      return;
    }

    addCartItem(product, normalizedAddons, nextSpecialInstructions);
    closeAddonModal();
  };

  const toggleInstructionChip = (chip) => {
    const currentTokens = pendingSpecialInstructions
      .split('|')
      .map(token => token.trim())
      .filter(Boolean);
    const alreadySelected = currentTokens.some(
      token => token.toLowerCase() === chip.toLowerCase()
    );
    const nextTokens = alreadySelected
      ? currentTokens.filter(token => token.toLowerCase() !== chip.toLowerCase())
      : [...currentTokens, chip];
    setPendingSpecialInstructions(nextTokens.join(' | ').slice(0, 100));
  };

  const validateGroupSelections = () => {
    const normalizedSelections = normalizeAddonSelections(selectedAddons);
    const grouped = (normalizedSelections || []).reduce((acc, item) => {
      const key = item?.groupId || 'default';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    for (const [groupId, rule] of Object.entries(selectionRulesByGroup || {})) {
      const dependsOnGroupId = rule?.dependsOnGroupId;
      if (dependsOnGroupId) {
        const parentSelectedCount = grouped[String(dependsOnGroupId)] || 0;
        if (parentSelectedCount === 0) {
          continue;
        }
      }

      const selectedCount = grouped[groupId] || 0;
      const min = Number(rule?.minSelection || 0);
      const max = Number(rule?.maxSelection || 99);

      if (rule?.isRequired && selectedCount === 0) {
        toast.error(`Please select at least one option for ${rule.groupName}`, { important: true });
        return false;
      }
      if (selectedCount < min) {
        toast.error(`Select minimum ${min} option(s) for ${rule.groupName}`, { important: true });
        return false;
      }
      if (selectedCount > max) {
        toast.error(`Select maximum ${max} option(s) for ${rule.groupName}`, { important: true });
        return false;
      }
    }
    return true;
  };

  const addToCart = (product) => {
    showAddonSelectionModal(product);
  };

  const normalizedSelectedAddons = normalizeAddonSelections(selectedAddons);

  const updateQuantity = (cartItemId, change) => {
    setCart(prev => prev.map(item => {
      if (item.cartItemId === cartItemId) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (cartItemId) => {
    setCart(prev => prev.filter(item => item.cartItemId !== cartItemId));
    toast.info('Item removed');
  };

  const clearCart = () => {
    try {
      // Clear from localStorage
      window.localStorage.removeItem(cartStorageKey);
      // Reset cart state
      setCart([]);
      setAppliedCoupon(null);
      setCouponCode('');
      setShowClearCartConfirm(false);
      // Show success notification
      toast.success('Cart cleared successfully!');
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const openClearCartConfirm = () => {
    if (cart.length === 0) return;
    setShowClearCartConfirm(true);
  };

  const closeClearCartConfirm = () => {
    setShowClearCartConfirm(false);
  };

  const getCartSubtotal = () => {
    return cart.reduce((total, item) => {
      const basePrice = item.discountPrice || item.price;
      const addonsPrice = (item.selectedAddons || []).reduce((sum, addon) => sum + Number(addon.addonPrice || 0), 0);
      const itemTotal = (basePrice + addonsPrice) * item.quantity;
      return total + itemTotal;
    }, 0);
  };

  const isMrpCartItem = (item) => Boolean(
    item?.categoryIsMrp ?? item?.category_is_mrp ?? item?.isMrp ?? item?.is_mrp
  );

  const getTaxableCartSubtotal = () => {
    return cart.reduce((total, item) => {
      if (isMrpCartItem(item)) {
        return total;
      }

      const basePrice = item.discountPrice || item.price;
      const addonsPrice = (item.selectedAddons || []).reduce((sum, addon) => sum + Number(addon.addonPrice || 0), 0);
      const itemTotal = (basePrice + addonsPrice) * item.quantity;
      return total + itemTotal;
    }, 0);
  };

  const buildDeliverySettingsFromRestaurant = (restaurantData) => {
    if (!restaurantData) return null;
    const slabs = Array.isArray(restaurantData?.deliverySlabs)
      ? restaurantData.deliverySlabs
      : [];

    const normalizedSlabs = slabs
      .filter((slab) => slab?.isActive !== false)
      .map((slab) => ({
        ...slab,
        minOrderAmount: Number(slab?.minOrderAmount || slab?.min || 0),
        maxOrderAmount: slab?.maxOrderAmount == null && slab?.max == null
          ? null
          : Number(slab?.maxOrderAmount ?? slab?.max),
        deliveryCharge: Number(slab?.deliveryCharge || slab?.charge || 0),
        displayOrder: Number(slab?.displayOrder ?? 9999)
      }))
      .sort((a, b) => {
        const orderDiff = a.displayOrder - b.displayOrder;
        if (orderDiff !== 0) return orderDiff;
        return a.minOrderAmount - b.minOrderAmount;
      });

    if (normalizedSlabs.length === 0) return null;

    return {
      isDeliveryEnabled: restaurantData?.isDeliveryEnabled !== false,
      isSlabPricingEnabled: restaurantData?.isSlabPricingEnabled === true || normalizedSlabs.length > 0,
      minimumOrderAmountForDelivery: Number(restaurantData?.minimumOrderAmountForDelivery || 0),
      fallbackDeliveryCharge: Number(restaurantData?.fallbackDeliveryCharge || 0),
      slabs: normalizedSlabs
    };
  };

  const normalizeDeliverySlabs = () => {
    const slabs = Array.isArray(deliverySettings?.slabs) ? deliverySettings.slabs : [];
    return slabs
      .filter((slab) => slab?.isActive !== false)
      .map((slab) => ({
        ...slab,
        minOrderAmount: Number(slab?.minOrderAmount || 0),
        maxOrderAmount: slab?.maxOrderAmount == null ? null : Number(slab.maxOrderAmount),
        deliveryCharge: Number(slab?.deliveryCharge || 0),
        displayOrder: Number(slab?.displayOrder ?? 9999)
      }))
      .sort((a, b) => {
        const orderDiff = a.displayOrder - b.displayOrder;
        if (orderDiff !== 0) return orderDiff;
        return a.minOrderAmount - b.minOrderAmount;
      });
  };

  const buildDeliverySlabInsight = () => {
    if (fulfillmentType !== 'DELIVERY') {
      return null;
    }
    const slabs = normalizeDeliverySlabs();
    const subtotal = Number(getCartSubtotal() || 0);
    if (subtotal <= 0) return null;

    const currentCharge = Number(getCartPricing().deliveryCharge || 0);

    if (!deliverySettings?.isDeliveryEnabled) {
      return {
        message: currentCharge > 0
          ? `Delivery charges ₹${currentCharge.toFixed(0)} apply at checkout.`
          : 'FREE Delivery is applicable on your order.',
        tone: currentCharge > 0 ? 'saving' : 'free'
      };
    }

    if (currentCharge <= 0) {
      return {
        message: 'FREE Delivery is applicable on your order.',
        tone: 'free'
      };
    }

    if (slabs.length === 0 || !deliverySettings?.isSlabPricingEnabled) {
      const minimumOrderAmount = Number(deliverySettings?.minimumOrderAmountForDelivery || 0);
      if (minimumOrderAmount > 0 && subtotal < minimumOrderAmount) {
        const delta = Math.max(minimumOrderAmount - subtotal, 0);
        return {
          message: `Add ₹${delta.toFixed(0)} more to reach the minimum order for delivery. Current delivery charge ₹${currentCharge.toFixed(0)}.`,
          tone: 'saving'
        };
      }

      return {
        message: `Delivery charges ₹${currentCharge.toFixed(0)} apply at checkout.`,
        tone: 'saving'
      };
    }

    const nextSlab = slabs.find((slab) => slab.minOrderAmount > subtotal && slab.deliveryCharge < currentCharge);

    if (!nextSlab) {
      return {
        message: `Delivery charges ₹${currentCharge.toFixed(0)} apply at checkout.`,
        tone: 'saving'
      };
    }

    const delta = Math.max(nextSlab.minOrderAmount - subtotal, 0);
    if (delta <= 0) {
      return {
        message: `Delivery charges ₹${currentCharge.toFixed(0)} apply at checkout.`,
        tone: 'saving'
      };
    }

    if (nextSlab.deliveryCharge <= 0) {
      return {
        message: `Current delivery charge ₹${currentCharge.toFixed(0)}. Add ₹${delta.toFixed(0)} more to avail FREE Delivery.`,
        tone: 'free'
      };
    }

    return {
      message: `Current delivery charge ₹${currentCharge.toFixed(0)}. Add ₹${delta.toFixed(0)} more to reduce delivery charges to ₹${nextSlab.deliveryCharge.toFixed(0)}.`,
      tone: 'saving'
    };
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const subtotal = Number(getCartSubtotal() || 0);
    if (!deliverySettings) return;
    const slabs = normalizeDeliverySlabs();
    const currentCharge = Number(getCartPricing().deliveryCharge || 0);
    const currentSlab = slabs.find((slab) => {
      const min = Number(slab?.minOrderAmount || 0);
      const max = slab?.maxOrderAmount == null ? null : Number(slab.maxOrderAmount);
      return subtotal >= min && (max == null || subtotal <= max);
    }) || null;
    const nextSlab = slabs.find((slab) => slab.minOrderAmount > subtotal && slab.deliveryCharge < currentCharge) || null;

    console.debug('[DELIVERY_SLABS] Insight recompute', {
      restaurantId,
      subtotal,
      deliveryCharge: currentCharge,
      slabs,
      currentSlab,
      nextSlab,
      insight: buildDeliverySlabInsight()
    });
  }, [restaurantId, cart, deliverySettings]);

  const resolveDeliveryCharge = (subtotal) => {
    const safeSubtotal = Number(subtotal || 0);
    if (!deliverySettings?.isDeliveryEnabled) {
      return 0;
    }

    if (deliverySettings?.isSlabPricingEnabled) {
      const slabs = Array.isArray(deliverySettings?.slabs) ? deliverySettings.slabs : [];
      for (const slab of slabs) {
        const min = Number(slab?.minOrderAmount || 0);
        const max = slab?.maxOrderAmount === null || slab?.maxOrderAmount === undefined
          ? null
          : Number(slab?.maxOrderAmount);
        const inRange = safeSubtotal >= min && (max === null || safeSubtotal <= max);
        if (inRange) {
          return Number(slab?.deliveryCharge || 0);
        }
      }
    }

    return Number(deliverySettings?.fallbackDeliveryCharge || 0);
  };

  const getBaseCartPricing = () => {
    const isDeliveryOrder = fulfillmentType === 'DELIVERY';
    const subtotal = getCartSubtotal();
    const taxableSubtotal = getTaxableCartSubtotal();
    const minimumOrderAmount = Number(deliverySettings?.minimumOrderAmountForDelivery || 0);
    const belowMinimum = isDeliveryOrder && minimumOrderAmount > 0 && subtotal < minimumOrderAmount;
    const deliveryCharge = isDeliveryOrder ? resolveDeliveryCharge(subtotal) : 0;
    const taxPercentage = Number(gstSettings?.taxPercentage || 0);
    const taxEnabled = Boolean(gstSettings?.isTaxEnabled) && taxPercentage > 0;
    // Delivery charge is not taxable — compute tax only on taxable subtotal
    const taxableAmount = taxableSubtotal;
    const taxAmount = taxEnabled ? (taxableAmount * taxPercentage) / 100 : 0;
    const totalBeforeDiscount = subtotal + deliveryCharge + taxAmount;

    return {
      subtotal,
      deliveryCharge,
      taxAmount,
      taxPercentage,
      taxEnabled,
      totalBeforeDiscount,
      minimumOrderAmount,
      belowMinimum,
    };
  };

  const getCartPricing = () => {
    const base = getBaseCartPricing();
    const rawDiscount = Number(appliedCoupon?.discountAmount || 0);
    const discountAmount = Number.isFinite(rawDiscount)
      ? Math.min(Math.max(rawDiscount, 0), base.totalBeforeDiscount)
      : 0;
    const total = Math.max(base.totalBeforeDiscount - discountAmount, 0);

    return {
      subtotal: base.subtotal,
      deliveryCharge: base.deliveryCharge,
      taxAmount: base.taxAmount,
      taxPercentage: base.taxPercentage,
      taxEnabled: base.taxEnabled,
      totalBeforeDiscount: base.totalBeforeDiscount,
      discountAmount,
      total,
      minimumOrderAmount: base.minimumOrderAmount,
      belowMinimum: base.belowMinimum,
      appliedCouponCode: appliedCoupon?.couponCode || null,
    };
  };

  const getCartTotal = () => getCartPricing().total;
  const deliverySlabInsight = buildDeliverySlabInsight();
  const stickyFooterDeliveryInsight = fulfillmentType !== 'DELIVERY'
    ? null
    : (deliverySlabInsight || {
      message: getCartPricing().deliveryCharge > 0
        ? `Delivery charges ₹${Number(getCartPricing().deliveryCharge || 0).toFixed(0)} apply at checkout.`
        : 'FREE Delivery is applicable on your order.',
      tone: getCartPricing().deliveryCharge > 0 ? 'saving' : 'free'
    });

  const hasValidSelectedLocality = Boolean(
    selectedLocality
    && String(selectedLocality?.localityName || '').trim()
    && String(selectedLocality?.localityCode || '').trim()
  );

  const requiredHint = 'Fill this detail';
  const isFilled = (value) => String(value || '').trim().length > 0;

  const handleApplyCoupon = async (overrideCode) => {
    const code = String(overrideCode ?? couponCode ?? '').trim().toUpperCase();
    if (!code) {
      toast.error('Please enter a coupon code');
      return;
    }

    const pricing = getBaseCartPricing();
    if (pricing.totalBeforeDiscount <= 0) {
      toast.error('Cart total must be greater than 0');
      return;
    }

    setApplyingCoupon(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/coupons/apply`,
        {
          restaurantId: parseInt(restaurantId),
          couponCode: code,
          amount: Number(pricing.totalBeforeDiscount.toFixed(2)),
        },
        { headers: getPublicRequestHeaders() }
      );

      if (!response?.data?.success || !response?.data?.data) {
        throw new Error(response?.data?.message || 'Failed to apply coupon');
      }

      setAppliedCoupon(response.data.data);
      setCouponCode(response.data.data.couponCode || code);
      toast.success('Coupon applied successfully');
    } catch (error) {
      setAppliedCoupon(null);
      const message = error?.response?.data?.message || error?.message || 'Invalid coupon';
      toast.error(message);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const resolveCouponOwnerId = () => {
    const candidates = [
      restaurant?.ownerId,
      restaurant?.vendorId,
      restaurant?.owner?.id,
      restaurant?.owner?.ownerId,
      restaurant?.vendor?.id,
      restaurant?.userId,
      restaurant?.user?.id,
      restaurant?.createdBy,
      restaurant?.createdById
    ];
    const directMatch = candidates.find((value) => Number.isFinite(Number(value)));
    if (Number.isFinite(Number(directMatch))) return Number(directMatch);

    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem('ownerId')
      || window.localStorage.getItem('vendorId')
      || window.localStorage.getItem('owner_id')
      || window.localStorage.getItem('vendor_id');
    return Number.isFinite(Number(stored)) ? Number(stored) : null;
  };

  const isCouponWithinDateRange = (coupon) => {
    const now = new Date();
    const validFrom = coupon?.validFrom ? new Date(coupon.validFrom) : null;
    const validTill = coupon?.validTill ? new Date(coupon.validTill) : null;
    if (validFrom && !Number.isNaN(validFrom.getTime()) && validFrom > now) return false;
    if (validTill && !Number.isNaN(validTill.getTime()) && validTill < now) return false;
    return true;
  };

  const formatCouponDescription = (coupon) => {
    const discountType = String(coupon?.discountType || '').toUpperCase();
    const discountValue = Number(coupon?.discountValue || 0);
    const minOrderValue = Number(coupon?.minimumOrderValue || 0);
    const maxDiscount = Number(coupon?.maxDiscountValue || coupon?.maxDiscount || 0);
    const upToValue = Number(coupon?.upToValue ?? coupon?.up_to_value ?? 0);

    let baseText = '';
    if (discountType === 'PERCENT') {
      baseText = `${discountValue}% Off`;
    } else {
      baseText = `Get ₹${discountValue.toFixed(0)} Off`;
    }

    if (minOrderValue > 0) {
      baseText += ` on orders above ₹${minOrderValue.toFixed(0)}`;
    }

    const suffixes = [];
    if (discountType === 'PERCENT') {
      if (upToValue > 0) {
        suffixes.push(`Up to ₹${upToValue.toFixed(0)}`);
      } else if (maxDiscount > 0) {
        suffixes.push(`Up to ₹${maxDiscount.toFixed(0)}`);
      }
    }

    if (suffixes.length > 0) {
      baseText = `${baseText} • ${suffixes.join(' • ')}`;
    }

    return baseText;
  };

  const formatCouponExpiry = (coupon) => {
    const validTill = coupon?.validTill ? new Date(coupon.validTill) : null;
    if (!validTill || Number.isNaN(validTill.getTime())) return '';
    return `Valid till ${validTill.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  };

  const fetchAvailableCoupons = async () => {
    const ownerId = resolveCouponOwnerId();
    if (!ownerId) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[COUPON] OwnerId not available for coupon list', {
          restaurantId,
          restaurantOwnerId: restaurant?.ownerId,
          restaurantVendorId: restaurant?.vendorId,
        });
      }
      setAvailableCoupons([]);
      setCouponsError('Coupons are not available for this restaurant.');
      return;
    }

    setCouponsLoading(true);
    setCouponsError('');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/vendor/coupons/owner/${ownerId}`,
        {
          params: {
            restaurantId: Number(restaurantId) || undefined
          },
          headers: getPublicRequestHeaders()
        }
      );

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[COUPON] Coupons API response', {
          ownerId,
          restaurantId,
          responseData: response?.data
        });
      }

      const payload = response?.data?.data || response?.data || [];
      const list = Array.isArray(payload) ? payload : [];
      const filtered = list.filter((coupon) => {
        if (!coupon || coupon?.isActive === false) return false;
        if (!isCouponWithinDateRange(coupon)) return false;
        if (coupon?.applyToAll === true || coupon?.restaurantId == null) return true;
        return Number(coupon.restaurantId) === Number(restaurantId);
      });

      setAvailableCoupons(filtered);
      if (filtered.length === 0) {
        setCouponsError('No active coupons available right now.');
      }
    } catch (error) {
      setAvailableCoupons([]);
      setCouponsError(error?.response?.data?.message || error?.message || 'Unable to load coupons');
    } finally {
      setCouponsLoading(false);
    }
  };

  const handleToggleCoupons = async () => {
    const nextState = !showCouponList;
    setShowCouponList(nextState);
    if (nextState && availableCoupons.length === 0 && !couponsLoading) {
      await fetchAvailableCoupons();
    }
  };

  useEffect(() => {
    if (!showCouponList) return;
    if (availableCoupons.length > 0 || couponsLoading) return;
    if (!restaurant?.ownerId && !restaurant?.vendorId) return;

    fetchAvailableCoupons();
  }, [showCouponList, restaurant?.ownerId, restaurant?.vendorId]);

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  // ── Razorpay Checkout ─────────────────────────────────────────
  const handleRazorpayCheckout = async () => {
    const normalizedPhone = String(checkoutForm.phone || '').replace(/\D/g, '').slice(0, 10);
    const isDeliveryOrder = fulfillmentType === 'DELIVERY';
    const isDriveThroughOrder = fulfillmentType === 'DRIVETHROUGH';

    if (!checkoutForm.name.trim() || !normalizedPhone) {
      setPaymentError('Name and WhatsApp number are required');
      return;
    }

    if (normalizedPhone.length !== 10) {
      setPaymentError('Please enter a valid 10-digit WhatsApp number');
      return;
    }

    if (isDeliveryOrder) {
      if (!hasValidSelectedLocality) {
        setPaymentError('Please select a valid locality');
        return;
      }

      const trimmedAddress = String(checkoutForm.addressLine || '').trim();
      if (!trimmedAddress) {
        setPaymentError('Please enter complete delivery address');
        if (addressLineRef.current) {
          addressLineRef.current.focus();
        }
        return;
      }
    }

    if (isDriveThroughOrder) {
      const vehicleNumber = String(checkoutForm.vehicleNumber || '').trim();
      if (!vehicleNumber) {
        setPaymentError('Please enter vehicle number for drive-through order');
        return;
      }
    }

    const trimmedAddress = String(checkoutForm.addressLine || '').trim();
    if (isDeliveryOrder && !trimmedAddress) {
      setPaymentError('Please enter complete delivery address');
      return;
    }

    const effectiveLatitude = Number(customerLocation?.latitude);
    const effectiveLongitude = Number(customerLocation?.longitude);
    const selectedLocalityLatitude = Number(selectedLocality?.latitude);
    const selectedLocalityLongitude = Number(selectedLocality?.longitude);
    const isDummyCoordinates = (lat, lng) => lat === 0.5 && lng === 0.5;
    const hasCustomerGps = Number.isFinite(effectiveLatitude)
      && Number.isFinite(effectiveLongitude)
      && !isDummyCoordinates(effectiveLatitude, effectiveLongitude);
    const hasLocalityGps = Number.isFinite(selectedLocalityLatitude)
      && Number.isFinite(selectedLocalityLongitude)
      && !isDummyCoordinates(selectedLocalityLatitude, selectedLocalityLongitude);
    const finalLatitude = hasCustomerGps
      ? effectiveLatitude
      : hasLocalityGps
        ? selectedLocalityLatitude
        : null;
    const finalLongitude = hasCustomerGps
      ? effectiveLongitude
      : hasLocalityGps
        ? selectedLocalityLongitude
        : null;
    const hasGps = Number.isFinite(finalLatitude) && Number.isFinite(finalLongitude);

    const pricing = getCartPricing();
    if (pricing.belowMinimum) {
      setPaymentError(`Minimum order for delivery is Rs ${pricing.minimumOrderAmount.toFixed(2)}.`);
      return;
    }

    setPaymentError('');
    setProcessingPayment(true);

    const buildOrderPayload = ({ paymentStatus, paymentFailureReason, razorpayOrderId, razorpayPaymentId }) => {
      const pricing = getCartPricing();
      const total = pricing.total;
      const composedAddress = isDeliveryOrder
        ? [
            checkoutForm.addressLine,
            checkoutForm.landmark,
            selectedLocality?.localityName,
            checkoutForm.city,
          ].map(v => (v || '').trim()).filter(Boolean).join(', ')
        : null;

      const singleLineAddress = composedAddress;

      const combinedInstructions = [
        (checkoutForm.deliveryInstructions || '').trim(),
        isDriveThroughOrder ? `Vehicle color: ${String(checkoutForm.vehicleColor || '').trim() || 'N/A'}` : null,
        isDriveThroughOrder ? `Vehicle number: ${String(checkoutForm.vehicleNumber || '').trim() || 'N/A'}` : null,
        customerLocation.accuracy ? `GPS accuracy: ${customerLocation.accuracy}m` : null,
        paymentFailureReason ? `Payment note: ${paymentFailureReason}` : null
      ].filter(Boolean).join(' | ');

      console.log('[LOCALITY] Building order payload with mapped locality + coordinates', {
        localityName: selectedLocality?.localityName || null,
        localityCode: selectedLocality?.localityCode || null,
        selectedLocalityLatitude: hasLocalityGps ? selectedLocalityLatitude : null,
        selectedLocalityLongitude: hasLocalityGps ? selectedLocalityLongitude : null,
        deliveryLatitude: hasGps ? finalLatitude : null,
        deliveryLongitude: hasGps ? finalLongitude : null,
      });

      return {
        sessionId,
        restaurantId: Number(restaurantId),
        customerName: checkoutForm.name,
        customerPhone: normalizedPhone,
        customerEmail: checkoutForm.email || '',
        orderType: fulfillmentType,
        vehicleColor: isDriveThroughOrder ? (String(checkoutForm.vehicleColor || '').trim() || null) : null,
        vehicleNumber: isDriveThroughOrder ? (String(checkoutForm.vehicleNumber || '').trim() || null) : null,
        deliveryAddress: isDeliveryOrder ? (composedAddress || null) : null,
        deliveryCity: isDeliveryOrder ? ((restaurant?.city || '').trim() || null) : null,
        deliveryLatitude: isDeliveryOrder && hasGps ? finalLatitude : null,
        deliveryLongitude: isDeliveryOrder && hasGps ? finalLongitude : null,
        latitude: isDeliveryOrder ? (hasLocalityGps ? selectedLocalityLatitude : (hasGps ? finalLatitude : null)) : null,
        longitude: isDeliveryOrder ? (hasLocalityGps ? selectedLocalityLongitude : (hasGps ? finalLongitude : null)) : null,
        localityLatitude: isDeliveryOrder && hasLocalityGps ? selectedLocalityLatitude : null,
        localityLongitude: isDeliveryOrder && hasLocalityGps ? selectedLocalityLongitude : null,
        deliveryInstructions: combinedInstructions || null,
        singleLineAddress: isDeliveryOrder ? (singleLineAddress || null) : null,
        addressLine: isDeliveryOrder ? ((checkoutForm.addressLine || '').trim() || null) : null,
        landmark: isDeliveryOrder ? ((checkoutForm.landmark || '').trim() || null) : null,
        localityCode: isDeliveryOrder ? (selectedLocality?.localityCode || null) : null,
        localityName: isDeliveryOrder ? (selectedLocality?.localityName || null) : null,
        locality_code: isDeliveryOrder ? (selectedLocality?.localityCode || null) : null,
        locality_name: isDeliveryOrder ? (selectedLocality?.localityName || null) : null,
        localityInfo: {
          name: isDeliveryOrder ? (selectedLocality?.localityName || null) : null,
          code: isDeliveryOrder ? (selectedLocality?.localityCode || null) : null
        },
        formattedDetails: {
          city: isDeliveryOrder ? ((restaurant?.city || '').trim() || null) : null,
          state: 'Rajasthan',
          state_code: 'RJ',
          state_district: isDeliveryOrder ? ((restaurant?.city || '').trim() || null) : null,
          town: isDeliveryOrder ? (selectedLocality?.localityName || null) : null,
          road: isDeliveryOrder ? ((checkoutForm.addressLine || '').trim() || null) : null,
          country: 'India'
        },
        paymentMethod: 'ONLINE',
        paymentStatus,
        razorpayOrderId: razorpayOrderId || null,
        razorpayPaymentId: razorpayPaymentId || null,
        couponId: appliedCoupon?.couponId || null,
        couponCode: pricing.appliedCouponCode || null,
        discountAmount: Number(pricing.discountAmount || 0),
        items: cart.map(item => {
          const selectedAddons = Array.isArray(item.selectedAddons) ? item.selectedAddons : [];
          const selectedAddonIds = selectedAddons
            .filter((addon) => !!(addon?.legacyAddonId || addon?.addonId) && !addon?.optionId)
            .map((addon) => Number(addon.legacyAddonId || addon.addonId));
          const modifierSelections = selectedAddons.reduce((acc, addon) => {
            const groupId = addon?.groupId;
            const optionId = addon?.optionId;
            if (!groupId || !optionId) {
              return acc;
            }

            const key = String(groupId);
            if (!acc[key]) {
              acc[key] = [];
            }
            if (!acc[key].includes(Number(optionId))) {
              acc[key].push(Number(optionId));
            }
            return acc;
          }, {});
          const modifierOptionIds = Object.values(modifierSelections).flat();

          return {
            productId: item.productId,
            quantity: item.quantity,
            specialInstructions: item.specialInstructions || '',
            selectedAddonIds,
            selectedModifiers: modifierSelections,
            modifierSelections,
            addons: selectedAddonIds.map((addonId) => ({ addonId })),
            modifierOptionIds,
          };
        }),
        taxAmount: Number(pricing.taxAmount || 0),
        taxPercentage: Number(pricing.taxPercentage || 0),
        totalAmount: total,
      };
    };

    const createPaidOrder = async ({ razorpayOrderId, razorpayPaymentId }) => {
      await axios.post(
        `${API_BASE_URL}/api/vendor/orders/restaurant/${restaurantId}`,
        buildOrderPayload({
          paymentStatus: 'COMPLETED',
          paymentFailureReason: null,
          razorpayOrderId,
          razorpayPaymentId,
        }),
        { headers: getPublicRequestHeaders() }
      );
    };

    try {
      const total = getCartTotal();

      // 1. Request order details from backend (key-only OR full order creation)
      const orderRes = await axios.post(`${API_BASE_URL}/api/vendor/payment/create-order`, {
        restaurantId: parseInt(restaurantId),
        amount: total,
        currency: 'INR',
        customerName: checkoutForm.name?.trim() || '',
        customerEmail: checkoutForm.email?.trim() || '',
        customerPhone: normalizedPhone,
      }, { headers: getPublicRequestHeaders() });

      if (!orderRes.data.success) throw new Error(orderRes.data.error || 'Order creation failed');

      const paymentGatewayData = orderRes?.data?.data || {};
      const razorpayOrderId = (paymentGatewayData.razorpayOrderId || '').toString().trim();
      const keyId = (paymentGatewayData.keyId || '').toString().trim();
      const keyOnly = Boolean(paymentGatewayData.keyOnly);
      const amount = Number(paymentGatewayData.amount || 0);

      if (!/^rzp_(test|live)_[A-Za-z0-9]+$/.test(keyId)) {
        throw new Error('Payment gateway is not configured correctly. Please contact the vendor.');
      }

      if (!keyOnly && !razorpayOrderId) {
        throw new Error('Payment order is missing. Please try again in a moment.');
      }

      // 2. Load Razorpay checkout script
      const loadScript = () => new Promise(resolve => {
        if (window.Razorpay) { resolve(true); return; }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });

      const loaded = await loadScript();
      if (!loaded) throw new Error('Could not load payment gateway. Please try again.');

      // 3. Open Razorpay checkout
      await new Promise((resolve, reject) => {
        const razorpayThemeColor = currentTheme?.primaryColor || currentTheme?.secondaryColor || currentTheme?.accentColor || '#4299f5';
        const options = {
          key: keyId,
          amount: keyOnly
            ? Math.round(total * 100)
            : Math.max(100, Math.round(amount * 100) || Math.round(total * 100)),
          currency: 'INR',
          name: restaurant?.restaurantName || restaurant?.name || 'Order',
          description: `Order of ${getCartCount()} item(s)`,
          prefill: {
            name: checkoutForm.name,
            contact: normalizedPhone,
            email: checkoutForm.email || '',
          },
          theme: { color: razorpayThemeColor },
          handler: async (response) => {
            try {
              // 4a. Full mode: verify signature on backend
              if (!keyOnly && razorpayOrderId) {
                const verifyRes = await axios.post(`${API_BASE_URL}/api/vendor/payment/verify`, {
                  restaurantId: parseInt(restaurantId),
                  razorpayOrderId: response.razorpay_order_id,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                }, { headers: getPublicRequestHeaders() });
                if (!verifyRes.data.success) {
                  reject(new Error('Payment verification failed'));
                  return;
                }
              }

              // 4b. Create the order only after payment is confirmed
              await createPaidOrder({
                razorpayOrderId: response.razorpay_order_id || null,
                razorpayPaymentId: response.razorpay_payment_id
              });

              setCart([]);
              setShowCheckoutModal(false);
              toast.success('🎉 Payment successful! Your order has been placed.');
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('showOrderConfirmPopup', 'true');
              }
              setCurrentView('home');
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          modal: {
            ondismiss: () => { setProcessingPayment(false); resolve(); }
          },
        };
        // Attach order_id only in full mode
        if (!keyOnly && razorpayOrderId) {
          options.order_id = razorpayOrderId;
        }
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', async (resp) => {
          reject(new Error(resp.error?.description || 'Payment failed'));
        });
        rzp.open();
      });
    } catch (err) {
      console.error('Checkout error:', err);
      const backendErrorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.data?.message ||
        null;
      const effectiveErrorMessage = backendErrorMessage || err?.message || 'Payment flow failed';
      setPaymentError(effectiveErrorMessage || 'Payment failed. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getProductQuantity = (productId) => {
    const item = cart.find(item => item.productId === productId);
    return item ? item.quantity : 0;
  };

  const getCallNumber = () => {
    const raw = restaurant?.contactPhone || restaurant?.whatsappNumber || restaurant?.contactPhoneNumber || '';
    return String(raw || '').trim();
  };

  const normalizeSearchText = (value) => String(value || '').trim().toLowerCase();
  const normalizedDebouncedQuery = normalizeSearchText(debouncedSearchQuery);
  const getCategoryName = (category) => category?.categoryName || category?.name || '';
  const getCategoryOrder = (category) => Number(category?.order ?? category?.displayOrder ?? category?.sortOrder ?? category?.position ?? 9999);

  const sortedCategories = [...categories].sort((a, b) => {
    const orderDiff = getCategoryOrder(a) - getCategoryOrder(b);
    if (orderDiff !== 0) return orderDiff;
    return getCategoryName(a).localeCompare(getCategoryName(b));
  });

  const fallbackCategoryNames = Array.from(
    new Set(products.map((p) => p?.productCategory?.categoryName).filter(Boolean))
  );

  const orderedCategoryBuckets = [
    ...sortedCategories
      .filter((c) => getCategoryName(c))
      .map((c) => ({
        id: c?.categoryId ?? c?.id ?? null,
        name: getCategoryName(c),
        order: getCategoryOrder(c),
      })),
    ...fallbackCategoryNames
      .filter(
        (name) => !sortedCategories.some((c) => normalizeSearchText(getCategoryName(c)) === normalizeSearchText(name))
      )
      .map((name, index) => ({
        id: null,
        name,
        order: 10000 + index,
      })),
  ];

  const getProductCategoryName = (p) => p?.productCategory?.categoryName || 'Uncategorized';
  const getProductCategoryId = (p) => p?.productCategory?.categoryId ?? p?.categoryId ?? null;

  const categoryScopedProducts = products;

  const matchedCategoryNames = normalizedDebouncedQuery
    ? new Set(
      orderedCategoryBuckets
        .filter((bucket) => normalizeSearchText(bucket.name).includes(normalizedDebouncedQuery))
        .map((bucket) => normalizeSearchText(bucket.name))
    )
    : new Set();

  // Filter products by debounced search query with exact match prioritization
  const filteredProducts = !normalizedDebouncedQuery
    ? categoryScopedProducts
    : categoryScopedProducts
      .filter((p) => {
        const searchableText = [
          p.productName,
          p.description,
          p.productCategory?.categoryName,
          p.foodType,
          p.cuisineType,
          p.isVeg ? 'veg vegetarian' : 'non veg non-veg',
          p.price,
          p.discountPrice,
        ]
          .map((val) => normalizeSearchText(val))
          .join(' ');

        const productCategoryName = normalizeSearchText(getProductCategoryName(p));

        return searchableText.includes(normalizedDebouncedQuery)
          || matchedCategoryNames.has(productCategoryName);
      })
      .sort((a, b) => {
        // Exact match priority: product name exact match comes first
        const aNameExact = normalizeSearchText(a.productName) === normalizedDebouncedQuery ? 1 : 0;
        const bNameExact = normalizeSearchText(b.productName) === normalizedDebouncedQuery ? 1 : 0;
        if (aNameExact !== bNameExact) return bNameExact - aNameExact;

        // Category name exact match priority
        const aCategoryExact = normalizeSearchText(a.productCategory?.categoryName) === normalizedDebouncedQuery ? 1 : 0;
        const bCategoryExact = normalizeSearchText(b.productCategory?.categoryName) === normalizedDebouncedQuery ? 1 : 0;
        if (aCategoryExact !== bCategoryExact) return bCategoryExact - aCategoryExact;

        // Preserve original order for partial matches
        return 0;
      });

  const productBelongsToCategory = (product, bucket) => {
    const productCatId = getProductCategoryId(product);
    if (bucket?.id != null && productCatId != null) {
      return String(bucket.id) === String(productCatId);
    }
    return normalizeSearchText(getProductCategoryName(product)) === normalizeSearchText(bucket?.name);
  };

  const visibleBuckets = orderedCategoryBuckets;

  const groupedMenuSections = visibleBuckets
    .map((bucket) => ({
      ...bucket,
      items: filteredProducts.filter((product) => productBelongsToCategory(product, bucket)),
    }))
    .filter((section) => section.items.length > 0);

  const categoryNamesKey = groupedMenuSections.map((section) => section.name).join('|');

  useEffect(() => {
    setExpandedCategories((prev) => {
      let changed = false;
      const next = { ...prev };
      groupedMenuSections.forEach((section) => {
        if (next[section.name] == null) {
          next[section.name] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [categoryNamesKey]);

  const toggleCategoryExpanded = (name) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [name]: !(prev?.[name] !== false)
    }));
  };

  const productSequentialIndexMap = {};
  let sequentialImageIndex = 0;
  groupedMenuSections.forEach((section) => {
    section.items.forEach((product) => {
      if (product?.productId != null && productSequentialIndexMap[product.productId] == null) {
        productSequentialIndexMap[product.productId] = sequentialImageIndex;
        sequentialImageIndex += 1;
      }
    });
  });



  // Normalize template name - remove _QR_template suffix and capitalize properly
  // Direct map handles typos in stored template values (autmn, navratra, sakaranti, etc.)
  const TEMPLATE_ALIAS_MAP = {
    'Normal_QR_template': 'Normal',
    'Spring_QR_template': 'Spring',
    'summer_QR_template': 'Summer',
    'autmn_QR_template': 'Autumn',
    'Winter_qr_template': 'Winter',
    'Christmas_qr_template': 'Christmas',
    'Diwali_QR_template': 'Diwali',
    'EID_qr_template': 'Eid',
    'HOLI_OR_template': 'Holi',
    'navratra_qr_template': 'Navratri',
    'Makar_sakaranti_qr_template': 'Makar Sankranti',
    'Teej_QR_template': 'Teej',
    'Gangaur_QR_template': 'Gangaur',
    'Royal_Rajasthan_QR_template': 'Royal Rajasthan'
  };

  const normalizeTemplateName = (templateName) => {
    if (!templateName) return 'Normal';

    // Direct alias lookup first — handles typos like autmn, navratra, sakaranti
    if (TEMPLATE_ALIAS_MAP[templateName]) return TEMPLATE_ALIAS_MAP[templateName];

    // Fuzzy fallback: strip suffixes and match against known theme keys.
    const cleaned = String(templateName)
      .replace(/_QR_template/gi, '')
      .replace(/_OR_template/gi, '')
      .replace(/_template/gi, '')
      .replace(/[^a-zA-Z\s_-]/g, ' ')
      .replace(/[\s_-]+/g, ' ')
      .trim()
      .toLowerCase();

    if (!cleaned) return 'Normal';

    // Match against known theme keys in a tolerant way (exact/includes).
    const themeKeys = Object.keys(TEMPLATE_THEMES);
    const matched = themeKeys.find((key) => {
      const lowerKey = key.toLowerCase();
      return cleaned === lowerKey || cleaned.includes(lowerKey) || lowerKey.includes(cleaned);
    });

    return matched || 'Normal';
  };

  // Get theme based on selected template
  const templateKey = normalizeTemplateName(restaurant?.menuTemplate || 'Normal');
  console.log("Applied Theme:", templateKey, "Raw Template:", restaurant?.menuTemplate);

  const currentTheme = TEMPLATE_THEMES[templateKey] || TEMPLATE_THEMES['Normal'];
  const isRajasthanTheme = templateKey === 'Royal Rajasthan';

  // Text colors - Dark themes use light text on dark glass, light themes use dark text
  const textColor = currentTheme.isDark ? '#f1f5f9' : '#1e293b';
  const headerTextColor = currentTheme.isDark
    ? (currentTheme.accentColor || currentTheme.primaryColor)
    : currentTheme.primaryColor;
  const subtitleTextColor = currentTheme.isDark ? '#e2e8f0' : '#64748b';

  // CSS Animations - Enhanced with theme-specific animations
  const styles = `

    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&family=Great+Vibes&family=Pacifico&family=Righteous&family=Lobster&family=Mountains+of+Christmas:wght@700&family=Satisfy&family=Amiri:wght@400;700&family=Fredoka+One&family=Bebas+Neue&family=Dancing+Script:wght@700&family=Chewy&display=swap');

    /* Royal Rajasthan exact shutter loader */
    .rr-loader .shutter-panel { transition: transform 0.8s cubic-bezier(0.77, 0, 0.175, 1) !important; }
    .rr-loader.loaded .shutter-top { transform: translateY(-100%) !important; }
    .rr-loader.loaded .shutter-bottom { transform: translateY(100%) !important; }

    @keyframes rr-fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes rr-float-char { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
    @keyframes rr-namaste-hands { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
    @keyframes rr-head-nod { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(2deg); } }
    @keyframes rr-mustache-move { 0%, 100% { transform: scaleX(1); } 50% { transform: scaleX(1.05); } }

    .rr-loader .animate-fade-in { animation: rr-fade-in-up 0.8s ease-out forwards; }
    .rr-loader .animate-float { animation: rr-float-char 3s ease-in-out infinite; }
    .rr-loader .animate-hands-move { animation: rr-namaste-hands 2s ease-in-out infinite; }
    .rr-loader .animate-head-move { animation: rr-head-nod 4s ease-in-out infinite; transform-origin: 100px 110px; }
    .rr-loader .animate-mustache-move { animation: rr-mustache-move 2s ease-in-out infinite; transform-origin: center; }

    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 0;
      overflow-x: hidden;
      scroll-behavior: smooth;
    }

    h1, h2, h3, h4, h5, h6 {
      font-style: normal;
      font-variant: normal;
    }

    .restaurant-name-themed {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }

    @keyframes waterWaveFloat {
      0% { border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; transform: translate(-50%, -50%) rotate(0deg) scale(1); }
      50% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: translate(-50%, -50%) rotate(180deg) scale(1.05); }
      100% { border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; transform: translate(-50%, -50%) rotate(360deg) scale(1); }
    }

    /* 🔥 Premium Animations */
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(20px); filter: blur(5px); }
      to { opacity: 1; transform: translateY(0); filter: blur(0); }
    }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes bloom {
      0% { transform: scale(0) rotate(0deg); opacity: 0; }
      50% { opacity: 1; }
      100% { transform: scale(1) rotate(360deg); opacity: 1; }
    }

    @keyframes pulse-soft {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }

    @keyframes bounce-gentle {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-8px); }
    }

    @keyframes float-icon {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-5px) rotate(5deg); }
    }

    @keyframes spin-slow {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes twinkle {
      0%, 100% { opacity: 1; transform: scale(1); filter: brightness(1); }
      50% { opacity: 0.6; transform: scale(0.9); filter: brightness(1.2); }
    }

    @keyframes shimmer {
      0% { transform: translateX(-150%) skewX(-15deg); }
      100% { transform: translateX(150%) skewX(-15deg); }
    }

    @keyframes slideInFromBottom {
      from { transform: translateY(100%) scale(0.95); opacity: 0; }
      to { transform: translateY(0) scale(1); opacity: 1; }
    }

    @keyframes scaleInCurve {
      from { transform: scale(0.85); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }

    /* 🍂 Autumn QR Animations */
    @keyframes float-leaf {
      0% { transform: translateY(0) rotate(0deg); }
      50% { transform: translateY(-5px) rotate(10deg); }
      100% { transform: translateY(0) rotate(0deg); }
    }

    @keyframes pulse-warm {
      0%, 100% { transform: scale(1); opacity: 0.8; }
      50% { transform: scale(1.05); opacity: 1; }
    }

    /* 🎨 Holi QR Animations */
    @keyframes bounce-slow {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }

    @keyframes wiggle {
      0%, 100% { transform: rotate(-3deg); }
      50% { transform: rotate(3deg); }
    }

    @keyframes splash-pop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }

    /* ❄️ Winter QR Animations */
    @keyframes pulse-glow {
      0%, 100% { box-shadow: 0 0 10px rgba(56, 189, 248, 0.2); }
      50% { box-shadow: 0 0 20px rgba(56, 189, 248, 0.6); }
    }

    @keyframes fadeInStagger {
      from { opacity: 0; transform: translateY(15px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* Animation utility classes */
    .animate-fade-in {
      animation: fadeIn 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
      animation-delay: 0.1s;
      opacity: 0;
    }

    .animate-fade-in-delay {
      animation: fadeIn 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
      animation-delay: 0.35s;
      opacity: 0;
    }

    .animate-bloom {
      animation: bloom 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .animate-pulse {
      animation: pulse-soft 3.5s ease-in-out infinite;
    }

    .animate-float {
      animation: float-icon 3s ease-in-out infinite;
    }

    .animate-spin {
      animation: spin-slow 12s linear infinite;
    }

    .animate-twinkle {
      animation: twinkle 3s ease-in-out infinite;
    }

    .animate-bounce {
      animation: bounce-gentle 2.5s ease-in-out infinite;
    }

    .animate-slide-up {
      animation: slideInFromBottom 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    }

    .animate-scale-in {
      animation: scaleInCurve 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }

    .animate-leaf {
      animation: float-leaf 4s ease-in-out infinite;
    }

    .animate-pulse-warm {
      animation: pulse-warm 3s infinite ease-in-out;
    }

    .animate-wiggle {
      animation: wiggle 2s infinite ease-in-out;
    }

    .animate-pop {
      animation: splash-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }

    .animate-bounce-slow {
      animation: bounce-slow 3s infinite ease-in-out;
    }

    @keyframes float-icon {
      0%, 100% { transform: translate(-50%, -50%) translateY(0px) rotate(0deg); }
      50% { transform: translate(-50%, -50%) translateY(-5px) rotate(5deg); }
    }

    .animate-float {
      animation: float-icon 3s ease-in-out infinite;
    }

    .animate-pulse-glow {
      animation: pulse-glow 3s infinite;
    }

    /* ✨ Interactive Premium States */
    .card-hover {
      transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
      position: relative;
    }

    .card-hover::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      border-radius: inherit;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0);
      transition: box-shadow 0.4s ease;
      pointer-events: none;
    }

    .card-hover:hover {
      transform: translateY(-5px) scale(1.01);
      box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06) !important;
    }

    .card-hover:hover::before {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
    }

    .card-hover:active {
      transform: translateY(-2px) scale(0.99);
      transition: all 0.1s ease;
    }

    .btn-hover {
      transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
      position: relative;
      overflow: hidden;
    }

    .btn-hover::after {
      content: '';
      position: absolute;
      top: 0; left: 0; width: 100%; height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
      transform: translateX(-100%);
    }

    .btn-hover:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    }

    .btn-hover:hover::after {
      animation: shimmer 1.5s infinite;
    }

    .btn-hover:active {
      transform: scale(0.96);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .total-section {
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
    }

    .tax-note {
      font-size: 12px;
      color: #6b7280;
      margin-top: 4px;
      line-height: 1.4;
    }

    /* Hide Scrollbar */
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    
    /* Hide scrollbar globally but allow scrolling */
    html, body {
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    html::-webkit-scrollbar, body::-webkit-scrollbar { width: 0; height: 0; }
    ::-webkit-scrollbar { width: 0; height: 0; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: transparent; }

    /* Frosted Glass Effect */
    .glass-effect {
      backdrop-filter: blur(20px) saturate(200%);
      -webkit-backdrop-filter: blur(20px) saturate(200%);
      border: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.05);
    }

    /* Mobile Enhancements */
    /* .menu-mobile-fab desktop visible */

    .menu-mobile-fab {
      position: fixed !important;
      right: 24px;
      bottom: 90px;
      z-index: 1000;
      display: inline-flex;
      align-items: center;
      border: 2px solid color-mix(in srgb, var(--primary-color) 55%, white);
      backdrop-filter: blur(6px);
      border-radius: 999px;
      background: var(--primary-color);
      color: #ffffff;
      font-weight: 700;
      letter-spacing: 0.3px;
      font-size: 14px; 
      padding: 10px 16px; 
      box-shadow: 0 6px 12px color-mix(in srgb, var(--primary-color) 40%, rgba(0, 0, 0, 0.18));
      transition: transform 0.14s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.14s ease;
      cursor: pointer;
    }

    .menu-mobile-fab:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px color-mix(in srgb, var(--primary-color) 40%, rgba(0, 0, 0, 0.25));
    }

    .menu-mobile-fab:active {
      transform: scale(0.95);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.2);
    }


    .menu-mobile-fab {
      position: fixed !important;
      right: 24px;
      bottom: 90px;
      z-index: 1000;
      display: inline-flex;
      align-items: center;
      border: 2px solid color-mix(in srgb, var(--primary-color) 55%, white);
      backdrop-filter: blur(6px);
      border-radius: 999px;
      background: var(--primary-color);
      color: #ffffff;
      font-weight: 700;
      letter-spacing: 0.3px;
      font-size: 14px; 
      padding: 10px 16px; 
      box-shadow: 0 6px 12px color-mix(in srgb, var(--primary-color) 40%, rgba(0, 0, 0, 0.18));
      transition: transform 0.14s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.14s ease;
      cursor: pointer;
    }

    .menu-mobile-fab:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px color-mix(in srgb, var(--primary-color) 40%, rgba(0, 0, 0, 0.25));
    }

    .menu-mobile-fab:active {
      transform: scale(0.95);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.2);
    }

    .menu-btn {
      position: fixed;
      right: 16px;
      bottom: 90px;
      z-index: 1000;
    }
    .menu-btn.shifted-up {
      bottom: 200px;
    }
    .cart-popup {
      position: fixed;
      bottom: 0;
      width: auto;
      z-index: 900;
    }
    .main-container {
      padding-bottom: 100px;
    }

    .theme-rajasthan {
      background: #ffffff;
    }

    .theme-default {
      /* keep original theme backgrounds/animations */
    }

    /* Desktop-first menu grid (restore 4-5 items view) */
    .menu-grid {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 20px;
      align-items: stretch;
      margin-bottom: 24px;
    }

    .menu-card {
      background: var(--menu-card-bg, rgba(255,255,255,0.92));
      border: var(--menu-card-border, 1px solid rgba(0,0,0,0.08));
      border-radius: 16px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 100%;
      box-shadow: var(--menu-card-shadow, 0 8px 20px rgba(0,0,0,0.06));
    }

    .menu-card-image {
      width: 100%;
      height: 170px;
      object-fit: cover;
      display: block;
    }

    .menu-card-content {
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
    }

    .menu-card-footer {
      margin-top: auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .powered-text a {
      color: #2563eb;
      font-weight: 800;
      text-decoration: none;
    }

    .powered-text a.yelofast-link {
      color: #eab308;
    }

    .powered-text a:hover {
      text-decoration: underline;
    }

    .powered-text-container {
      border-radius: 10px;
      padding: 6px 10px;
    }

    .powered-logos-container {
      margin-top: 2px;
      border-radius: 10px;
      padding: 0;
      background: transparent;
      border: none;
    }

    .powered-logos {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
      margin-top: 0;
      flex-wrap: wrap;
    }

    .powered-logos a {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 88px;
      padding: 8px 12px;
      border-radius: 12px;
      background: transparent;
      border: none;
      text-decoration: none;
    }

    .powered-logos img {
      height: 40px;
      object-fit: contain;
      max-width: 160px;
      width: auto;
      background: transparent;
    }

    .powered-logos .trivana-logo {
      height: 130px;
      max-width: 400px;
      background: transparent;
      mix-blend-mode: multiply;
      object-fit: contain;
    }

    .add-btn-container {
      display: flex;
      justify-content: center;
      margin-top: 8px;
    }

    .category-nav {
      display: flex;
      overflow-x: auto;
      gap: 12px;
      position: sticky;
      top: 0;
      z-index: 100;
      background: color-mix(in srgb, #ffffff 88%, transparent);
      backdrop-filter: blur(8px);
    }

    @media (max-width: 1023px) {
      .menu-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }
    }

.menu-add-btn {
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  min-width: 80px;
  text-align: center;
}

.menu-mobile-sections {
  padding-bottom: 40px;
}

/* Unified add button used in list overlay */
.add-btn {
  background: white;
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  padding: 8px 0;
  width: 100%;
  border-radius: 10px;
  font-weight: 700;
  font-size: 15px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  text-transform: uppercase;
}

.qty-control {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.menu-qty-btn {
  width: 36px;
  height: 36px;
  font-size: 18px;
  background: transparent;
  border: none;
  color: var(--primary-color);
  font-weight: 800;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.menu-qty-text {
  font-size: 15px;
  font-weight: 700;
  color: var(--primary-color);
  min-width: 30px;
  text-align: center;
}

.menu-mobile-fab {
  position: fixed;
  right: 16px;
  bottom: 90px;
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  border: 2px solid color-mix(in srgb, var(--primary-color) 55%, white);
  backdrop-filter: blur(6px);
  border-radius: 999px;
  background: var(--primary-color);
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.3px;
  font-size: 12px; /* smaller */
  padding: 8px 12px; /* smaller */
  box-shadow: 0 6px 12px color-mix(in srgb, var(--primary-color) 40%, rgba(0, 0, 0, 0.18));
  transition: transform 0.14s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.14s ease;
}

.menu-mobile-fab:active {
  transform: scale(0.95);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.2);
}

.menu-mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 10030;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 14px 90px;
  animation: fadeIn 0.3s ease-out;
}

.menu-mobile-sheet {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 24px;
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
  animation: slideInFromBottom 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
  box-shadow: 0 -10px 40px rgba(0,0,0,0.2);
}

.menu-mobile-sheet-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.menu-mobile-cat-btn {
  border: 1px solid rgba(0,0,0,0.05);
  border-radius: 12px;
  text-align: left;
  padding: 14px 12px;
  font-size: 15px;
  font-weight: 600;
  background: #f8fafc;
  color: #334155;
  transition: all 0.2s ease;
}

.menu-mobile-cat-btn.active {
  background: #eff6ff;
  color: #2563eb;
  border-color: #bfdbfe;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.1);
}


    @media (max-width: 767px) {
      .menu-category-strip { display: flex !important; }
      .menu-grid {
        grid-template-columns: 1fr !important;
        gap: 16px !important;
      }

      .menu-card {
        display: flex !important;
        flex-direction: row-reverse !important;
        align-items: stretch !important;
        gap: 12px !important;
        padding: 12px !important;
        border-radius: 16px !important;
        min-height: 120px;
        overflow: hidden !important;
      }

      .menu-card-image {
        width: 100px !important;
        height: 100px !important;
        margin: 0 !important;
        border-radius: 12px !important;
        flex-shrink: 0 !important;
        object-fit: cover;
      }

      .menu-card-content {
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: space-between !important;
        min-width: 0;
        gap: 6px;
        flex: 1 1 auto !important;
      }

      .menu-card-header {
        display: flex !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 8px;
        margin-bottom: 4px !important;
      }

      .menu-card-title {
        font-size: 16px !important;
        font-weight: 600 !important;
        line-height: 1.3 !important;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin: 0 !important;
      }

      .menu-veg-badge {
        font-size: 10px !important;
        padding: 3px 6px !important;
        flex-shrink: 0;
      }

      .menu-description {
        display: -webkit-box !important;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 12px !important;
        margin-bottom: 6px !important;
        line-height: 1.4 !important;
        opacity: 0.8;
      }

      .menu-card-footer {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        gap: 8px;
        margin-top: auto;
      }

      .menu-price { font-size: 17px !important; font-weight: 600 !important; }
      .menu-old-price { font-size: 13px !important; opacity: 0.6; }

      .menu-add-btn {
        padding: 8px 16px !important;
        border-radius: 12px !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        min-width: 80px;
        text-align: center;
      }

      .menu-mobile-sections {
          padding-bottom: 40px;

      /* Unified add button used in list overlay */
      .add-btn {
        background: white;
        border: 2px solid var(--primary-color);
        color: var(--primary-color);
        padding: 8px 0;
        width: 100%;
        border-radius: 10px;
        font-weight: 700;
        font-size: 15px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        cursor: pointer;
        text-transform: uppercase;
      }

      .qty-control {
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 4px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.05);
      }

      .menu-qty-btn {
        width: 36px !important;
        height: 36px !important;
        font-size: 18px !important;
        background: transparent !important;
        border: none !important;
        color: var(--primary-color) !important;
        font-weight: 800 !important;
        cursor: pointer !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
      }

      .menu-qty-text {
        font-size: 15px !important;
        font-weight: 700 !important;
        color: var(--primary-color) !important;
        min-width: 30px !important;
        text-align: center;
      }

      .menu-mobile-fab {
        position: fixed !important;
        right: 16px;
        bottom: 90px;
        z-index: 1000;
        display: inline-flex;
        align-items: center;
        border: 2px solid color-mix(in srgb, var(--primary-color) 55%, white);
        backdrop-filter: blur(6px);
        border-radius: 999px;
        background: var(--primary-color);
        color: #ffffff;
        font-weight: 700;
        letter-spacing: 0.3px;
        font-size: 12px; /* smaller */
        padding: 8px 12px; /* smaller */
        box-shadow: 0 6px 12px color-mix(in srgb, var(--primary-color) 40%, rgba(0, 0, 0, 0.18));
        transition: transform 0.14s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.14s ease;
      }

      .menu-mobile-fab:active {
        transform: scale(0.95);
        box-shadow: 0 6px 14px rgba(0, 0, 0, 0.2);
      }

      .menu-mobile-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        z-index: 10030;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: 0 14px 90px;
        animation: fadeIn 0.3s ease-out;
      }

      .menu-mobile-sheet {
        width: 100%;
        max-width: 420px;
        background: #ffffff;
        border-radius: 24px;
        padding: 20px;
        max-height: 60vh;
        overflow-y: auto;
        animation: slideInFromBottom 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
        box-shadow: 0 -10px 40px rgba(0,0,0,0.2);
      }

      .menu-mobile-sheet-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }

      .menu-mobile-cat-btn {
        border: 1px solid rgba(0,0,0,0.05);
        border-radius: 12px;
        text-align: left;
        padding: 14px 12px;
        font-size: 15px;
        font-weight: 600;
        background: #f8fafc;
        color: #334155;
        transition: all 0.2s ease;
      }

      .menu-mobile-cat-btn.active {
        background: #eff6ff;
        color: #2563eb;
        border-color: #bfdbfe;
        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.1);
      }

      .powered-logos img {
        height: 30px;
        max-width: 130px;
      }

      .powered-logos .trivana-logo {
        height: 100px;
        max-width: 320px;
        object-fit: contain;
      }
    }

    /* ── Splash screen animations (QR Templates + Fallback) ── */
    @keyframes float-leaf { 0% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-5px) rotate(10deg); } 100% { transform: translateY(0) rotate(0deg); } }
    @keyframes pulse-warm { 0%, 100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.05); opacity: 1; } }
    @keyframes twinkle { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.3; transform: scale(0.7); } }
    @keyframes sway-tree { 0%, 100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
    @keyframes pop-in { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
    @keyframes flicker { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.9); } }
    @keyframes float-moon { 0% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-10px) rotate(5deg); } 100% { transform: translateY(0) rotate(0deg); } }
    @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
    @keyframes wiggle { 0%, 100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
    @keyframes splash-pop { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
    @keyframes float-kite { 0% { transform: translateY(0) rotate(0deg) translateX(0); } 25% { transform: translateY(-15px) rotate(5deg) translateX(10px); } 50% { transform: translateY(0) rotate(0deg) translateX(0); } 75% { transform: translateY(-15px) rotate(-5deg) translateX(-10px); } 100% { transform: translateY(0) rotate(0deg) translateX(0); } }
    @keyframes sway-kalash { 0%, 100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-10px) rotate(2deg); } }
    @keyframes bloom { 0% { transform: scale(0) rotate(0deg); opacity: 0; } 50% { opacity: 1; } 100% { transform: scale(1) rotate(360deg); opacity: 1; } }
    @keyframes pulse-soft { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
    @keyframes float-icon { 0%, 100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-5px) rotate(5deg); } }
    @keyframes animate-sun { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

    .animate-leaf { animation: float-leaf 4s ease-in-out infinite; }
    .animate-pulse-warm { animation: pulse-warm 3s infinite ease-in-out; }
    .animate-twinkle { animation: twinkle 3s infinite ease-in-out; }
    .animate-sway { animation: sway-tree 4s ease-in-out infinite; }
    .animate-pop-in { animation: pop-in 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
    .animate-flicker { animation: flicker 2s infinite; }
    .animate-moon { animation: float-moon 5s ease-in-out infinite; }
    .animate-bounce-slow { animation: bounce-slow 3s infinite ease-in-out; }
    .animate-wiggle { animation: wiggle 2s infinite ease-in-out; }
    .animate-pop { animation: splash-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
    .animate-kite { animation: float-kite 4s ease-in-out infinite; }
    .animate-bloom { animation: bloom 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
    .animate-pulse-soft { animation: pulse-soft 3s infinite ease-in-out; }
    .animate-float { animation: float-icon 3s ease-in-out infinite; }
    .animate-sun { animation: animate-sun 20s linear infinite; }
    .festive-font { font-family: 'Mountains of Christmas', cursive; letter-spacing: 1px; }
    .fun-font { font-family: 'Chewy', cursive; letter-spacing: 1px; }

    /* New premium loaders */
    @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes spin-reverse-fast { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    @keyframes pulse-slow { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.05); } }

    .animate-spin-slow { animation: spin-slow 12s linear infinite; }
    .animate-spin-reverse-fast { animation: spin-reverse-fast 2s linear infinite; }
    .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }

    @keyframes seq-fade-1 {
      0%, 10% { opacity: 0; transform: translateY(10px) scale(0.95); filter: blur(2px); }
      25%, 75% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
      90%, 100% { opacity: 0; transform: translateY(-10px) scale(1.05); filter: blur(2px); }
    }
    @keyframes seq-fade-2 {
      0% { opacity: 0; transform: translateY(10px) scale(0.95); filter: blur(2px); }
      100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
    }

    /* Premium fallback animations (Normal, Gangaur, Teej) */
    @keyframes splash-ambient-a {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(15px, 10px) scale(1.05); }
    }
    @keyframes splash-ambient-b {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(-15px, -10px) scale(1.08); }
    }
    @keyframes splash-orb-pulse {
      0%, 100% { transform: scale(1); opacity: 0.7; }
      50% { transform: scale(1.06); opacity: 1; }
    }
    @keyframes splash-text-in {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  // ── OLD generic loader disabled per instructions ──
  /*
  const OldSplashScreen = () => (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: currentTheme.splashBg,
      transition: 'opacity 1s, visibility 1s', opacity: showSplash ? 1 : 0,
      visibility: showSplash ? 'visible' : 'hidden', pointerEvents: showSplash ? 'all' : 'none', overflow: 'hidden'
    }}>
      <div style={{ position: 'absolute', top: '-20%', left: '-15%', width: '320px', height: '320px', background: `radial-gradient(circle, ${currentTheme.primaryColor}30, transparent 70%)`, borderRadius: '50%', filter: 'blur(60px)', animation: 'splash-ambient-a 6s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', bottom: '-20%', right: '-15%', width: '320px', height: '320px', background: `radial-gradient(circle, ${currentTheme.secondaryColor}25, transparent 70%)`, borderRadius: '50%', filter: 'blur(60px)', animation: 'splash-ambient-b 6s ease-in-out infinite' }} />
      <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: currentTheme.isDark ? `radial-gradient(circle at 35% 35%, ${currentTheme.primaryColor}25, ${currentTheme.secondaryColor}10, transparent)` : `radial-gradient(circle at 35% 35%, ${currentTheme.primaryColor}18, ${currentTheme.secondaryColor}08, transparent)`, border: `1.5px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.1)' : currentTheme.primaryColor + '20'}`, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: `0 0 40px ${currentTheme.primaryColor}15, inset 0 0 30px ${currentTheme.primaryColor}08`, marginBottom: '40px', animation: 'splash-orb-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: currentTheme.primaryColor, boxShadow: `0 0 20px ${currentTheme.primaryColor}60, 0 0 40px ${currentTheme.primaryColor}30`, opacity: 0.8 }} />
      </div>
      <h1 style={{ fontSize: '42px', marginBottom: '6px', fontFamily: currentTheme.font, color: currentTheme.isDark ? '#ffffff' : currentTheme.primaryColor, textAlign: 'center', fontWeight: '700', opacity: 0, animation: 'splash-text-in 0.8s ease-out 0.3s forwards', letterSpacing: '-0.01em' }}>{currentTheme.splashTitle}</h1>
      <p style={{ fontSize: '13px', letterSpacing: '0.3em', textTransform: 'uppercase', color: currentTheme.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)', fontWeight: '500', marginBottom: '0', opacity: 0, animation: 'splash-text-in 0.8s ease-out 0.6s forwards' }}>{currentTheme.splashSubtitle}</p>
      <div style={{ marginTop: '48px', width: '192px', height: '3px', background: currentTheme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', borderRadius: '999px', overflow: 'hidden', position: 'relative' }}>
        <div style={{ position: 'absolute', height: '100%', width: `${loadingProgress}%`, background: `linear-gradient(90deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`, transition: 'width 0.4s ease-out', borderRadius: '999px', boxShadow: `0 0 12px ${currentTheme.primaryColor}50` }} />
      </div>
    </div>
  );
  */

  // ── NEW Exact Per-Theme Splash Screen Loader ──

  // ── Theme-Specific Loader Render Functions ──────────────────────────────
  const renderAutumnLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff7ed', overflow: 'hidden' }}>
      <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-orange-200 rounded-full blur-3xl opacity-40 animate-pulse-warm"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-yellow-200 rounded-full blur-3xl opacity-40 animate-pulse-warm" style={{ animationDelay: '1s' }}></div>
      <div className="relative w-56 h-56 mb-4 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-leaf origin-center drop-shadow-xl">
          <path d="M100,180 C100,180 100,130 100,130 C100,130 70,150 50,130 C40,120 60,100 60,100 C60,100 30,90 40,60 C45,45 70,70 70,70 C70,70 80,30 100,20 C120,30 130,70 130,70 C130,70 155,45 160,60 C170,90 140,100 140,100 C140,100 160,120 150,130 C130,150 100,130 100,130 C100,130 100,180 100,180 Z" fill="url(#leafGradient)" stroke="#9a3412" strokeWidth="2" />
          <defs>
            <linearGradient id="leafGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#ea580c', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <h1 className="text-6xl text-orange-700 mb-1 script-font animate-fade-in drop-shadow-sm" style={{ animationDelay: '0.8s' }}>Namaste</h1>
      <p className="text-orange-900/60 text-lg tracking-[0.4em] uppercase font-light animate-fade-in" style={{ animationDelay: '1.2s' }}>Atithi Devo Bhava</p>
      <div className="mt-10 w-48 h-1.5 bg-orange-100 rounded-full overflow-hidden relative">
        <div className="absolute h-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 transition-all duration-[2500ms] ease-out" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderWinterLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0f172a', overflow: 'hidden' }}>
      <div className="relative w-48 h-48 mb-6 flex items-center justify-center">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full animate-spin-slow origin-center drop-shadow-[0_0_15px_rgba(56,189,248,0.8)]">
          <g stroke="#38bdf8" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none">
            {[0, 60, 120, 180, 240, 300].map(angle => (
              <g key={angle} style={{ transform: `rotate(${angle}deg)`, transformOrigin: '100px 100px' }}>
                <line x1="100" y1="100" x2="100" y2="30" />
                <path d="M100 70 L85 55 M100 70 L115 55" strokeWidth="3" />
                <path d="M100 45 L90 35 M100 45 L110 35" strokeWidth="2" />
              </g>
            ))}
            <circle cx="100" cy="100" r="12" fill="#0ea5e9" opacity="0.6" stroke="none" />
            <polygon points="100,85 113,92.5 113,107.5 100,115 87,107.5 87,92.5" strokeWidth="2" stroke="#e0f2fe" opacity="0.8" />
          </g>
        </svg>
      </div>
      <h1 className="text-5xl text-sky-400 mb-2 script-font animate-fade-in drop-shadow-lg">Namaste</h1>
      <p className="text-slate-400 text-lg tracking-[0.3em] uppercase font-light animate-fade-in" style={{ animationDelay: '0.5s' }}>Atithi Devo Bhava</p>
      <div className="mt-12 w-48 h-1 bg-slate-800 rounded-full overflow-hidden relative">
        <div className="absolute h-full bg-sky-500 transition-all duration-[3000ms] ease-out shadow-[0_0_10px_#0ea5e9]" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderDiwaliLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom right, #991b1b, #ea580c)', overflow: 'hidden' }}>
      <div className="relative w-64 h-64 mb-6 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-[0_0_25px_rgba(253,224,71,0.6)]">
          <path d="M100,20 Q130,70 100,100 Q70,70 100,20 Z" fill="#fef08a" stroke="#facc15" strokeWidth="2">
            <animate attributeName="d" dur="0.8s" repeatCount="indefinite" values="M100,20 Q130,70 100,100 Q70,70 100,20 Z; M100,10 Q120,65 100,100 Q80,65 100,10 Z; M100,20 Q130,70 100,100 Q70,70 100,20 Z" />
          </path>
          <path d="M100,40 Q115,70 100,90 Q85,70 100,40 Z" fill="#ef4444" opacity="0.8" className="animate-flicker" />
          <path d="M30,100 Q100,180 170,100 L160,100 Q100,160 40,100 Z" fill="#b45309" stroke="#78350f" strokeWidth="2" />
          <path d="M40,100 Q100,160 160,100" fill="#d97706" />
          <path d="M30,100 Q100,130 170,100" fill="none" stroke="#fcd34d" strokeWidth="3" strokeDasharray="5,5" />
          <circle cx="100" cy="60" r="30" fill="#fef08a" opacity="0.3" className="animate-pulse" />
        </svg>
      </div>
      <h1 className="text-5xl text-yellow-200 mb-2 script-font animate-fade-in drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">Happy Diwali</h1>
      <p className="text-white text-lg tracking-widest uppercase font-bold animate-fade-in" style={{ animationDelay: '0.5s' }}>Festival of Flavors</p>
      <div className="mt-10 w-48 h-2 bg-red-900 rounded-full overflow-hidden relative border border-yellow-400">
        <div className="absolute h-full bg-yellow-400 transition-all duration-[2500ms] ease-out" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderHoliLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#ffffff', overflow: 'hidden' }}>
      <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-300 rounded-full blur-2xl opacity-60 animate-bounce-slow"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-400 rounded-full blur-2xl opacity-60 animate-bounce-slow" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-10 w-24 h-24 bg-blue-400 rounded-full blur-2xl opacity-60 animate-bounce-slow" style={{ animationDelay: '0.5s' }}></div>
      <div className="relative w-64 h-64 mb-4 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-wiggle drop-shadow-xl">
          <path d="M140,60 Q160,40 180,50" stroke="#3b82f6" strokeWidth="4" strokeDasharray="5,5" className="animate-pulse" />
          <circle cx="185" cy="50" r="5" fill="#3b82f6" className="animate-ping" />
          <rect x="60" y="80" width="80" height="40" rx="5" transform="rotate(-45 100 100)" fill="#ec4899" stroke="#be185d" strokeWidth="2" />
          <rect x="40" y="100" width="30" height="10" transform="rotate(-45 55 105)" fill="#f59e0b" />
          <rect x="110" y="30" width="10" height="40" transform="rotate(-45 115 50)" fill="#fbbf24" stroke="#d97706" strokeWidth="2" />
          <circle cx="50" cy="150" r="25" fill="#8b5cf6" stroke="#6d28d9" strokeWidth="2" />
          <path d="M50,125 L75,100" stroke="#6d28d9" strokeWidth="4" />
        </svg>
      </div>
      <h1 className="text-6xl mb-2 fun-font animate-pop text-gradient drop-shadow-sm">Happy Holi!</h1>
      <p className="text-gray-500 text-lg tracking-widest uppercase font-bold animate-fade-in" style={{ animationDelay: '0.5s' }}>Splash of Flavors</p>
      <div className="mt-10 w-48 h-2 bg-gray-100 rounded-full overflow-hidden relative border border-pink-200">
        <div className="absolute h-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 transition-all duration-[2500ms] ease-out" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderSpringLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff0f5', overflow: 'hidden' }}>
      <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-yellow-200 rounded-full blur-3xl opacity-30 animate-pulse-soft"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-pink-200 rounded-full blur-3xl opacity-30 animate-pulse-soft" style={{ animationDelay: '1s' }}></div>
      <div className="relative w-56 h-56 mb-4 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-bloom origin-center drop-shadow-2xl">
          <circle cx="100" cy="100" r="15" fill="#f59e0b" />
          <g fill="#fb7185" opacity="0.9">
            <ellipse cx="100" cy="70" rx="12" ry="30" transform="rotate(0 100 100)" />
            <ellipse cx="100" cy="70" rx="12" ry="30" transform="rotate(60 100 100)" />
            <ellipse cx="100" cy="70" rx="12" ry="30" transform="rotate(120 100 100)" />
            <ellipse cx="100" cy="70" rx="12" ry="30" transform="rotate(180 100 100)" />
            <ellipse cx="100" cy="70" rx="12" ry="30" transform="rotate(240 100 100)" />
            <ellipse cx="100" cy="70" rx="12" ry="30" transform="rotate(300 100 100)" />
          </g>
          <g fill="#fda4af" opacity="0.6" stroke="#fff" strokeWidth="1">
            <ellipse cx="100" cy="55" rx="15" ry="40" transform="rotate(30 100 100)" />
            <ellipse cx="100" cy="55" rx="15" ry="40" transform="rotate(90 100 100)" />
            <ellipse cx="100" cy="55" rx="15" ry="40" transform="rotate(150 100 100)" />
            <ellipse cx="100" cy="55" rx="15" ry="40" transform="rotate(210 100 100)" />
            <ellipse cx="100" cy="55" rx="15" ry="40" transform="rotate(270 100 100)" />
            <ellipse cx="100" cy="55" rx="15" ry="40" transform="rotate(330 100 100)" />
          </g>
        </svg>
      </div>
      <h1 className="text-6xl text-rose-500 mb-1 script-font animate-fade-in drop-shadow-sm" style={{ animationDelay: '0.8s' }}>Namaste</h1>
      <p className="text-slate-500 text-lg tracking-[0.4em] uppercase font-light animate-fade-in" style={{ animationDelay: '1.2s' }}>Atithi Devo Bhava</p>
      <div className="mt-10 w-48 h-1.5 bg-rose-100 rounded-full overflow-hidden relative">
        <div className="absolute h-full bg-gradient-to-r from-yellow-400 via-rose-400 to-purple-400 transition-all duration-[2500ms] ease-out" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );


  const renderChristmasLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#14532d', overflow: 'hidden' }}>
      <div className="absolute top-10 left-10 w-32 h-32 bg-red-500 rounded-full blur-3xl opacity-40 animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-yellow-400 rounded-full blur-3xl opacity-40 animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="relative w-64 h-64 mb-6 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-sway drop-shadow-2xl">
          <path d="M100,20 L130,60 L70,60 Z" fill="#16a34a" />
          <path d="M100,40 L140,90 L60,90 Z" fill="#15803d" />
          <path d="M100,70 L150,130 L50,130 Z" fill="#166534" />
          <rect x="90" y="130" width="20" height="40" fill="#78350f" />
          <polygon points="100,10 105,25 120,25 108,35 112,50 100,40 88,50 92,35 80,25 95,25" fill="#facc15" className="animate-twinkle" />
          <circle cx="90" cy="50" r="4" fill="#ef4444" className="animate-pulse" />
          <circle cx="110" cy="80" r="4" fill="#facc15" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
          <circle cx="80" cy="110" r="4" fill="#3b82f6" className="animate-pulse" style={{ animationDelay: '1s' }} />
          <circle cx="120" cy="110" r="4" fill="#ec4899" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
        </svg>
      </div>
      <h1 className="text-6xl mb-2 festive-font animate-pop-in text-white drop-shadow-md text-center transform -rotate-2">Merry Christmas</h1>
      <p className="text-yellow-300 text-lg tracking-widest uppercase font-bold animate-fade-in mt-2" style={{ animationDelay: '0.5s' }}>Tis the season to eat!</p>
      <div className="mt-12 w-56 h-3 bg-red-900 rounded-full overflow-hidden relative border border-yellow-500">
        <div className="absolute h-full bg-gradient-to-r from-red-500 via-green-500 to-red-500 transition-all duration-[2500ms] ease-out" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderEidLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#022c22', overflow: 'hidden' }}>
      <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-emerald-600 rounded-full blur-[120px] opacity-40 animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-yellow-500 rounded-full blur-[120px] opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="relative w-56 h-56 mb-4 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-moon origin-center drop-shadow-[0_0_20px_rgba(212,175,55,0.6)]">
          <path d="M100,20 A80,80 0 1,0 180,100 A60,60 0 1,1 100,20" fill="#d4af37" />
          <polygon points="135,50 140,65 155,65 143,75 147,90 135,80 123,90 127,75 115,65 130,65" fill="#fef3c7" className="animate-twinkle" />
        </svg>
      </div>
      <h1 className="text-6xl text-amber-400 mb-1 script-font animate-fade-in drop-shadow-md" style={{ animationDelay: '0.8s' }}>Eid Mubarak</h1>
      <p className="text-emerald-200/60 text-lg tracking-[0.3em] uppercase font-light animate-fade-in" style={{ animationDelay: '1.2s' }}>Feast with Joy</p>
      <div className="mt-10 w-48 h-1.5 bg-emerald-900 rounded-full overflow-hidden relative">
        <div className="absolute h-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 transition-all duration-[2500ms] ease-out shadow-[0_0_10px_#d4af37]" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderMakarSankrantiLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#bae6fd', overflow: 'hidden' }}>
      <div className="absolute top-10 right-10 w-32 h-32 bg-yellow-300 rounded-full blur-xl opacity-80 animate-pulse"></div>
      <div className="absolute top-1/4 left-[-50px] text-white opacity-60 text-9xl animate-pulse"><i className="fas fa-cloud"></i></div>
      <div className="absolute bottom-1/4 right-[-50px] text-white opacity-40 text-8xl"><i className="fas fa-cloud"></i></div>
      <div className="relative w-64 h-64 mb-6 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-kite drop-shadow-xl">
          <path d="M100,150 Q120,250 80,300" stroke="#f0f9ff" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M100,20 L160,80 L100,150 L40,80 Z" fill="#ec4899" stroke="#be185d" strokeWidth="2" />
          <path d="M100,20 L160,80 L100,80 Z" fill="#f472b6" />
          <path d="M40,80 L100,80 L100,150 Z" fill="#db2777" />
          <path d="M40,80 L160,80" stroke="#fcd34d" strokeWidth="2" />
          <path d="M100,20 L100,150" stroke="#fcd34d" strokeWidth="2" />
          <path d="M100,150 L85,170 L115,170 Z" fill="#f59e0b" />
        </svg>
      </div>
      <h1 className="text-6xl mb-2 festive-font animate-pop-in text-gradient drop-shadow-sm text-center transform -rotate-2">Kai Po Che!</h1>
      <p className="text-sky-700 text-lg tracking-widest uppercase font-bold animate-fade-in mt-2" style={{ animationDelay: '0.5s' }}>Happy Makar Sankranti</p>
      <div className="mt-12 w-56 h-3 bg-white/50 rounded-full overflow-hidden relative border border-white">
        <div className="absolute h-full bg-gradient-to-r from-orange-400 via-pink-500 to-yellow-400 transition-all duration-[2500ms] ease-out" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderNavratriLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff7ed', overflow: 'hidden' }}>
      <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-orange-400 rounded-full blur-[100px] opacity-30 animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-yellow-400 rounded-full blur-[100px] opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="relative w-56 h-56 mb-4 flex items-center justify-center">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-sway origin-center drop-shadow-xl">
          <circle cx="100" cy="70" r="25" fill="#78350f" />
          <path d="M100,45 Q115,30 100,10 Q85,30 100,45" fill="#15803d" />
          <path d="M75,60 Q60,40 85,55" fill="#15803d" />
          <path d="M125,60 Q140,40 115,55" fill="#15803d" />
          <path d="M60,90 Q100,70 140,90 L130,160 Q100,180 70,160 Z" fill="#b91c1c" />
          <path d="M60,90 Q100,110 140,90" fill="none" stroke="#facc15" strokeWidth="4" />
          <path d="M85,115 L115,115 M100,100 L100,130" stroke="#facc15" strokeWidth="3" strokeLinecap="round" />
          <path d="M85,115 L85,105 M115,115 L115,125 M100,100 L110,100 M100,130 L90,130" stroke="#facc15" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
      <h1 className="text-6xl text-orange-700 mb-1 script-font animate-fade-in drop-shadow-sm" style={{ animationDelay: '0.8s' }}>Jai Mata Di</h1>
      <p className="text-orange-900/60 text-lg tracking-[0.3em] uppercase font-light animate-fade-in" style={{ animationDelay: '1.2s' }}>Shubh Navratri</p>
      <div className="mt-10 w-48 h-1.5 bg-orange-100 rounded-full overflow-hidden relative border border-orange-200">
        <div className="absolute h-full bg-gradient-to-r from-orange-500 via-yellow-500 to-red-600 transition-all duration-[2500ms] ease-out shadow-[0_0_10px_#f97316]" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );

  const renderSummerLoader = (progress) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fffbf0', overflow: 'hidden' }}>
      <div className="absolute top-[-20%] left-[-15%] w-96 h-96 rounded-full blur-[80px] animate-pulse-slow" style={{ background: `radial-gradient(circle, rgba(255, 159, 67, 0.3), transparent 70%)` }}></div>
      <div className="absolute bottom-[-20%] right-[-15%] w-96 h-96 rounded-full blur-[80px] animate-pulse-slow" style={{ background: `radial-gradient(circle, rgba(225, 112, 85, 0.2), transparent 70%)` }}></div>

      <div className="relative w-40 h-40 mb-8 flex items-center justify-center scale-95">
        {/* Animated Sun Rings */}
        <svg viewBox="0 0 200 200" className="absolute w-full h-full animate-spin-slow origin-center">
          <circle cx="100" cy="100" r="80" fill="none" stroke="#ff9f43" strokeWidth="1.5" strokeDasharray="15 15" opacity="0.4" />
          <path d="M100,10 L100,30 M100,170 L100,190 M10,100 L30,100 M170,100 L190,100 M36,36 L50,50 M150,150 L164,164 M36,164 L50,150 M150,50 L164,36" stroke="#ff9f43" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
        </svg>

        <svg viewBox="0 0 100 100" className="absolute w-3/4 h-3/4 animate-spin-reverse-fast origin-center">
          <circle cx="50" cy="50" r="38" fill="none" stroke="#e17055" strokeWidth="2.5" strokeDasharray="50 150" strokeLinecap="round" opacity="0.8" className="drop-shadow-md" />
        </svg>

        {/* Mango Icon */}
        <svg viewBox="0 0 200 200" className="absolute w-1/2 h-1/2 drop-shadow-lg">
          <path d="M75,140 Q60,110 85,80 Q100,60 115,80 Q140,110 125,140 L100,160 L75,140 Z" fill="#e17055" />
          <path d="M85,80 L100,110 L115,80" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5" />
        </svg>
      </div>

      <div className="relative flex flex-col items-center justify-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-2 tracking-wide script-font text-[#ff9f43]">
          Namaste 🙏
        </h2>
        <p className="text-sm md:text-base tracking-[0.2em] font-medium opacity-80 text-[#e17055]">
          Atithi Devo Bhava
        </p>
      </div>

      <div className="mt-10 w-56 h-1.5 bg-orange-100/50 rounded-full overflow-hidden relative backdrop-blur-sm border border-orange-200/50">
        <div className="absolute h-full rounded-full"
          style={{
            width: `${progress}%`,
            background: `linear-gradient(90deg, #ff9f43, #e17055)`,
            boxShadow: `0 0 10px rgba(255, 159, 67, 0.4)`,
            transition: 'width 0.1s linear'
          }}>
        </div>
      </div>
    </div>
  );

  const renderRoyalRajasthanLoader = (progress) => (
    (() => {
      const loaded = royalLoaderOpened;
      return (
        <div className={`rr-loader fixed inset-0 pointer-events-none flex flex-col ${loaded ? 'loaded' : ''}`} style={{ position: 'fixed', inset: 0, zIndex: 60, pointerEvents: 'none', display: 'flex', flexDirection: 'column' }}>
          <div className="shutter-panel shutter-top bg-orange-600 w-full relative z-10 flex flex-col items-center justify-end pb-4" style={{ background: '#ea580c', width: '100%', height: '50vh', position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 16 }}>
            <div className="relative w-64 h-64 animate-float" style={{ position: 'relative', width: 256, height: 256 }}>
              <div style={{ position: 'absolute', inset: 0, background: '#fbbf24', borderRadius: '9999px', filter: 'blur(48px)', opacity: 0.2 }}></div>
              <svg viewBox="0 0 200 240" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 25px 25px rgba(0,0,0,0.15))' }}>
                <defs>
                  <linearGradient id="skinGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#feeec9" /><stop offset="100%" stopColor="#f5cca0" /></linearGradient>
                  <linearGradient id="clothGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#ffb347" /><stop offset="100%" stopColor="#ffcc33" /></linearGradient>
                </defs>
                <g transform="translate(0, 10)">
                  <path d="M60 230 L60 140 Q60 110 100 110 Q140 110 140 140 L140 230" fill="url(#clothGrad)" stroke="black" strokeWidth="2" />
                  <path d="M60 230 L140 230" stroke="black" strokeWidth="2" fill="url(#clothGrad)" />
                  <path d="M60 140 Q100 140 140 200" fill="none" stroke="black" strokeWidth="1.5" strokeDasharray="4 2" />
                  <circle cx="100" cy="150" r="2.5" fill="#92400e" stroke="black" strokeWidth="0.5" />
                  <circle cx="112" cy="165" r="2.5" fill="#92400e" stroke="black" strokeWidth="0.5" />
                  <circle cx="125" cy="180" r="2.5" fill="#92400e" stroke="black" strokeWidth="0.5" />
                  <path d="M85 110 L85 100 Q100 105 115 100 L115 110 L100 120 Z" fill="#feeec9" stroke="black" strokeWidth="1.5" />
                </g>
                <g className="animate-head-move">
                  <rect x="75" y="60" width="50" height="55" rx="20" ry="20" fill="url(#skinGrad)" stroke="black" strokeWidth="2" />
                  <circle cx="75" cy="85" r="6" fill="url(#skinGrad)" stroke="black" strokeWidth="1.5" />
                  <circle cx="125" cy="85" r="6" fill="url(#skinGrad)" stroke="black" strokeWidth="1.5" />
                  <path d="M70 60 Q60 30 100 25 Q140 30 130 60 Q100 70 70 60 Z" fill="#ef4444" stroke="black" strokeWidth="2" />
                  <path d="M70 50 Q100 30 130 50" fill="none" stroke="black" strokeWidth="1.5" />
                  <path d="M75 40 Q100 20 125 40" fill="none" stroke="black" strokeWidth="1.5" />
                  <circle cx="100" cy="40" r="4" fill="#fbbf24" stroke="black" strokeWidth="1" />
                  <circle cx="88" cy="82" r="2.5" fill="#1f2937" />
                  <circle cx="112" cy="82" r="2.5" fill="#1f2937" />
                  <path d="M84 76 Q90 74 94 76" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M106 76 Q110 74 116 76" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                  <path className="animate-mustache-move" d="M100 95 Q115 90 125 92 Q120 98 110 96 L100 98 L90 96 Q80 98 75 92 Q85 90 100 95" fill="#1f2937" stroke="black" strokeWidth="0.5" />
                  <path d="M92 105 Q100 112 108 105" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M100 70 L100 62" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
                </g>
                <g className="animate-hands-move" transform="translate(0, 8)">
                  <path d="M60 125 Q50 160 85 160" fill="none" stroke="black" strokeWidth="16" strokeLinecap="round" />
                  <path d="M60 125 Q50 160 85 160" fill="none" stroke="url(#clothGrad)" strokeWidth="13" strokeLinecap="round" />
                  <path d="M140 125 Q150 160 115 160" fill="none" stroke="black" strokeWidth="16" strokeLinecap="round" />
                  <path d="M140 125 Q150 160 115 160" fill="none" stroke="url(#clothGrad)" strokeWidth="13" strokeLinecap="round" />
                  <circle cx="85" cy="160" r="8" fill="url(#clothGrad)" stroke="black" strokeWidth="2" />
                  <circle cx="115" cy="160" r="8" fill="url(#clothGrad)" stroke="black" strokeWidth="2" />
                  <g transform="translate(100, 150)">
                    <path d="M-4 18 L-10 5 Q-12 -8 -4 -15 L 0 -18 L 4 -15 Q 12 -8 10 5 L 4 18 Z" fill="none" stroke="black" strokeWidth="3.5" strokeLinejoin="round" />
                    <path d="M-4 18 L-10 5 Q-12 -8 -4 -15 L 0 -18" fill="#feeec9" stroke="none" />
                    <path d="M-4 18 L-14 8 Q-18 -5 -10 -15 L 0 -18" fill="#f5cca0" stroke="none" />
                    <path d="M4 18 L10 5 Q12 -8 4 -15 L 0 -18" fill="#feeec9" stroke="none" />
                    <path d="M4 18 L14 8 Q18 -5 10 -15 L 0 -18" fill="#f5cca0" stroke="none" />
                    <line x1="0" y1="-18" x2="0" y2="18" stroke="black" strokeWidth="1" />
                    <path d="M-8 -5 Q-4 0 -4 -10" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M8 -5 Q4 0 4 -10" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
                  </g>
                </g>
              </svg>
            </div>
            <h1 className="text-4xl font-black text-white tracking-tight" style={{ fontSize: 36, fontWeight: 900, color: 'white', letterSpacing: '-0.025em', opacity: 1 }}>Namaste</h1>
          </div>

          <div className="shutter-panel shutter-bottom bg-orange-600 w-full relative z-10 flex flex-col items-center justify-start pt-4 px-6" style={{ background: '#ea580c', height: '50vh', width: '100%', position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 16, paddingLeft: 24, paddingRight: 24 }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', top: -16, left: -16, fontSize: 48, color: 'rgba(255,255,255,0.2)', fontFamily: 'serif' }}>❝</span>
              <p className="text-xl font-serif italic text-white font-bold" style={{ fontSize: 20, fontFamily: 'serif', fontStyle: 'italic', color: 'white', fontWeight: 700, opacity: 1 }}>Atithi Devo Bhava</p>
              <span style={{ position: 'absolute', bottom: -24, right: -16, fontSize: 48, color: 'rgba(255,255,255,0.2)', fontFamily: 'serif' }}>❞</span>
            </div>
          </div>
        </div>
      );
    })()
  );

  const renderMinimalFallbackLoader = (theme, progress) => (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: theme.splashBg, overflow: 'hidden'
    }}>
      <div className="absolute top-[-20%] left-[-15%] w-96 h-96 rounded-full blur-[80px] animate-pulse-slow" style={{ background: `radial-gradient(circle, ${theme.primaryColor}20, transparent 70%)` }}></div>
      <div className="absolute bottom-[-20%] right-[-15%] w-96 h-96 rounded-full blur-[80px] animate-pulse-slow" style={{ background: `radial-gradient(circle, ${theme.secondaryColor}15, transparent 70%)`, animationDelay: '2s' }}></div>

      <div className="relative w-32 h-32 mb-8 flex items-center justify-center">
        <svg viewBox="0 0 100 100" className="absolute w-full h-full animate-spin-slow origin-center">
          <circle cx="50" cy="50" r="46" fill="none" stroke={theme.primaryColor} strokeWidth="1.5" strokeDasharray="15 10" opacity="0.3" />
        </svg>
        <svg viewBox="0 0 100 100" className="absolute w-full h-full animate-spin-reverse-fast origin-center">
          <circle cx="50" cy="50" r="38" fill="none" stroke={theme.primaryColor} strokeWidth="2" strokeDasharray="60 180" strokeLinecap="round" opacity="0.8" className="drop-shadow-lg" />
        </svg>
        <div className="w-4 h-4 rounded-full blur-[1px]" style={{ background: theme.primaryColor, boxShadow: `0 0 20px ${theme.primaryColor}` }}></div>
      </div>

      <div className="relative w-full h-24 flex flex-col items-center justify-center">
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ animation: 'seq-fade-1 3.5s cubic-bezier(0.4, 0, 0.2, 1) forwards' }}>
          <h2 className="text-3xl md:text-5xl font-bold mb-2 tracking-wide" style={{ color: theme.primaryColor, fontFamily: theme.font }}>
            Namaste 🙏
          </h2>
          <p className="text-sm md:text-base tracking-[0.2em] font-medium opacity-80" style={{ color: theme.isDark ? '#cbd5e1' : '#64748b' }}>
            Atithi Devo Bhava
          </p>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none opacity-0" style={{ animation: 'seq-fade-2 1s cubic-bezier(0.4, 0, 0.2, 1) 3s forwards' }}>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-center" style={{ color: theme.isDark ? '#fff' : theme.primaryColor, fontFamily: theme.font }}>
            {theme.splashTitle}
          </h1>
          <p className="text-xs md:text-sm uppercase tracking-widest font-medium mt-3 opacity-80" style={{ color: theme.isDark ? '#cbd5e1' : '#64748b' }}>
            {theme.splashSubtitle}
          </p>
        </div>
      </div>

      <div className="mt-12 w-48 h-1 bg-black/5 dark:bg-white/10 rounded-full overflow-hidden relative backdrop-blur-sm">
        <div className="absolute h-full transition-all duration-[2000ms] ease-out rounded-full"
          style={{
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
            boxShadow: `0 0 10px ${theme.primaryColor}50`
          }}>
        </div>
      </div>
    </div>
  );

  const SplashScreen = () => {
    if (!showSplash || !themeReady) return null;

    switch (templateKey) {
      case 'Autumn': return renderAutumnLoader(loadingProgress);
      case 'Winter': return renderWinterLoader(loadingProgress);
      case 'Diwali': return renderDiwaliLoader(loadingProgress);
      case 'Holi': return renderHoliLoader(loadingProgress);
      case 'Spring': return renderSpringLoader(loadingProgress);
      case 'Christmas': return renderChristmasLoader(loadingProgress);
      case 'Eid': return renderEidLoader(loadingProgress);
      case 'Makar Sankranti': return renderMakarSankrantiLoader(loadingProgress);
      case 'Navratri': return renderNavratriLoader(loadingProgress);
      case 'Summer': return renderSummerLoader(loadingProgress);
      case 'Royal Rajasthan': return renderRoyalRajasthanLoader(loadingProgress);
      default: return renderMinimalFallbackLoader(currentTheme, loadingProgress);
    }
  };

  return (
    <>
      <style>{styles}</style>
      {!isRajasthanTheme && <ThemeEffects themeName={templateKey} />}
      <ToastContainer position="top-right" autoClose={2000} style={{ zIndex: 99999 }} />

      {showOrderConfirmPopup && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9990,
            padding: '16px',
            animation: 'fadeIn 0.25s ease-out'
          }}
          onClick={() => setShowOrderConfirmPopup(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '520px',
              borderRadius: '18px',
              background: currentTheme.isDark ? '#0f172a' : '#ffffff',
              color: currentTheme.isDark ? '#f8fafc' : '#0f172a',
              border: `1px solid ${currentTheme.isDark ? 'rgba(148,163,184,0.25)' : '#e2e8f0'}`,
              boxShadow: '0 24px 60px rgba(2, 6, 23, 0.28)',
              padding: '24px',
              animation: 'scaleInCurve 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>
                  Payment Confirmed Successfully
                </h3>
              </div>
            </div>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', lineHeight: '1.6', color: currentTheme.isDark ? '#cbd5e1' : '#475569' }}>
              Your payment was successful.
            </p>
            <p style={{ margin: '0 0 18px 0', fontSize: '14px', lineHeight: '1.6', color: currentTheme.isDark ? '#cbd5e1' : '#475569' }}>
              Contact us for further assistance.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowOrderConfirmPopup(false)}
                style={{
                  padding: '10px 18px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#10b981',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showSplash && SplashScreen()}

      {restaurant && (
        <>
        <div className={`main-container ${isRajasthanTheme ? 'theme-rajasthan' : 'theme-default'}`} style={{
        '--primary-color': currentTheme.primaryColor,
        fontFamily: "'Poppins', sans-serif",
        background: isRajasthanTheme ? '#ffffff' : currentTheme.gradient,
        minHeight: '100vh',
        paddingBottom: '100px',
        color: textColor,
        height: showSplash ? '100vh' : 'auto',
        overflow: showSplash ? 'hidden' : 'visible'
      }}>

        {currentView === 'home' && (
          <div style={{ padding: '20px' }}>

            {/* ── Closed Banner ─────────────────────────────────────────── */}
            {!restaurantOpen && (
              <div style={{
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                color: 'white',
                borderRadius: '16px',
                padding: '20px 24px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                boxShadow: '0 8px 24px rgba(220, 38, 38, 0.35)',
              }}>
                <span style={{ fontSize: '32px' }}>🔒</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Restaurant is Currently Closed</div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>{closedMessage || 'We are not taking orders right now. Please check back later.'}</div>
                </div>
              </div>
            )}
            {/* ─────────────────────────────────────────────────────────── */}
            {/* Restaurant Name Card — QR template: text-center mt-2 mb-8 */}
            <div style={{
              textAlign: 'center',
              marginTop: '8px',
              marginBottom: '20px' // Reduced for better fit
            }} className="animate-fade-in">
              {/* Avatar circle — QR template: w-28 h-28 bg-white rounded-full p-1 shadow-lg */}
              <div style={{
                width: '112px',
                height: '112px',
                margin: '0 auto 16px',
                background: currentTheme.isDark ? 'rgba(255,255,255,0.08)' : 'white',
                borderRadius: '50%',
                padding: '4px',
                boxShadow: `0 10px 30px ${currentTheme.primaryColor}20`,
                position: 'relative'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: `4px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.1)' : currentTheme.primaryColor + '20'}`,
                  background: currentTheme.isDark
                    ? `linear-gradient(to bottom, ${currentTheme.primaryColor}15, transparent)`
                    : `linear-gradient(to bottom, ${currentTheme.primaryColor}10, white)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '40px',
                  overflow: 'hidden'
                }}>
                  {restaurant.logoUrl ? (
                    <img
                      src={resolveMediaUrl(restaurant.logoUrl)}
                      alt={restaurant.restaurantName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="animate-float">{currentTheme.headerIcon || currentTheme.icon}</span>
                  )}
                </div>
                {/* Star badge — QR template style */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '36px',
                  height: '36px',
                  background: currentTheme.accentColor || '#f59e0b',
                  borderRadius: '50%',
                  border: `4px solid ${currentTheme.isDark ? currentTheme.splashBg : 'white'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }} className="animate-bounce">⭐</div>
              </div>
              {/* Name — QR template: text-4xl font-bold tracking-tight script-font bg-clip-text gradient */}
              <h1 style={{
                fontSize: '36px',
                fontWeight: '700',
                color: headerTextColor,
                marginBottom: '4px',
                fontFamily: `${currentTheme.font}, cursive, serif`,
                letterSpacing: '-0.025em',
                lineHeight: '1.2',
                background: currentTheme.isDark ? 'none' : `linear-gradient(to right, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                WebkitBackgroundClip: currentTheme.isDark ? 'unset' : 'text',
                WebkitTextFillColor: currentTheme.isDark ? headerTextColor : 'transparent'
              }}>
                {restaurant.restaurantName}
              </h1>
              {/* Subtitle — QR template: text-sm mt-1 font-medium tracking-wide */}
              <p style={{
                fontSize: '14px',
                color: subtitleTextColor,
                fontWeight: '500',
                marginTop: '4px',
                letterSpacing: '0.05em'
              }}>
                {restaurant.cuisineType || 'Delicious Food'} {currentTheme.icon}
              </p>
            </div>

            {/* Quick Access - Social Media Links Header (No Background) */}
            {(restaurant.contactPhone || restaurant.instagramUrl || restaurant.mapLocation || restaurant.facebookUrl) && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px',
                flexWrap: 'wrap',
                marginBottom: '20px',
                padding: '0 16px'
              }} className="animate-fade-in">
                {restaurant.contactPhone && (
                  <a
                    href={`tel:${restaurant.contactPhone}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '56px',
                      height: '56px',
                      background: '#25D366',
                      borderRadius: '50%',
                      textDecoration: 'none',
                      boxShadow: '0 6px 16px rgba(37, 211, 102, 0.3)',
                      transition: 'transform 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                    </svg>
                  </a>
                )}

                {restaurant.instagramUrl && (
                  <a
                    href={restaurant.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '56px',
                      height: '56px',
                      background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                      borderRadius: '50%',
                      textDecoration: 'none',
                      boxShadow: '0 6px 16px rgba(220, 39, 67, 0.3)',
                      transition: 'transform 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                )}

                {restaurant.mapLocation && (
                  <a
                    href={restaurant.mapLocation}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '56px',
                      height: '56px',
                      background: '#EA4335',
                      borderRadius: '50%',
                      textDecoration: 'none',
                      boxShadow: '0 6px 16px rgba(234, 67, 53, 0.3)',
                      transition: 'transform 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </a>
                )}

                {restaurant.facebookUrl && (
                  <a
                    href={restaurant.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '56px',
                      height: '56px',
                      background: '#1877F2',
                      borderRadius: '50%',
                      textDecoration: 'none',
                      boxShadow: '0 6px 16px rgba(24, 119, 242, 0.3)',
                      transition: 'transform 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.81 9-9.95z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}

            {/* CTA Banner — Clean, Professional & Stylish */}
            <div style={{
              background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
              padding: '24px 20px',
              borderRadius: '24px',
              color: 'white',
              textAlign: 'center',
              marginBottom: '15px',
              boxShadow: `0 12px 30px -8px ${currentTheme.primaryColor}90`,
              position: 'relative',
              overflow: 'hidden',
              border: `1px solid rgba(255, 255, 255, 0.2)`
            }} className="animate-fade-in">
              {/* Glassmorphism accent circle */}
              <div style={{
                position: 'absolute',
                top: '-30px',
                right: '-30px',
                width: '180px',
                height: '180px',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                position: 'absolute',
                bottom: '-20px',
                left: '-20px',
                fontSize: '100px',
                opacity: 0.08,
                pointerEvents: 'none'
              }}>{currentTheme.headerIcon || currentTheme.icon}</div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                
                {/* Heading — QR template: text-3xl font-bold script-font */}
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  marginBottom: '8px',
                  fontFamily: currentTheme.font,
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
                }}>
                  {currentTheme.greeting || 'Welcome to Our Menu'}
                </h2>
                <p style={{
                  fontSize: '14px',
                  opacity: 0.95,
                  marginBottom: '20px',
                  fontWeight: '500',
                  letterSpacing: '0.01em'
                }}>
                  {currentTheme.tagline || 'Fresh ingredients & delicious flavors ✨'}
                </p>
                <button
                  onClick={() => setCurrentView('menu')}
                  style={{
                    background: 'white',
                    color: currentTheme.primaryColor,
                    padding: '12px 32px',
                    borderRadius: '999px',
                    fontWeight: '800',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    margin: '0 auto',
                    maxWidth: '300px',
                    width: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  className="btn-hover"
                >
                  <span>View Full Menu</span>
                  <span style={{ fontSize: '18px' }}>→</span>
                </button>
              </div>
            </div>



            {/* Website Link Section - Compact Button */}
            {restaurant.websiteUrl && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }} className="animate-fade-in">
                <a
                  href={restaurant.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    background: currentTheme.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                    color: currentTheme.isDark ? 'white' : currentTheme.primaryColor,
                    padding: '16px 32px',
                    borderRadius: '999px',
                    fontWeight: '700',
                    fontSize: '16px',
                    textDecoration: 'none',
                    boxShadow: `0 8px 24px ${currentTheme.isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                    border: `1px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)'}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 12px 30px ${currentTheme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)'}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${currentTheme.isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`;
                  }}
                >
                  <span style={{ fontSize: '20px' }}>🌐</span>
                  <span>Visit Our Website</span>
                  <span>→</span>
                </a>
              </div>
            )}
          </div>
        )}

        {currentView === 'menu' && (
          <div style={{ padding: '20px' }}>
            <div style={{
              marginBottom: '14px',
              background: currentTheme.isDark ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.1)'}`,
              borderRadius: '14px',
              padding: '8px',
              backdropFilter: 'blur(12px)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px', opacity: 0.85 }}>🔍</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for dishes, categories..."
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: textColor,
                    fontSize: isMobile ? '14px' : '15px',
                    fontWeight: '600'
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    style={{
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      padding: '6px 10px',
                      color: '#ffffff',
                      fontWeight: '700',
                      background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div style={{
                marginTop: '6px',
                fontSize: '12px',
                color: currentTheme.isDark ? '#cbd5e1' : '#64748b',
                fontWeight: '600'
              }}>
                {debouncedSearchQuery ? `Showing ${filteredProducts.length} result(s)` : ''}
              </div>
            </div>



            {isMobile ? (
              <div ref={menuGridRef} className="menu-mobile-sections">
                {groupedMenuSections.map((section) => (
                  <div key={`mobile-${section.name}`} id={`category-mobile-${section.name}`} style={{ marginBottom: '8px' }}>
                    {(() => {
                      const isExpanded = expandedCategories?.[section.name] !== false;
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleCategoryExpanded(section.name)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              padding: 0,
                              margin: '10px 0 6px 0',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer'
                            }}
                          >
                            <span style={{
                              fontSize: '19px',
                              fontWeight: '800',
                              color: currentTheme.isDark ? '#f8fafc' : '#1e293b'
                            }}>
                              {section.name}
                            </span>
                            <span style={{
                              fontSize: '16px',
                              color: currentTheme.primaryColor,
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}>
                              ▼
                            </span>
                          </button>
                          <div style={{
                            overflow: 'hidden',
                            maxHeight: isExpanded ? '5000px' : '0px',
                            opacity: isExpanded ? 1 : 0,
                            transition: 'max-height 0.35s ease, opacity 0.25s ease'
                          }}>
                            {section.items.map((product) => {
                      const displayPriceMeta = getProductDisplayPriceMeta(product);
                      const showOriginalPrice = Number(product?.discountPrice) > 0 && Number(product?.price) > Number(product?.discountPrice);

                      return (
                      <div
                        key={product.productId}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (restaurantOpen) addToCart(product);
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '20px 0 24px 0',
                          borderBottom: `1px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                          background: 'transparent',
                          cursor: restaurantOpen ? 'pointer' : 'default'
                        }}
                        className="menu-list-item"
                      >
                        <div style={{ flex: 1, paddingRight: '16px', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                            {product.isVeg !== undefined && product.isVeg !== null && (
                              <div style={{
                                width: '18px', height: '18px',
                                border: `1px solid ${product.isVeg ? '#16a34a' : '#dc2626'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '2px', flexShrink: 0, marginTop: '4px'
                              }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: product.isVeg ? '#16a34a' : '#dc2626' }}></div>
                              </div>
                            )}
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: currentTheme.isDark ? '#f8fafc' : '#1e293b', margin: 0, lineHeight: '1.3' }}>
                              {product.productName}
                            </h3>
                          </div>

                          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: currentTheme.isDark ? '#f1f5f9' : '#334155' }}>
                              {displayPriceMeta.estimatedFromModifiers ? 'From ' : ''}₹{Number(displayPriceMeta.price || 0).toFixed(0)}
                            </span>
                            {showOriginalPrice && !displayPriceMeta.estimatedFromModifiers && (
                              <span style={{ fontSize: '14px', color: '#94a3b8', textDecoration: 'line-through' }}>
                                ₹{product.price}
                              </span>
                            )}
                          </div>

                          {product.description && (
                            <p style={{
                              fontSize: '14px', color: currentTheme.isDark ? '#94a3b8' : '#64748b', margin: 0, lineHeight: '1.4',
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                            }}>
                              {product.description}
                            </p>
                          )}
                        </div>

                        <div style={{ position: 'relative', width: '170px', height: '170px', flexShrink: 0 }}>
                          {product.imageUrl ? (
                            <LazyImage
                              src={resolveMediaUrl(product.imageUrl)}
                              alt={product.productName}
                              index={productSequentialIndexMap[product.productId] || 0}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }}
                              placeholderStyle={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '16px',
                                background: currentTheme.isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'
                              }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', borderRadius: '16px' }} />
                          )}

                          <div style={{ position: 'absolute', bottom: '-14px', left: '50%', transform: 'translateX(-50%)', width: '100px', display: 'flex', justifyContent: 'center', zIndex: 2 }}>
                            {getProductQuantity(product.productId) === 0 ? (
                              <button
                                className="add-btn menu-add-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (restaurantOpen) addToCart(product);
                                }}
                                disabled={!restaurantOpen}
                              >
                                {restaurantOpen ? 'ADD +' : 'Closed'}
                              </button>
                            ) : (
                              <div className="qty-control">
                                <button
                                  className="menu-qty-btn"
                                  onClick={(event) => { event.stopPropagation(); const ci = cart.find(i => i.productId === product.productId); if (ci) updateQuantity(ci.cartItemId, -1); }}
                                >
                                  -
                                </button>
                                <span className="menu-qty-text">{getProductQuantity(product.productId)}</span>
                                <button
                                  className="menu-qty-btn"
                                  onClick={(event) => { event.stopPropagation(); const ci = cart.find(i => i.productId === product.productId); if (ci) updateQuantity(ci.cartItemId, 1); }}
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )})}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div ref={menuGridRef} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {groupedMenuSections.map((section) => (
                  <div key={`desktop-${section.name}`} id={`category-desktop-${section.name}`}>
                    {(() => {
                      const isExpanded = expandedCategories?.[section.name] !== false;
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleCategoryExpanded(section.name)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              padding: 0,
                              margin: '6px 0 12px 0',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer'
                            }}
                          >
                            <span style={{
                              fontSize: '22px',
                              fontWeight: '800',
                              color: currentTheme.isDark ? '#f8fafc' : '#1e293b'
                            }}>
                              {section.name}
                            </span>
                            <span style={{
                              fontSize: '18px',
                              color: currentTheme.primaryColor,
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}>
                              ▼
                            </span>
                          </button>
                          <div style={{
                            overflow: 'hidden',
                            maxHeight: isExpanded ? '5000px' : '0px',
                            opacity: isExpanded ? 1 : 0,
                            transition: 'max-height 0.35s ease, opacity 0.25s ease'
                          }}>
                            <div
                              className="menu-grid"
                              style={{
                                '--menu-card-bg': currentTheme.glassCard,
                                '--menu-card-border': currentTheme.glassCardBorder || (currentTheme.isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)'),
                                '--menu-card-shadow': currentTheme.glassCardShadow || '0 8px 20px rgba(0,0,0,0.06)'
                              }}
                            >
                              {section.items.map((product) => {
                        const displayPriceMeta = getProductDisplayPriceMeta(product);
                        const showOriginalPrice = Number(product?.discountPrice) > 0 && Number(product?.price) > Number(product?.discountPrice);
                        const currentQty = getProductQuantity(product.productId);
                        return (
                          <div
                            key={product.productId}
                            className="menu-card card-hover"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (restaurantOpen) addToCart(product);
                            }}
                            style={{ cursor: restaurantOpen ? 'pointer' : 'default' }}
                          >
                            {product.imageUrl ? (
                              <LazyImage
                                className="menu-card-image"
                                src={resolveMediaUrl(product.imageUrl)}
                                alt={product.productName}
                                index={productSequentialIndexMap[product.productId] || 0}
                                placeholderStyle={{ background: currentTheme.isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}
                              />
                            ) : (
                              <div className="menu-card-image" style={{ background: currentTheme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }} />
                            )}

                            <div className="menu-card-content">
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                {product.isVeg !== undefined && product.isVeg !== null && (
                                  <div style={{
                                    width: '18px', height: '18px',
                                    border: `1px solid ${product.isVeg ? '#16a34a' : '#dc2626'}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    borderRadius: '2px', flexShrink: 0, marginTop: '4px'
                                  }}>
                                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: product.isVeg ? '#16a34a' : '#dc2626' }}></div>
                                  </div>
                                )}
                                <h3 style={{ fontSize: '17px', fontWeight: '700', color: currentTheme.isDark ? '#f8fafc' : '#1e293b', margin: 0, lineHeight: '1.3' }}>
                                  {product.productName}
                                </h3>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '16px', fontWeight: '700', color: currentTheme.isDark ? '#f1f5f9' : '#334155' }}>
                                  {displayPriceMeta.estimatedFromModifiers ? 'From ' : ''}₹{Number(displayPriceMeta.price || 0).toFixed(0)}
                                </span>
                                {showOriginalPrice && !displayPriceMeta.estimatedFromModifiers && (
                                  <span style={{ fontSize: '14px', color: '#94a3b8', textDecoration: 'line-through' }}>
                                    ₹{product.price}
                                  </span>
                                )}
                              </div>

                              {product.description && (
                                <p style={{
                                  fontSize: '14px', color: currentTheme.isDark ? '#94a3b8' : '#64748b', margin: 0, lineHeight: '1.45',
                                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                }}>
                                  {product.description}
                                </p>
                              )}

                              <div className="menu-card-footer">
                                <div className="add-btn-container">
                                  {currentQty === 0 ? (
                                    <button
                                      className="add-btn menu-add-btn"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          if (restaurantOpen) addToCart(product);
                                        }}
                                      disabled={!restaurantOpen}
                                      style={{ maxWidth: '120px' }}
                                    >
                                      {restaurantOpen ? 'ADD +' : 'Closed'}
                                    </button>
                                  ) : (
                                    <div className="qty-control" style={{ maxWidth: '120px' }}>
                                      <button
                                        className="menu-qty-btn"
                                          onClick={(event) => { event.stopPropagation(); const ci = cart.find(i => i.productId === product.productId); if (ci) updateQuantity(ci.cartItemId, -1); }}
                                      >
                                        -
                                      </button>
                                      <span className="menu-qty-text">{currentQty}</span>
                                      <button
                                        className="menu-qty-btn"
                                          onClick={(event) => { event.stopPropagation(); const ci = cart.find(i => i.productId === product.productId); if (ci) updateQuantity(ci.cartItemId, 1); }}
                                      >
                                        +
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}

            {filteredProducts.length === 0 && (
              <div style={{
                marginTop: '16px',
                textAlign: 'center',
                background: currentTheme.isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.75)',
                border: `1px dashed ${currentTheme.primaryColor}70`,
                borderRadius: '14px',
                padding: '20px'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔎</div>
                <div style={{ fontWeight: '700', color: textColor, marginBottom: '4px' }}>No items found</div>
                <div style={{ fontSize: '13px', color: subtitleTextColor }}>Try another item name or category keyword.</div>
              </div>
            )}

            <button
              className={`menu-mobile-fab menu-btn ${currentView === 'menu' && cart.length > 0 ? 'shifted-up' : ''}`}
              onClick={() => setShowCategoryMenuModal(true)}
              style={{
                display: 'inline-flex'
              }}
            >
              ☰ MENU
            </button>

            {showCategoryMenuModal && (
              <div className="menu-mobile-overlay" onClick={() => setShowCategoryMenuModal(false)}>
                <div className="menu-mobile-sheet" onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '28px', color: '#111827' }}>Menu</h3>
                    <button
                      onClick={() => setShowCategoryMenuModal(false)}
                      style={{ border: 'none', background: 'transparent', fontSize: '30px', fontWeight: '700', color: '#374151', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </div>
                  <div className="menu-mobile-sheet-grid">
                    {groupedMenuSections.map(section => (
                      <button
                        key={section.name}
                        className="menu-mobile-cat-btn"
                        onClick={() => {
                          setExpandedCategories((prev) => ({
                            ...prev,
                            [section.name]: true
                          }));
                          setShowCategoryMenuModal(false);
                          setTimeout(() => {
                            const element = document.getElementById(`category-${isMobile ? 'mobile' : 'desktop'}-${section.name}`);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 50);
                        }}
                      >
                        {section.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {currentView === 'cart' && (
          <div style={{ padding: isMobile ? '12px' : '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: isMobile ? '12px' : '20px',
              gap: '12px'
            }}>
              <h2 style={{
                fontSize: isMobile ? '22px' : '26px',
                fontWeight: '700',
                color: currentTheme.isDark ? '#ffffff' : currentTheme.primaryColor,
                fontFamily: currentTheme.font,
                textShadow: currentTheme.isDark
                  ? '0 2px 8px rgba(0,0,0,0.5)'
                  : '0 2px 4px rgba(0,0,0,0.1)',
                margin: 0
              }}>
                Your Cart ({getCartCount()} items)
              </h2>
              {cart.length > 0 && (
                <button
                  onClick={openClearCartConfirm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    background: '#ef4444',
                    color: 'white',
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: isMobile ? '13px' : '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '88px' : '110px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Clear all items from cart"
                >
                  <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🗑️</span>
                  {isMobile ? 'Clear' : 'Clear Cart'}
                </button>
              )}
            </div>

            <div style={{
              marginBottom: isMobile ? '12px' : '14px',
              padding: isMobile ? '10px' : '12px',
              borderRadius: '12px',
              background: currentTheme.glassCard,
              border: currentTheme.glassCardBorder || '1px solid rgba(148,163,184,0.3)'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '700',
                marginBottom: '8px',
                color: currentTheme.isDark ? '#cbd5e1' : '#334155'
              }}>
                Order Type
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { key: 'DELIVERY', label: 'Delivery' },
                  { key: 'TAKEAWAY', label: 'Takeaway' },
                  { key: 'DRIVETHROUGH', label: 'Drive-through' }
                ].map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    onClick={() => setFulfillmentType(type.key)}
                    style={{
                      border: fulfillmentType === type.key ? `1px solid ${currentTheme.primaryColor}` : '1px solid #cbd5e1',
                      background: fulfillmentType === type.key ? `${currentTheme.primaryColor}22` : '#ffffff',
                      color: '#0f172a',
                      borderRadius: '10px',
                      padding: '8px 10px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: '80px', marginBottom: '20px', opacity: 0.8 }}>🛒</div>
                <p style={{ fontSize: '18px', color: subtitleTextColor, marginBottom: '30px', fontWeight: '500' }}>
                  Your cart is empty
                </p>
                <button
                  onClick={() => setCurrentView('menu')}
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                    color: 'white',
                    padding: '14px 30px',
                    borderRadius: '50px',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '16px',
                    cursor: 'pointer',
                    boxShadow: `0 6px 16px ${currentTheme.primaryColor}50`,
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s'
                  }}
                  className="btn-hover"
                >
                  Start Ordering
                </button>
              </div>
            ) : (
              <>
                {cart.map(item => {
                  const basePrice = item.discountPrice || item.price;
                  const addonsPrice = (item.selectedAddons || []).reduce((sum, addon) => sum + Number(addon.addonPrice || 0), 0);
                  const itemTotal = (basePrice + addonsPrice) * item.quantity;

                  return (
                    <div
                      key={item.cartItemId}
                      onClick={() => {
                        if (Array.isArray(item.selectedAddons) && item.selectedAddons.length > 0) {
                          openCartItemAddonModal(item);
                        }
                      }}
                      style={{
                        background: currentTheme.isDark
                          ? 'rgba(0, 0, 0, 0.4)'
                          : 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(15px)',
                        padding: isMobile ? '10px' : '16px',
                        borderRadius: '16px',
                        marginBottom: isMobile ? '8px' : '12px',
                        display: 'flex',
                        gap: isMobile ? '10px' : '16px',
                        border: `2px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                        cursor: Array.isArray(item.selectedAddons) && item.selectedAddons.length > 0 ? 'pointer' : 'default'
                      }}>
                      {item.imageUrl && (
                        <img
                          src={resolveMediaUrl(item.imageUrl)}
                          alt={item.productName}
                          style={{
                            width: isMobile ? '64px' : '80px',
                            height: isMobile ? '64px' : '80px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          fontSize: isMobile ? '15px' : '17px',
                          fontWeight: '700',
                          marginBottom: isMobile ? '2px' : '4px',
                          color: currentTheme.isDark ? '#f1f5f9' : '#1e293b'
                        }}>
                          {item.productName}
                        </h3>

                        {/* Show selected addons */}
                        {item.selectedAddons && item.selectedAddons.length > 0 && (
                          <div style={{
                            marginTop: '6px',
                            marginBottom: '8px'
                          }}>
                            {item.selectedAddons.map((addon, idx) => (
                              <div key={`${addon.addonId || addon.optionId}-${idx}`} style={{
                                fontSize: isMobile ? '12px' : '13px',
                                color: currentTheme.isDark ? '#94a3b8' : '#64748b',
                                marginBottom: '2px'
                              }}>
                                {addon.addonName} (₹{Number(addon.addonPrice || 0).toFixed(2)})
                              </div>
                            ))}
                          </div>
                        )}

                        {item.specialInstructions && (
                          <div style={{
                            marginBottom: '6px',
                            fontSize: isMobile ? '12px' : '13px',
                            color: currentTheme.isDark ? '#cbd5e1' : '#475569'
                          }}>
                            Special request: {item.specialInstructions}
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openSpecialRequestEditor(item);
                          }}
                          style={{
                            border: 'none',
                            background: `linear-gradient(90deg, #10b981, #059669)`,
                            color: 'white',
                            padding: isMobile ? '4px 8px' : '8px 12px',
                            fontSize: isMobile ? '11px' : '13px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            marginBottom: isMobile ? '6px' : '8px',
                            borderRadius: '999px',
                            boxShadow: isMobile ? 'none' : '0 6px 14px rgba(16,185,129,0.18)',
                            display: 'inline-block',
                            lineHeight: 1
                          }}
                        >
                          {item.specialInstructions ? 'Edit special request' : 'Add special request'}
                        </button>

                        <p style={{
                          fontSize: isMobile ? '16px' : '20px',
                          fontWeight: '700',
                          color: currentTheme.primaryColor
                        }}>
                          ₹{itemTotal.toFixed(2)}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            updateQuantity(item.cartItemId, -1);
                          }}
                          style={{
                            background: currentTheme.isDark
                              ? 'rgba(255, 255, 255, 0.15)'
                              : 'rgba(255, 255, 255, 0.9)',
                            border: `2px solid ${currentTheme.primaryColor}`,
                            color: currentTheme.isDark ? '#ffffff' : currentTheme.primaryColor,
                            width: isMobile ? '30px' : '36px',
                            height: isMobile ? '30px' : '36px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '700',
                            fontSize: isMobile ? '15px' : '18px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          -
                        </button>
                        <span style={{
                          fontSize: isMobile ? '15px' : '18px',
                          fontWeight: '700',
                          minWidth: isMobile ? '22px' : '32px',
                          textAlign: 'center',
                          color: currentTheme.isDark ? '#ffffff' : '#1e293b'
                        }}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            updateQuantity(item.cartItemId, 1);
                          }}
                          style={{
                            background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                            border: 'none',
                            color: 'white',
                            width: isMobile ? '30px' : '36px',
                            height: isMobile ? '30px' : '36px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '700',
                            fontSize: isMobile ? '15px' : '18px',
                            boxShadow: `0 4px 12px ${currentTheme.primaryColor}40`,
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div style={{
                  background: currentTheme.glassCard,
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  padding: isMobile ? '16px' : '24px',
                  borderRadius: '20px',
                  marginTop: isMobile ? '12px' : '20px',
                  border: currentTheme.glassCardBorder || `1px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}`,
                  boxShadow: currentTheme.glassCardShadow || '0 8px 24px rgba(0, 0, 0, 0.1)'
                }}>
                  {/* QR-style Order Summary header */}
                  <h4 style={{
                    fontWeight: '700',
                    borderBottom: `1px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    paddingBottom: '12px',
                    marginBottom: '12px',
                    color: currentTheme.isDark ? '#e2e8f0' : currentTheme.primaryColor,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    letterSpacing: '0.1em'
                  }}>Order Summary</h4>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px', color: currentTheme.isDark ? '#cbd5e1' : '#334155' }}>
                      Order Type
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {[
                        { key: 'DELIVERY', label: 'Delivery' },
                        { key: 'TAKEAWAY', label: 'Takeaway' },
                        { key: 'DRIVETHROUGH', label: 'Drive-through' }
                      ].map((type) => (
                        <button
                          key={type.key}
                          type="button"
                          onClick={() => setFulfillmentType(type.key)}
                          style={{
                            border: fulfillmentType === type.key ? `1px solid ${currentTheme.primaryColor}` : '1px solid #cbd5e1',
                            background: fulfillmentType === type.key ? `${currentTheme.primaryColor}22` : '#ffffff',
                            color: '#0f172a',
                            borderRadius: '10px',
                            padding: '8px 10px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: currentTheme.isDark ? '#94a3b8' : '#64748b' }}>
                    <span>Subtotal</span>
                    <span style={{ fontWeight: '700' }}>₹{getCartSubtotal().toFixed(2)}</span>
                  </div>
                  {fulfillmentType === 'DELIVERY' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: currentTheme.isDark ? '#94a3b8' : '#64748b' }}>
                      <span>Delivery Charge</span>
                      <span style={{ fontWeight: '700' }}>₹{getCartPricing().deliveryCharge.toFixed(2)}</span>
                    </div>
                  )}
                  {getCartPricing().taxAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: currentTheme.isDark ? '#94a3b8' : '#64748b' }}>
                      <span>{gstSettings.taxName || 'GST'} {getCartPricing().taxEnabled ? `(${getCartPricing().taxPercentage.toFixed(2)}%)` : ''}</span>
                      <span style={{ fontWeight: '700' }}>₹{getCartPricing().taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {getCartPricing().belowMinimum && (
                    <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '8px' }}>
                      Add items worth Rs {(getCartPricing().minimumOrderAmount - getCartSubtotal()).toFixed(2)} more for delivery.
                    </div>
                  )}
                  {/* Dashed divider — QR template style */}
                  <div style={{
                    borderTop: `1px dashed ${currentTheme.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                    paddingTop: '12px',
                    marginTop: '12px'
                  }}>
                    <div className="total-section">
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{
                          fontSize: isMobile ? '18px' : '20px',
                          fontWeight: '700',
                          color: currentTheme.isDark ? '#ffffff' : '#1e293b'
                        }}>Total</span>
                        <span style={{
                          fontSize: isMobile ? '22px' : '24px',
                          fontWeight: '700',
                          color: currentTheme.primaryColor,
                          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          ₹{getCartTotal().toFixed(2)}
                        </span>
                      </div>
                      <p className="tax-note">
                        {getCartPricing().taxAmount > 0 ? 'Inclusive of all taxes' : 'Taxes not applicable'}
                      </p>
                    </div>
                  </div>
                  {deliverySlabInsight && (
                    <div style={{
                      marginTop: '12px',
                      marginBottom: '12px',
                      padding: isMobile ? '10px 12px' : '12px 14px',
                      borderRadius: '14px',
                      background: currentTheme.isDark
                        ? 'rgba(148, 163, 184, 0.12)'
                        : '#f8fafc',
                      border: `1px solid ${currentTheme.isDark ? 'rgba(148,163,184,0.25)' : '#e2e8f0'}`,
                      boxShadow: currentTheme.isDark
                        ? '0 6px 16px rgba(15, 23, 42, 0.35)'
                        : '0 6px 16px rgba(15, 23, 42, 0.08)'
                    }}>
                      <div style={{
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '700',
                        color: currentTheme.isDark ? '#e2e8f0' : '#0f172a',
                        lineHeight: 1.4
                      }}>
                        {deliverySlabInsight.message}
                      </div>
                      <div style={{
                        marginTop: '4px',
                        fontSize: '11px',
                        color: currentTheme.isDark ? '#94a3b8' : '#64748b',
                        fontWeight: '600'
                      }}>
                        Based on current delivery slabs
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => { setShowCheckoutModal(true); setPaymentError(''); }}
                    style={{
                      background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                      color: 'white',
                      width: '100%',
                      padding: isMobile ? '12px' : '16px',
                      borderRadius: '12px',
                      border: 'none',
                      fontWeight: '700',
                      fontSize: isMobile ? '15px' : '17px',
                      cursor: 'pointer',
                      boxShadow: `0 6px 16px ${currentTheme.primaryColor}50`,
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Global Powered By Footer for ALL views */}
        <div style={{
          textAlign: 'center',
          paddingTop: '14px',
          paddingBottom: '100px', // Keep clear above bottom nav
          width: '100%',
          marginTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: '12px',
          paddingRight: '12px'
        }} className="animate-fade-in">
          <div style={{
            background: currentTheme.isDark ? '#0f172a' : '#ffffff',
            width: 'min(100%, 700px)',
            borderRadius: '12px',
            padding: '10px 12px',
            color: currentTheme.isDark ? '#cbd5e1' : '#64748b',
            fontSize: '12px',
            fontWeight: '600',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            opacity: 1
          }}>
            {currentView === 'menu' && (
              <div style={{
                color: '#000000',
                fontWeight: '700',
                fontSize: '12px',
                textTransform: 'none',
                letterSpacing: 'normal',
                margin: '0 auto 8px',
                maxWidth: '920px',
                lineHeight: '1.5',
                padding: '0 6px'
              }}>
                • For your order, the business is responsible for all payments. Trivana Hospitality and Yelofast act only as intermediaries and do not handle any payments. In case of any issue, please contact the business owner directly.
              </div>
            )}
            <div className="powered-text-container">
              <p className="powered-text" style={{ margin: 0 }}>
                POWERED BY{' '}
                <a href="https://trivanahospitality.com" target="_blank" rel="noopener noreferrer">
                  TRIVANA
                </a>{' '}
                X{' '}
                <a className="yelofast-link" href="https://yelofast.com" target="_blank" rel="noopener noreferrer">
                  YELOFAST
                </a>
              </p>
            </div>
          </div>

          <div className="powered-logos-container" style={{ width: 'min(100%, 700px)' }}>
            <div className="powered-logos" aria-label="Powered by logos">
              <a href="https://trivanahospitality.com" target="_blank" rel="noopener noreferrer" aria-label="Trivana Hospitality">
                <img className="trivana-logo" src={trivanaLogo} alt="Trivana Hospitality" />
              </a>
              <a href="https://yelofast.com" target="_blank" rel="noopener noreferrer" aria-label="Yelofast">
                <img src={yelofastLogo} alt="Yelofast" />
              </a>
            </div>
          </div>
        </div>

        {currentView === 'menu' && cart.length > 0 && (
          <div className="cart-popup" style={{
            position: 'fixed',
            left: isMobile ? '10px' : '16px',
            right: isMobile ? '10px' : '16px',
            bottom: isMobile ? 'calc(86px + env(safe-area-inset-bottom, 0px))' : '90px',
            zIndex: 900,
            background: currentTheme.isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${currentTheme.primaryColor}55`,
            borderRadius: '12px',
            boxShadow: `0 8px 20px ${currentTheme.primaryColor}35`,
            padding: isMobile ? '10px 12px' : '12px 14px',
            minHeight: isMobile ? '50px' : '58px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            {stickyFooterDeliveryInsight && (
              <div style={{
                padding: '6px 8px',
                marginBottom: '8px',
                borderRadius: '10px',
                background: currentTheme.isDark
                  ? 'rgba(148, 163, 184, 0.12)'
                  : '#f8fafc',
                border: `1px solid ${currentTheme.isDark ? 'rgba(148,163,184,0.25)' : '#e2e8f0'}`
              }}>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: currentTheme.isDark ? '#e2e8f0' : '#0f172a',
                  lineHeight: 1.3
                }}>
                  {stickyFooterDeliveryInsight.message}
                </span>
              </div>
            )}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: isMobile ? '8px' : '12px',
              minHeight: isMobile ? '32px' : '38px',
              width: '100%'
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: isMobile ? '10px' : '11px',
                  fontWeight: '600',
                  color: currentTheme.isDark ? '#cbd5e1' : '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Next step
                </div>
                <div style={{
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '800',
                  color: currentTheme.primaryColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: '2px'
                }}>
                  {getCartCount()} item(s) • ₹{getCartTotal().toFixed(2)}
                </div>
              </div>
              <button
                onClick={() => setCurrentView('cart')}
                style={{
                  border: 'none',
                  borderRadius: '8px',
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: isMobile ? '12px' : '13px',
                  color: '#ffffff',
                  background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                  boxShadow: `0 4px 12px ${currentTheme.primaryColor}45`,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minWidth: isMobile ? '102px' : '120px',
                  marginLeft: '8px'
                }}
              >
                View Cart →
              </button>
            </div>
          </div>
        )}

        {/* Bottom Navigation — Fixed to bottom */}
        {!showSplash && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: currentTheme.glassPanel,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderTop: currentTheme.glassPanelBorder || `1px solid ${currentTheme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
          display: 'flex',
          justifyContent: 'space-around',
          padding: '8px 0 calc(8px + env(safe-area-inset-bottom, 0px))', 
          boxShadow: currentTheme.glassPanelShadow || '0 -4px 20px rgba(0, 0, 0, 0.1)',
          zIndex: 9999
        }}>
          {['home', 'menu', 'cart'].map(view => {
            const isActive = currentView === view;
            return (
              <button
                key={view}
                onClick={() => setCurrentView(view)}
                style={{
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  color: isActive ? currentTheme.primaryColor : currentTheme.isDark ? '#cbd5e1' : '#64748b',
                  cursor: 'pointer',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '4px 0',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
                  transition: 'all 0.3s ease'
                }}
                className="btn-hover"
              >
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  zIndex: 2
                }}>
                  {/* Fluid Wave Background */}
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '45px',
                      height: '45px',
                      background: currentTheme.primaryColor,
                      opacity: 0.15,
                      transform: 'translate(-50%, -50%)',
                      animation: 'waterWaveFloat 4s linear infinite',
                      zIndex: -1
                    }} />
                  )}
                  
                  {/* Icons */}
                  {view === 'home' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill={isActive ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  )}
                  {view === 'menu' && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                      <path d="M7 2v20" />
                      <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
                    </svg>
                  )}
                  {view === 'cart' && (
                    <>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill={isActive ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="9" cy="21" r="1"></circle>
                        <circle cx="20" cy="21" r="1"></circle>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                      </svg>
                      {cart.length > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-10px',
                          background: '#ef4444',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          border: `2px solid ${currentTheme.glassPanel}`
                        }}>
                          {getCartCount()}
                        </span>
                      )}
                    </>
                  )}
                </div>
                
                <span style={{
                  fontSize: '11px',
                  letterSpacing: '0.03em',
                  fontWeight: isActive ? '700' : '500',
                  marginTop: '2px',
                  transition: 'all 0.3s ease'
                }}>
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </span>
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* ── Checkout Modal ────────────────────────────────────────── */}
      {showCheckoutModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(18, 18, 18, 0.58)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: isMobile ? '10px' : '20px' }}
          onClick={() => !processingPayment && setShowCheckoutModal(false)}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 22px 50px rgba(0,0,0,0.22)',
            borderRadius: '16px',
            padding: isMobile ? '20px 16px' : '24px',
            width: isMobile ? '100%' : 'min(94vw, 620px)',
            maxWidth: isMobile ? '420px' : '620px',
            position: 'relative',
            maxHeight: '92vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
            onClick={e => e.stopPropagation()}>
            <button
              onClick={() => !processingPayment && setShowCheckoutModal(false)}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280' }}
            >✕</button>

            <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1f2937', marginBottom: '2px' }}>Checkout</h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Delivering from {restaurant?.restaurantName || 'Restaurant'}
            </p>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '800', color: '#6b7280', letterSpacing: '0.04em', marginBottom: '8px' }}>
                ORDER TYPE
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { key: 'DELIVERY', label: 'Delivery' },
                  { key: 'TAKEAWAY', label: 'Takeaway' },
                  { key: 'DRIVETHROUGH', label: 'Drive-through' }
                ].map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    onClick={() => setFulfillmentType(type.key)}
                    disabled={processingPayment}
                    style={{
                      border: fulfillmentType === type.key ? '1px solid #2563eb' : '1px solid #cbd5e1',
                      background: fulfillmentType === type.key ? '#dbeafe' : '#ffffff',
                      color: '#0f172a',
                      borderRadius: '10px',
                      padding: '8px 10px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: processingPayment ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{
              border: '1px solid #f1f5f9',
              background: '#f8fafc',
              borderRadius: '12px',
              padding: '10px 12px',
              marginBottom: '14px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginBottom: '4px' }}>
                <span>Item total</span><strong>₹{getCartPricing().subtotal.toFixed(2)}</strong>
              </div>
              {fulfillmentType === 'DELIVERY' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginBottom: '4px' }}>
                  <span>Delivery fee</span><strong>₹{getCartPricing().deliveryCharge.toFixed(2)}</strong>
                </div>
              )}
              {getCartPricing().discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#16a34a', marginBottom: '4px' }}>
                  <span>Discount ({getCartPricing().appliedCouponCode || 'Coupon'})</span>
                  <strong>-₹{getCartPricing().discountAmount.toFixed(2)}</strong>
                </div>
              )}
              {getCartPricing().taxAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginBottom: '4px' }}>
                  <span>{gstSettings.taxName || 'GST'}{getCartPricing().taxEnabled ? ` (${getCartPricing().taxPercentage.toFixed(2)}%)` : ''}</span>
                  <strong>₹{getCartPricing().taxAmount.toFixed(2)}</strong>
                </div>
              )}
              <div style={{ height: '1px', background: '#e2e8f0', margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', color: '#111827', fontWeight: '800' }}>
                <span>To Pay</span><span>₹{getCartTotal().toFixed(2)}</span>
              </div>
            </div>
            {fulfillmentType === 'DELIVERY' && getCartPricing().belowMinimum && (
              <div style={{ background: '#ffedd5', color: '#9a3412', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
                Minimum order for delivery is Rs {getCartPricing().minimumOrderAmount.toFixed(2)}.
              </div>
            )}

            {paymentError && (
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                {paymentError}
              </div>
            )}

            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#6b7280', letterSpacing: '0.04em' }}>
              COUPON
            </div>

            <div style={{ marginBottom: '8px', display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(String(e.target.value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                disabled={processingPayment || applyingCoupon}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  background: '#ffffff',
                  color: '#1f2937'
                }}
              />
              <button
                type="button"
                onClick={() => handleApplyCoupon()}
                disabled={processingPayment || applyingCoupon}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '13px',
                  cursor: (processingPayment || applyingCoupon) ? 'not-allowed' : 'pointer',
                  background: (processingPayment || applyingCoupon) ? '#94a3b8' : '#2563eb',
                  color: '#ffffff'
                }}
              >
                {applyingCoupon ? 'Applying...' : 'Apply'}
              </button>
            </div>

            <button
              type="button"
              onClick={handleToggleCoupons}
              disabled={processingPayment}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#2563eb',
                background: 'transparent',
                border: 'none',
                padding: 0,
                marginBottom: '12px',
                cursor: processingPayment ? 'not-allowed' : 'pointer',
                textDecoration: 'underline'
              }}
            >
              {showCouponList ? 'Hide Available Coupons' : 'View Available Coupons'}
            </button>

            {showCouponList && (
              <div style={{ marginBottom: '14px' }}>
                {couponsLoading && (
                  <div style={{ fontSize: '12px', color: '#6b7280', padding: '8px 4px' }}>
                    Loading coupons...
                  </div>
                )}
                {!couponsLoading && couponsError && (
                  <div style={{ fontSize: '12px', color: '#dc2626', padding: '8px 4px' }}>
                    {couponsError}
                  </div>
                )}
                {!couponsLoading && !couponsError && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {availableCoupons.map((coupon) => (
                      <div
                        key={coupon?.id || coupon?.couponCode}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '10px 12px',
                          background: '#ffffff',
                          boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.03em' }}>
                              {String(coupon?.couponCode || '').toUpperCase()}
                            </div>
                            <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>
                              {formatCouponDescription(coupon)}
                            </div>
                            {formatCouponExpiry(coupon) && (
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                {formatCouponExpiry(coupon)}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const code = String(coupon?.couponCode || '').toUpperCase();
                              setCouponCode(code);
                              setShowCouponList(false);
                              handleApplyCoupon(code);
                            }}
                            disabled={processingPayment || applyingCoupon}
                            style={{
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 12px',
                              background: '#2563eb',
                              color: '#ffffff',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: (processingPayment || applyingCoupon) ? 'not-allowed' : 'pointer',
                              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.2)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Apply Coupon
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {appliedCoupon && (
              <div style={{
                marginBottom: '14px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #bbf7d0',
                background: '#f0fdf4',
                color: '#166534',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px'
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>
                  {(() => {
                    const appliedUpToValue = Number(appliedCoupon?.upToValue ?? appliedCoupon?.up_to_value ?? 0);
                    const isPercent = String(appliedCoupon?.discountType || '').toUpperCase() === 'PERCENT';
                    const upToLabel = isPercent && appliedUpToValue > 0
                      ? ` (Up to ₹${appliedUpToValue.toFixed(0)})`
                      : '';
                    return `Applied ${appliedCoupon.couponCode}${upToLabel} • Saved ₹${Number(appliedCoupon.discountAmount || 0).toFixed(2)}`;
                  })()}
                </div>
                <button
                  type="button"
                  onClick={handleRemoveCoupon}
                  disabled={processingPayment}
                  onMouseEnter={(e) => {
                    if (!processingPayment) {
                      e.currentTarget.style.background = '#dc2626';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = processingPayment ? '#9ca3af' : '#ef4444';
                  }}
                  style={{
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    background: processingPayment ? '#9ca3af' : '#ef4444',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: processingPayment ? 'not-allowed' : 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            )}

            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#6b7280', letterSpacing: '0.04em' }}>
              CONTACT DETAILS
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: isFilled(checkoutForm.name) ? '#374151' : '#dc2626', marginBottom: '6px' }}>
                Full Name * {!isFilled(checkoutForm.name) && <span style={{ fontSize: '12px', fontWeight: '700' }}>({requiredHint})</span>}
              </label>
              <input
                type="text"
                placeholder="Enter your name"
                value={checkoutForm.name}
                onChange={e => setCheckoutForm(p => ({ ...p, name: e.target.value }))}
                title={!isFilled(checkoutForm.name) ? requiredHint : ''}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                  border: `1px solid ${isFilled(checkoutForm.name) ? '#d1d5db' : '#dc2626'}`,
                  background: '#ffffff',
                  color: '#1f2937'
                }}
              />
              {!isFilled(checkoutForm.name) && <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>{requiredHint}</div>}
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: isFilled(checkoutForm.phone) ? '#374151' : '#dc2626', marginBottom: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <path d="M20.52 3.48A11.89 11.89 0 0 0 12.04 0C5.46 0 .1 5.35.1 11.93c0 2.1.55 4.16 1.6 5.97L0 24l6.3-1.65a11.92 11.92 0 0 0 5.73 1.46h.01c6.58 0 11.93-5.35 11.93-11.93 0-3.19-1.24-6.19-3.45-8.4Z" fill="#25D366"/>
                  <path d="M17.2 14.15c-.28-.14-1.64-.8-1.9-.9-.25-.1-.43-.14-.62.14-.18.28-.71.9-.87 1.08-.16.18-.32.21-.6.07-.28-.14-1.17-.43-2.23-1.38-.82-.73-1.37-1.63-1.53-1.9-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.48.14-.16.19-.28.28-.46.1-.18.05-.35-.02-.49-.07-.14-.62-1.5-.85-2.05-.22-.54-.44-.46-.62-.47h-.53c-.18 0-.46.07-.7.35-.24.28-.92.9-.92 2.2 0 1.3.94 2.55 1.07 2.72.13.18 1.86 2.84 4.52 3.98.63.27 1.12.43 1.5.55.63.2 1.2.17 1.65.1.5-.07 1.64-.67 1.87-1.32.23-.65.23-1.2.16-1.32-.06-.12-.24-.2-.52-.35Z" fill="white"/>
                </svg>
                WhatsApp Number *
                {!isFilled(checkoutForm.phone) && <span style={{ fontSize: '12px', fontWeight: '700' }}>({requiredHint})</span>}
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${isFilled(checkoutForm.phone) ? '#d1d5db' : '#dc2626'}`,
                borderRadius: '8px',
                background: '#ffffff',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '10px 12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#111827',
                  background: '#f8fafc',
                  borderRight: '1px solid #e5e7eb',
                  userSelect: 'none'
                }}>
                  +91
                </div>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  placeholder="Enter 10-digit WhatsApp number"
                  value={checkoutForm.phone}
                  onChange={e => {
                    const digitsOnly = String(e.target.value || '').replace(/\D/g, '').slice(0, 10);
                    setCheckoutForm(p => ({ ...p, phone: digitsOnly }));
                  }}
                  title={!isFilled(checkoutForm.phone) ? requiredHint : ''}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '0', fontSize: '14px', boxSizing: 'border-box',
                    border: 'none',
                    outline: 'none',
                    background: '#ffffff',
                    color: '#1f2937'
                  }}
                />
              </div>
              {!isFilled(checkoutForm.phone) && <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>{requiredHint}</div>}
            </div>

            <div style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Email for payment confirmation & invoice (optional)</label>
              <input
                type="email"
                placeholder="Enter email for payment confirmation & invoice"
                value={checkoutForm.email}
                onChange={e => setCheckoutForm(p => ({ ...p, email: e.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                  border: '1px solid #d1d5db',
                  background: '#ffffff',
                  color: '#1f2937'
                }}
              />
            </div>

            {fulfillmentType === 'DELIVERY' && (
              <>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#6b7280', letterSpacing: '0.04em' }}>
              DELIVERY DETAILS
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div ref={localityDropdownRef} style={{ gridColumn: '1 / span 2', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                    Search and select locality *
                  </label>
                  {!hasValidSelectedLocality && !inputValue.trim() && (
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#dc2626' }}>{requiredHint}</span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Search and select locality *"
                  value={inputValue}
                  onFocus={() => setShowLocalityDropdown(true)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLocalitySearchText(value);
                    setFilteredLocalities(filterLocalities(localities, value));
                    setShowLocalityDropdown(true);

                    if (!selectedLocality) return;
                    const selectedName = String(selectedLocality.localityName || '').trim().toLowerCase();
                    const typed = String(value || '').trim().toLowerCase();
                    if (typed !== selectedName) {
                      setSelectedLocality(null);
                    }
                  }}
                  onBlur={() => {
                    window.setTimeout(() => {
                      setShowLocalityDropdown(false);
                    }, 120);
                  }}
                  title={!hasValidSelectedLocality ? requiredHint : ''}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                    border: hasValidSelectedLocality ? '1px solid #10b981' : '1px solid #dc2626',
                    background: '#ffffff',
                    color: '#1f2937'
                  }}
                />

                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 0,
                  maxHeight: '190px',
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #d1d5db',
                  borderRadius: '0 0 8px 8px',
                  zIndex: 30,
                  boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                  display: showLocalityDropdown ? 'block' : 'none'
                }}>
                  {loadingLocalities && (
                    <div style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280' }}>Loading localities...</div>
                  )}

                  {!loadingLocalities && hasFetchedLocalities && filteredLocalities.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280' }}>No matching localities found</div>
                  )}

                  {!loadingLocalities && filteredLocalities.map((locality) => {
                    const key = `${locality.localityCode || 'code'}-${locality.localityName || 'name'}`;
                    return (
                      <button
                        type="button"
                        key={key}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelectLocality(locality)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '10px 12px',
                          borderBottom: '1px solid #f1f5f9'
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>
                          {renderHighlightedLocalityText(locality.localityName, inputValue)}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {inputValue.trim() && !selectedLocality && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#dc2626' }}>
                    Please select locality
                  </div>
                )}

              </div>

              <input
                type="text"
                placeholder="Landmark (optional)"
                value={checkoutForm.landmark}
                onChange={e => setCheckoutForm(p => ({ ...p, landmark: e.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                  border: '1px solid #d1d5db',
                  background: '#ffffff',
                  color: '#1f2937'
                }}
              />
              <input
                type="text"
                placeholder="City"
                value={checkoutForm.city}
                disabled
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                  border: '1px solid #d1d5db',
                  background: '#f1f5f9',
                  color: '#1f2937'
                }}
              />
            </div>

            {/* Map/GPS block disabled (manual address only) */}

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: isFilled(checkoutForm.addressLine) ? '#374151' : '#dc2626', marginBottom: '6px' }}>
                Complete Delivery Address * {!isFilled(checkoutForm.addressLine) && <span style={{ fontSize: '12px', fontWeight: '700' }}>({requiredHint})</span>}
              </label>
              <textarea
                ref={addressLineRef}
                placeholder="House/Flat, Street, Area"
                value={checkoutForm.addressLine}
                onChange={e => setCheckoutForm(p => ({ ...p, addressLine: e.target.value }))}
                rows={2}
                title={!isFilled(checkoutForm.addressLine) ? requiredHint : ''}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical',
                  border: `1px solid ${isFilled(checkoutForm.addressLine) ? '#d1d5db' : '#dc2626'}`,
                  background: '#ffffff',
                  color: '#1f2937'
                }}
              />
            </div>
              </>
            )}

            {fulfillmentType === 'DRIVETHROUGH' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
                <input
                  type="text"
                  placeholder="Vehicle colour (optional)"
                  value={checkoutForm.vehicleColor}
                  onChange={e => setCheckoutForm(p => ({ ...p, vehicleColor: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                    border: '1px solid #d1d5db',
                    background: '#ffffff',
                    color: '#1f2937'
                  }}
                />
                <input
                  type="text"
                  placeholder="Vehicle number *"
                  value={checkoutForm.vehicleNumber}
                  onChange={e => setCheckoutForm(p => ({ ...p, vehicleNumber: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                    border: isFilled(checkoutForm.vehicleNumber) ? '1px solid #d1d5db' : '1px solid #dc2626',
                    background: '#ffffff',
                    color: '#1f2937'
                  }}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
              <textarea
                placeholder="Delivery notes (optional)"
                value={checkoutForm.deliveryInstructions}
                onChange={e => setCheckoutForm(p => ({ ...p, deliveryInstructions: e.target.value }))}
                rows={2}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', gridColumn: '1 / span 2',
                  border: '1px solid #d1d5db',
                  background: '#ffffff',
                  color: '#1f2937'
                }}
              />
            </div>

            <button
              onClick={handleRazorpayCheckout}
              disabled={processingPayment || (fulfillmentType === 'DELIVERY' && (getCartPricing().belowMinimum || !hasValidSelectedLocality))}
              style={{
                width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                background: processingPayment
                  ? '#9ca3af'
                  : '#ef4f5f',
                color: 'white', fontSize: '16px', fontWeight: '700',
                cursor: (processingPayment || (fulfillmentType === 'DELIVERY' && (getCartPricing().belowMinimum || !hasValidSelectedLocality))) ? 'not-allowed' : 'pointer',
                boxShadow: processingPayment ? 'none' : '0 8px 20px rgba(239,79,95,0.35)'
              }}
            >
              {processingPayment ? 'Processing payment...' : `Proceed to Pay ₹${getCartTotal().toFixed(2)}`}
            </button>

            <p style={{ marginTop: '12px', fontSize: '11px', color: '#6b7280', textAlign: 'center' }}>
              100% secure payments powered by Razorpay
            </p>
          </div>
        </div>
      )}

      {showClearCartConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
            padding: isMobile ? '16px' : '24px'
          }}
          onClick={closeClearCartConfirm}
        >
          <div
            style={{
              width: isMobile ? '100%' : 'min(92vw, 420px)',
              maxWidth: '420px',
              borderRadius: '16px',
              background: currentTheme.isDark ? '#0f172a' : '#ffffff',
              border: `1px solid ${currentTheme.isDark ? 'rgba(148,163,184,0.25)' : '#e2e8f0'}`,
              boxShadow: '0 24px 60px rgba(2, 6, 23, 0.35)',
              padding: isMobile ? '18px 16px' : '22px 20px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ fontSize: '22px' }}>🗑️</span>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '800',
                color: currentTheme.isDark ? '#f8fafc' : '#0f172a'
              }}>
                Clear cart?
              </h3>
            </div>

            <p style={{
              margin: '0 0 16px 0',
              fontSize: '14px',
              lineHeight: '1.5',
              color: currentTheme.isDark ? '#cbd5e1' : '#475569'
            }}>
              This will remove all items from your cart. You can add them again anytime.
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={closeClearCartConfirm}
                style={{
                  border: `1px solid ${currentTheme.isDark ? 'rgba(148,163,184,0.45)' : '#cbd5e1'}`,
                  background: currentTheme.isDark ? 'rgba(15,23,42,0.6)' : '#f8fafc',
                  color: currentTheme.isDark ? '#e2e8f0' : '#334155',
                  borderRadius: '10px',
                  padding: isMobile ? '10px 14px' : '10px 16px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={clearCart}
                style={{
                  border: 'none',
                  background: '#dc2626',
                  color: '#ffffff',
                  borderRadius: '10px',
                  padding: isMobile ? '10px 14px' : '10px 16px',
                  fontSize: '14px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(220, 38, 38, 0.28)'
                }}
              >
                Yes, clear cart
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─────────────────────────────────────────────────────────── */}

      {/* Addon Selection Modal */}
      {showAddonModal && currentProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: isMobile ? '8px' : '14px'
        }}
          onClick={closeAddonModal}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? '18px' : '20px',
            maxWidth: isMobile ? '96vw' : '560px',
            width: isMobile ? '96vw' : '100%',
            maxHeight: isMobile ? '94vh' : '88vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: isMobile ? '14px 14px 12px 14px' : '20px',
              borderBottom: '2px solid #e5e7eb',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              zIndex: 10
            }}>
              <h2 style={{
                fontSize: isMobile ? '18px' : '22px',
                fontWeight: '700',
                color: '#1f2937',
                marginBottom: '4px'
              }}>
                Customize Your {currentProduct.productName}
              </h2>
              <p style={{
                fontSize: isMobile ? '12px' : '14px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                Select add-ons to customize your order
              </p>
            </div>

            {/* Addons List */}
            <div style={{ padding: isMobile ? '12px' : '20px' }}>
              {loadingAddons ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p style={{ color: '#6b7280' }}>Loading add-ons...</p>
                </div>
              ) : availableAddons.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p style={{ color: '#6b7280' }}>No add-ons available</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '14px' }}>
                  {Object.values(
                    availableAddons.reduce((acc, addon) => {
                      const fallbackId = addon?.groupId || `group-${addon?.groupName || 'addons'}`;
                      const groupId = String(fallbackId);
                      if (!acc[groupId]) {
                        acc[groupId] = {
                          groupId,
                          groupName: addon?.groupName || selectionRulesByGroup?.[groupId]?.groupName || 'Add-ons',
                          groupDisplayOrder: Number(
                            addon?.groupDisplayOrder
                            ?? selectionRulesByGroup?.[groupId]?.displayOrder
                            ?? 9999
                          ),
                          groupOptions: []
                        };
                      }
                      acc[groupId].groupOptions.push(addon);
                      return acc;
                    }, {})
                  )
                    .sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder)
                    .map(({ groupId, groupName, groupOptions }) => {
                    const matchedRule = selectionRulesByGroup?.[groupId]
                      || Object.values(selectionRulesByGroup || {}).find(
                        (rule) => String(rule?.groupName || '').toLowerCase() === String(groupName || '').toLowerCase()
                      );

                    const dependsOnGroupId = matchedRule?.dependsOnGroupId;
                    const selectedParentOption = dependsOnGroupId
                      ? selectedAddons.find((item) => String(item?.groupId) === String(dependsOnGroupId))
                      : null;

                    if (dependsOnGroupId && !selectedParentOption) {
                      return null;
                    }

                    const contextFilteredOptions = (groupOptions || []).filter((addon) =>
                      matchesDependentContext(addon, selectedParentOption)
                    );

                    if (contextFilteredOptions.length === 0) {
                      return null;
                    }

                    const resolvedGroupName = matchedRule?.dynamicLabel && selectedParentOption
                      ? `${groupName} [${selectedParentOption?.addonName || selectedParentOption?.optionName || ''}]`
                      : groupName;

                    const groupMin = Number(matchedRule?.minSelection || 0);
                    const groupMax = Number(matchedRule?.maxSelection || 0);
                    const selectionText = matchedRule ? getSelectionText(groupMin, groupMax) : '';
                    const isGroupRequired = groupMin > 0 || !!matchedRule?.isRequired;
                    const isAddonGroup = /addon|add-on|add ons|add-ons/i.test(String(resolvedGroupName || ''));
                    const showMandatoryLabel = isGroupRequired && !isAddonGroup;

                    return (
                      <div key={groupId} style={{ border: '1px solid #e2e8f0', borderRadius: '14px', background: '#ffffff', overflow: 'hidden', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.06)' }}>
                        <div style={{ padding: isMobile ? '10px 12px' : '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#ffffff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: '13px', fontWeight: '800', color: currentTheme.primaryColor }}>
                              {resolvedGroupName}
                            </div>
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: showMandatoryLabel ? 700 : 600,
                                color: showMandatoryLabel ? '#e53935' : '#16a34a'
                              }}
                            >
                              {showMandatoryLabel ? 'Mandatory *' : '(Optional)'}
                            </span>
                          </div>
                          {selectionText && (
                            <div style={{ marginTop: '2px', fontSize: '11px', color: '#64748b' }}>
                              {selectionText}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {contextFilteredOptions.map((addon, index) => {
                            const isSelected = selectedAddons.find((a) => getAddonIdentityKey(a) === getAddonIdentityKey(addon));
                            const optionKey = addon?.optionId || addon?.legacyAddonId || addon?.addonId || addon?.addonName;
                            return (
                              <div
                                key={`${groupId}-${optionKey}`}
                                onClick={() => toggleAddonSelection(addon)}
                                style={{
                                  padding: isMobile ? '12px' : '14px',
                                  borderBottom: index === contextFilteredOptions.length - 1 ? 'none' : '1px solid #e2e8f0',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '12px',
                                  background: '#ffffff'
                                }}
                              >
                                <div style={{
                                  fontSize: isMobile ? '15px' : '16px',
                                  fontWeight: '600',
                                  color: '#1f2937',
                                  flex: 1,
                                  minWidth: 0
                                }}>
                                  {addon.addonName}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                  <div style={{
                                    fontSize: isMobile ? '13px' : '14px',
                                    fontWeight: '600',
                                    color: '#059669'
                                  }}>
                                    ₹{Number(addon.addonPrice || 0).toFixed(2)}
                                  </div>
                                  <div style={{
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    border: `2px solid ${isSelected ? '#10b981' : '#d1d5db'}`,
                                    backgroundColor: isSelected ? '#10b981' : 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: 'white'
                                  }}>
                                    {isSelected && '✓'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ marginTop: isMobile ? '10px' : '14px' }}>
                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                  Special requests can be added after the item is placed in cart.
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: isMobile ? '12px' : '20px',
              borderTop: '2px solid #e5e7eb',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: isMobile ? '10px' : '16px'
              }}>
                <span style={{ fontSize: '16px', color: '#6b7280' }}>
                  Total Price:
                </span>
                <span style={{
                  fontSize: isMobile ? '32px' : '22px',
                  fontWeight: '700',
                  color: currentTheme.primaryColor
                }}>
                  ₹{((currentProduct.discountPrice || currentProduct.price) +
                    selectedAddons.reduce((sum, addon) => sum + Number(addon.addonPrice || 0), 0)).toFixed(2)}
                </span>
              </div>

              {deliverySlabInsight && (
                <div style={{
                  marginBottom: isMobile ? '10px' : '14px',
                  padding: isMobile ? '10px 12px' : '12px 14px',
                  borderRadius: '14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)'
                }}>
                  <div style={{
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '700',
                    color: '#0f172a',
                    lineHeight: 1.4
                  }}>
                    {deliverySlabInsight.message}
                  </div>
                  <div style={{
                    marginTop: '4px',
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: '600'
                  }}>
                    Based on current delivery slabs
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: isMobile ? '10px' : '12px' }}>
                <button
                  onClick={closeAddonModal}
                  style={{
                    flex: 1,
                    padding: isMobile ? '12px' : '14px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!validateGroupSelections()) return;
                    addToCartWithAddons(currentProduct, normalizedSelectedAddons);
                  }}
                  style={{
                    flex: 2,
                    padding: isMobile ? '12px' : '14px',
                    borderRadius: '12px',
                    border: 'none',
                    background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                    color: 'white',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                >
                  {editingCartItemId ? 'Update Cart' : 'Add to Cart'} {normalizedSelectedAddons.length > 0 && `(${normalizedSelectedAddons.length} selected)`}
                </button>
              </div>
            </div>
          </div>
        </div>
          )}

      {editingSpecialRequest.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10003,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '12px' : '20px'
          }}
          onClick={closeSpecialRequestEditor}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              borderRadius: '16px',
              background: currentTheme.isDark ? '#0f172a' : '#ffffff',
              border: `1px solid ${currentTheme.isDark ? 'rgba(148,163,184,0.25)' : '#e2e8f0'}`,
              boxShadow: '0 24px 60px rgba(2, 6, 23, 0.32)',
              padding: isMobile ? '18px 16px' : '22px'
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '18px',
              fontWeight: '800',
              color: currentTheme.isDark ? '#f8fafc' : '#0f172a'
            }}>
              Special request
            </h3>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '13px',
              color: currentTheme.isDark ? '#cbd5e1' : '#475569'
            }}>
              Add a note for this item only.
            </p>

            <textarea
              value={editingSpecialRequest.text}
              onChange={(event) => setEditingSpecialRequest((prev) => ({
                ...prev,
                text: event.target.value.slice(0, 100)
              }))}
              rows={4}
              maxLength={100}
              placeholder="e.g. Less spicy, no onion"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                padding: '12px 14px',
                fontSize: '14px',
                resize: 'vertical',
                background: currentTheme.isDark ? 'rgba(15,23,42,0.75)' : '#f8fafc',
                color: currentTheme.isDark ? '#f8fafc' : '#0f172a'
              }}
            />

            <div style={{ marginTop: '6px', textAlign: 'right', fontSize: '11px', color: '#94a3b8' }}>
              {editingSpecialRequest.text.length}/100
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
              {quickInstructionChips.map((chip) => {
                const selectedInstructionSet = new Set(
                  String(editingSpecialRequest.text || '')
                    .split('|')
                    .map((token) => token.trim().toLowerCase())
                    .filter(Boolean)
                );
                const isSelected = selectedInstructionSet.has(chip.toLowerCase());
                return (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => {
                      const currentTokens = String(editingSpecialRequest.text || '')
                        .split('|')
                        .map((token) => token.trim())
                        .filter(Boolean);
                      const alreadySelected = currentTokens.some((token) => token.toLowerCase() === chip.toLowerCase());
                      const nextTokens = alreadySelected
                        ? currentTokens.filter((token) => token.toLowerCase() !== chip.toLowerCase())
                        : [...currentTokens, chip];
                      setEditingSpecialRequest((prev) => ({
                        ...prev,
                        text: nextTokens.join(' | ').slice(0, 100)
                      }));
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '999px',
                      border: isSelected ? '2px solid #3b82f6' : '1px solid #d1d5db',
                      background: isSelected ? '#dbeafe' : '#fff',
                      color: isSelected ? '#1e40af' : '#374151',
                      fontSize: '12px',
                      fontWeight: isSelected ? '700' : '600',
                      cursor: 'pointer'
                    }}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                type="button"
                onClick={closeSpecialRequestEditor}
                style={{
                  border: '1px solid #cbd5e1',
                  background: currentTheme.isDark ? 'rgba(15,23,42,0.6)' : '#f8fafc',
                  color: currentTheme.isDark ? '#e2e8f0' : '#334155',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveSpecialRequest}
                style={{
                  border: 'none',
                  background: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${currentTheme.secondaryColor})`,
                  color: '#ffffff',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '800',
                  cursor: 'pointer'
                }}
              >
                Save request
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </>
  );
};

export default TemplatedMenuPage;
